import React, { useState, useEffect, useRef, useMemo } from "react";
import { Geolocation } from "@capacitor/geolocation";
import { createPortal } from "react-dom";
import MaintenancePage from "./MaintenancePage";
import DownloadAppPage from "./DownloadAppPage";
import { MAINTENANCE_CONFIG } from "./config/maintenance";
import { MapPin, Mail, Lock, User, ArrowRight, Camera } from "lucide-react";
import {
  calculateEventStatus,
  formatDateTime,
  focusCurrentTime,
} from "./utils/dateTime";
import { calculateDistance, getGoogleMapsUrl } from "./utils/geo";
import { detectPlaceCategory, getModernPlaceIcon } from "./utils/places";
import { validatePassword } from "./utils/validation";
import { formatTimeAgo, CustomLoadingSpinner } from "./utils/helpers";
import {
  EVENT_CATEGORIES,
  CATEGORY_MAP,
  normalizeCategory,
} from "./constants/categories";
import {
  ADMIN_USER_IDS,
  isAdmin,
  checkDailyEventLimit,
} from "./constants/admin";
import "./services/reporting";
import "./services/sharing";
import "./services/notifications";
import "./services/categoryFilter";
import "./services/userProfile";
import "./services/participants";
import "./services/chatsList";
import "./services/eventEdit";
import "./services/eventCreation";
import "./services/chat";
import "./utils/toast";
import "./services/modals";
import "./services/eventJoin";
import "./services/eventRealtime";
import "./services/eventsListPanel";
import "./services/eventDetailsPanel";
import Toast from "./components/Toast";
import FullScreenImage from "./components/FullScreenImage";
import ConfirmModal from "./components/ConfirmModal";
import PatchNotesModal from "./components/PatchNotesModal";
import SupportModal from "./components/SupportModal";
import ReportModal from "./components/ReportModal";
import JoinRequestPopup from "./components/JoinRequestPopup";
import EditPeopleLimitModal from "./components/EditPeopleLimitModal";
import ChatsListContent from "./components/ChatsListContent";
import UserProfilePanel from "./components/UserProfilePanel";
import AllParticipantsPanel from "./components/AllParticipantsPanel";
import GuestEventDetailsPanel from "./components/GuestEventDetailsPanel";
import NotificationsPanel from "./components/NotificationsPanel";
import ProfilePanel from './components/ProfilePanel';
import LocationPermissionBanner from './components/LocationPermissionBanner';
import MapHeader from "./components/MapHeader";
import FiltersPanel from "./components/FiltersPanel";
import { supabase } from "./supabase/config";
import {
  registerUser,
  loginUser,
  logoutUser,
  resetPassword,
  updatePassword,
  signInWithGoogle,
  onAuthChange,
  getCurrentUser,
} from "./supabase/auth";
import {
  createUserProfile,
  getUserProfile,
  updateUserProfile,
} from "./supabase/users";
import { getAllEvents, deleteEvent, updateEvent } from "./supabase/events";
import { uploadProfilePicture } from "./supabase/storage";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getRandomGradient } from "./constants/gradients";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Track last clicked message for mobile
let lastClickedMessageId = null;

window.hasUnreadChats = false;

window.CustomLoadingSpinner = CustomLoadingSpinner;

window.updateProfileBackground = async (color, userId) => {
  try {
    // Update Supabase
    await updateUserProfile(userId, { background: color });

    // Dispatch event to update UI
    const event = new CustomEvent("updateProfileBackground", {
      detail: { color },
    });
    window.dispatchEvent(event);
  } catch (error) {
    console.error("❌ Error updating background:", error);
  }
};

const DevBadge = () => {
  const isDev = process.env.REACT_APP_ENV === "development";

  if (!isDev) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        top: "50%",
        transform: "translateY(-50%)",
        background: "linear-gradient(135deg, #ff0000 0%, #cc0000 100%)",
        color: "white",
        padding: "8px 16px",
        fontSize: "12px",
        fontWeight: "bold",
        letterSpacing: "1px",
        zIndex: 999999,
        boxShadow: "2px 0 10px rgba(255, 0, 0, 0.5)",
        borderRadius: "0 8px 8px 0",
        textTransform: "uppercase",
        animation: "pulse 2s ease-in-out infinite",
      }}
    >
      DEV
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

window.ADMIN_USER_IDS = ADMIN_USER_IDS;

// Admin Panel Component
const AdminPanelContent = ({ currentUser, navigateTo }) => {
  const [activeTab, setActiveTab] = useState("event-reports");
  const [reports, setReports] = useState([]);
  const [hiddenEvents, setHiddenEvents] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const supabaseClient = window.supabase || supabase;

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "event-reports") {
        const { data } = await supabaseClient
          .from("reports")
          .select("*")
          .eq("type", "event")
          .eq("status", "pending")
          .order("created_at", { ascending: false });
        setReports(data || []);
      } else if (activeTab === "user-reports") {
        const { data } = await supabaseClient
          .from("reports")
          .select("*")
          .eq("type", "user")
          .eq("status", "pending")
          .order("created_at", { ascending: false });
        setReports(data || []);
      } else if (activeTab === "hidden-events") {
        const { data } = await supabaseClient
          .from("events")
          .select("*")
          .eq("hidden", true)
          .order("created_at", { ascending: false });
        setHiddenEvents(data || []);
      } else if (activeTab === "banned-users") {
        const { data } = await supabaseClient
          .from("users")
          .select("*")
          .eq("banned", true)
          .order("name", { ascending: true });
        setBannedUsers(data || []);
      }
    } catch (err) {
      console.error("Admin load error:", err);
    }
    setLoading(false);
  };
  

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Expose refresh to the parent via DOM button
  useEffect(() => {
    const refreshBtn = document.getElementById("admin-refresh-btn");
    if (refreshBtn) {
      const handler = () => {
        refreshBtn.querySelector("svg").style.animation = "spin 0.6s ease-out";
        setTimeout(() => {
          refreshBtn.querySelector("svg").style.animation = "";
        }, 600);
        setReports([]);
        setHiddenEvents([]);
        setBannedUsers([]);
        loadData();
      };
      refreshBtn.addEventListener("click", handler);
      return () => refreshBtn.removeEventListener("click", handler);
    }
  }, []);

  // Group event reports by event_id with count
  const groupedEventReports = useMemo(() => {
    if (activeTab !== "event-reports") return [];
    const groups = {};
    reports.forEach((r) => {
      const eid = r.reported_event_id;
      if (!groups[eid]) {
        groups[eid] = {
          eventId: eid,
          eventTitle: r.reported_event_title,
          reports: [],
          pendingCount: 0,
        };
      }
      groups[eid].reports.push(r);
      if (r.status === "pending") groups[eid].pendingCount++;
    });
    return Object.values(groups).sort(
      (a, b) => b.pendingCount - a.pendingCount,
    );
  }, [reports, activeTab]);

  // Group user reports by user_id
  const groupedUserReports = useMemo(() => {
    if (activeTab !== "user-reports") return [];
    const groups = {};
    reports.forEach((r) => {
      const uid = r.reported_user_id;
      if (!groups[uid]) {
        groups[uid] = {
          userId: uid,
          userName: r.reported_user_name,
          userEmail: r.reported_user_email,
          reports: [],
          pendingCount: 0,
        };
      }
      groups[uid].reports.push(r);
      if (r.status === "pending") groups[uid].pendingCount++;
    });
    return Object.values(groups).sort(
      (a, b) => b.pendingCount - a.pendingCount,
    );
  }, [reports, activeTab]);

  const handleDismissReports = async (type, id) => {
    setActionLoading(`dismiss-${id}`);
    const field = type === "event" ? "reported_event_id" : "reported_user_id";
    const { error } = await supabaseClient
      .from("reports")
      .update({ status: "dismissed" })
      .eq(field, id)
      .eq("status", "pending");

    if (error) {
      console.error("❌ Failed to dismiss reports:", error);
      window.showToast("Failed to dismiss reports: " + error.message, "error");
      setActionLoading(null);
      return;
    }

    window.showToast("Reports dismissed", "success");
    setActionLoading(null);
    setReports([]);
    await loadData();
  };

  const handleHideEvent = async (eventId) => {
    setActionLoading(`hide-${eventId}`);
    await supabaseClient
      .from("events")
      .update({ hidden: true })
      .eq("event_id", eventId);
    await supabaseClient
      .from("reports")
      .update({ status: "reviewed" })
      .eq("reported_event_id", eventId)
      .eq("status", "pending");
    window.showToast("Event hidden", "success");
    setActionLoading(null);
    loadData();
  };

  const handleUnhideEvent = async (eventId) => {
    setActionLoading(`unhide-${eventId}`);
    await supabaseClient
      .from("events")
      .update({ hidden: false })
      .eq("event_id", eventId);
    window.showToast("Event restored", "success");
    setActionLoading(null);
    loadData();
  };

  const [deleteModal, setDeleteModal] = useState(null);

  const handleDeleteEvent = (eventId) => {
    setDeleteModal(eventId);
  };

  const confirmDeleteEvent = async () => {
    const eventId = deleteModal;
    setDeleteModal(null);
    setActionLoading(`delete-${eventId}`);
    await supabaseClient.from("events").delete().eq("event_id", eventId);
    await supabaseClient
      .from("reports")
      .update({ status: "reviewed" })
      .eq("reported_event_id", eventId)
      .eq("status", "pending");
    window.showToast("Event deleted", "success");
    setActionLoading(null);
    loadData();
  };

  const [banModal, setBanModal] = useState(null); // { userId, userName }
  const [banReason, setBanReason] = useState("");

  const handleBanUser = (userId, userName) => {
    setBanModal({ userId, userName });
    setBanReason("");
  };

  const confirmBanUser = async () => {
    if (!banReason.trim()) return;
    const { userId, userName } = banModal;
    setBanModal(null);
    setActionLoading(`ban-${userId}`);

    const { error: banError } = await supabaseClient
      .from("users")
      .update({ banned: true, ban_reason: banReason.trim() })
      .eq("id", userId);

    if (banError) {
      console.error("❌ Failed to ban user:", banError);
      window.showToast("Failed to ban user: " + banError.message, "error");
      setActionLoading(null);
      return;
    }

    // Hide all their events
    const { error: hideError } = await supabaseClient
      .from("events")
      .update({ hidden: true })
      .eq("creator_id", userId);

    // Mark ALL user reports for this user as reviewed
    const { error: reviewError } = await supabaseClient
      .from("reports")
      .update({ status: "reviewed" })
      .eq("reported_user_id", userId)
      .eq("status", "pending");

    // Also mark ALL event reports for events created by this user as reviewed
    // First get all event IDs by this creator
    const { data: creatorEvents } = await supabaseClient
      .from("events")
      .select("event_id")
      .eq("creator_id", userId);

    if (creatorEvents && creatorEvents.length > 0) {
      const eventIds = creatorEvents.map((e) => e.event_id);
      const { error: eventReviewError } = await supabaseClient
        .from("reports")
        .update({ status: "reviewed" })
        .in("reported_event_id", eventIds)
        .eq("status", "pending");
    }

    window.showToast(`${userName} has been banned`, "success");
    setActionLoading(null);
    setBanReason("");
    setReports([]);
    await loadData();
  };

  const [unbanModal, setUnbanModal] = useState(null);

  const handleUnbanUser = (userId, userName) => {
    setUnbanModal({ userId, userName });
  };

  const confirmUnbanUser = async () => {
    const { userId, userName } = unbanModal;
    setUnbanModal(null);
    setActionLoading(`unban-${userId}`);
    await supabaseClient
      .from("users")
      .update({ banned: false, ban_reason: null })
      .eq("id", userId);
    window.showToast(`${userName} has been unbanned`, "success");
    setActionLoading(null);
    loadData();
  };

  const tabs = [
    { id: "event-reports", label: "Event Reports", icon: "📋" },
    { id: "user-reports", label: "User Reports", icon: "👤" },
    { id: "hidden-events", label: "Hidden Events", icon: "👁️" },
    { id: "banned-users", label: "Banned Users", icon: "🚫" },
  ];

  const cardStyle = {
    background: "white",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "12px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  };

  const btnStyle = (color) => ({
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
    fontFamily: "'Outfit', sans-serif",
    background:
      color === "red"
        ? "#fef2f2"
        : color === "green"
          ? "#f0fdf4"
          : color === "blue"
            ? "#eff6ff"
            : color === "yellow"
              ? "#fffbeb"
              : "#f3f4f6",
    color:
      color === "red"
        ? "#dc2626"
        : color === "green"
          ? "#16a34a"
          : color === "blue"
            ? "#2563eb"
            : color === "yellow"
              ? "#d97706"
              : "#374151",
  });

  return (
    <>
      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          padding: "12px 16px",
          overflowX: "auto",
          background: "white",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 16px",
              borderRadius: "10px",
              border: "none",
              fontSize: "13px",
              fontWeight: activeTab === tab.id ? "600" : "500",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontFamily: "'Outfit', sans-serif",
              background: activeTab === tab.id ? "#1a1a1a" : "#f3f4f6",
              color: activeTab === tab.id ? "white" : "#6b7280",
              transition: "all 0.2s",
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: "16px", maxWidth: "800px", margin: "0 auto" }}>
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}
          >
            <div
              style={{
                width: "30px",
                height: "30px",
                border: "3px solid #e5e7eb",
                borderTop: "3px solid #1a1a1a",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 12px",
              }}
            />
            Loading...
          </div>
        ) : (
          <>
            {/* Event Reports Tab */}
            {activeTab === "event-reports" &&
              (groupedEventReports.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 0",
                    color: "#9ca3af",
                  }}
                >
                  <p style={{ fontSize: "16px", fontWeight: "600" }}>
                    No event reports
                  </p>
                  <p style={{ fontSize: "14px" }}>All clear! 🎉</p>
                </div>
              ) : (
                groupedEventReports.map((group) => (
                  <div key={group.eventId} style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h3
                          style={{
                            margin: "0 0 4px",
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#1a1a1a",
                          }}
                        >
                          {group.eventTitle || "Unknown Event"}
                        </h3>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "#9ca3af",
                            wordBreak: "break-all",
                          }}
                        >
                          Event ID: {group.eventId}
                        </p>
                      </div>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          background:
                            group.pendingCount >= 3 ? "#fef2f2" : "#fffbeb",
                          color:
                            group.pendingCount >= 3 ? "#dc2626" : "#d97706",
                        }}
                      >
                        {group.pendingCount} pending
                      </span>
                    </div>

                    {/* Individual reports */}
                    <div style={{ marginBottom: "12px" }}>
                      {group.reports.slice(0, 5).map((r) => (
                        <div
                          key={r.id}
                          style={{
                            padding: "8px 12px",
                            background:
                              r.status === "pending" ? "#fefce8" : "#f9fafb",
                            borderRadius: "8px",
                            marginBottom: "6px",
                            fontSize: "13px",
                            border: `1px solid ${r.status === "pending" ? "#fef08a" : "#e5e7eb"}`,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "2px",
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", color: "#374151" }}
                            >
                              {r.reporter_name}
                            </span>
                            <span
                              style={{ fontSize: "11px", color: "#9ca3af" }}
                            >
                              {new Date(r.created_at).toLocaleDateString()}{" "}
                              {new Date(r.created_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}{" "}
                              ·{" "}
                              <span
                                style={{
                                  color:
                                    r.status === "pending"
                                      ? "#d97706"
                                      : r.status === "dismissed"
                                        ? "#6b7280"
                                        : "#16a34a",
                                }}
                              >
                                {r.status}
                              </span>
                            </span>
                          </div>
                          <p style={{ margin: 0, color: "#6b7280" }}>
                            <strong>{r.reason?.replace(/_/g, " ")}</strong>
                            {r.description &&
                            r.description !== "No additional details provided"
                              ? ` — ${r.description}`
                              : ""}
                          </p>
                        </div>
                      ))}
                      {group.reports.length > 5 && (
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#9ca3af",
                            margin: "4px 0 0",
                            textAlign: "center",
                          }}
                        >
                          +{group.reports.length - 5} more reports
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      <button
                        onClick={async () => {
                          await handleDismissReports("event", group.eventId);
                          // Also un-hide the event
                          await supabaseClient
                            .from("events")
                            .update({ hidden: false })
                            .eq("event_id", group.eventId);
                        }}
                        disabled={actionLoading === `dismiss-${group.eventId}`}
                        style={btnStyle("green")}
                      >
                        ✓ Dismiss & Restore
                      </button>
                      <button
                        onClick={() => handleHideEvent(group.eventId)}
                        disabled={actionLoading === `hide-${group.eventId}`}
                        style={btnStyle("yellow")}
                      >
                        👁️ Hide Event
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(group.eventId)}
                        disabled={actionLoading === `delete-${group.eventId}`}
                        style={btnStyle("red")}
                      >
                        🗑️ Delete Event
                      </button>
                      <button
                        onClick={() => {
                          const creatorReport = group.reports.find(
                            (r) => r.reporter_id,
                          );
                          // We need the event's creator_id - fetch it
                          (async () => {
                            const { data: eventData } = await supabaseClient
                              .from("events")
                              .select("creator_id, created_by")
                              .eq("event_id", group.eventId)
                              .single();
                            if (eventData) {
                              handleBanUser(
                                eventData.creator_id,
                                eventData.created_by || "Unknown",
                              );
                            }
                          })();
                        }}
                        disabled={actionLoading?.startsWith("ban-")}
                        style={btnStyle("red")}
                      >
                        🚫 Ban Creator
                      </button>
                    </div>
                  </div>
                ))
              ))}

            {/* User Reports Tab */}
            {activeTab === "user-reports" &&
              (groupedUserReports.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 0",
                    color: "#9ca3af",
                  }}
                >
                  <p style={{ fontSize: "16px", fontWeight: "600" }}>
                    No user reports
                  </p>
                  <p style={{ fontSize: "14px" }}>All clear! 🎉</p>
                </div>
              ) : (
                groupedUserReports.map((group) => (
                  <div key={group.userId} style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <h3
                          style={{
                            margin: "0 0 4px",
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#1a1a1a",
                          }}
                        >
                          {group.userName}
                        </h3>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "#9ca3af",
                          }}
                        >
                          {group.userEmail || group.userId?.substring(0, 8)}
                        </p>
                      </div>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "600",
                          background:
                            group.pendingCount >= 3 ? "#fef2f2" : "#fffbeb",
                          color:
                            group.pendingCount >= 3 ? "#dc2626" : "#d97706",
                        }}
                      >
                        {group.pendingCount} pending
                      </span>
                    </div>

                    {group.reports.slice(0, 5).map((r) => (
                      <div
                        key={r.id}
                        style={{
                          padding: "8px 12px",
                          background:
                            r.status === "pending" ? "#fefce8" : "#f9fafb",
                          borderRadius: "8px",
                          marginBottom: "6px",
                          fontSize: "13px",
                          border: `1px solid ${r.status === "pending" ? "#fef08a" : "#e5e7eb"}`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "2px",
                          }}
                        >
                          <span style={{ fontWeight: "600", color: "#374151" }}>
                            {r.reporter_name}
                          </span>
                          <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                            {new Date(r.created_at).toLocaleDateString()}{" "}
                            {new Date(r.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}{" "}
                            · {r.status}
                          </span>
                        </div>
                        <p style={{ margin: 0, color: "#6b7280" }}>
                          <strong>{r.reason?.replace(/_/g, " ")}</strong>
                          {r.description &&
                          r.description !== "No additional details provided"
                            ? ` — ${r.description}`
                            : ""}
                        </p>
                      </div>
                    ))}

                    <div
                      style={{ display: "flex", gap: "8px", marginTop: "12px" }}
                    >
                      <button
                        onClick={() =>
                          handleDismissReports("user", group.userId)
                        }
                        style={btnStyle("green")}
                      >
                        ✓ Dismiss All
                      </button>
                      <button
                        onClick={() =>
                          handleBanUser(group.userId, group.userName)
                        }
                        style={btnStyle("red")}
                      >
                        🚫 Ban User
                      </button>
                    </div>
                  </div>
                ))
              ))}

            {/* Hidden Events Tab */}
            {activeTab === "hidden-events" &&
              (hiddenEvents.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 0",
                    color: "#9ca3af",
                  }}
                >
                  <p style={{ fontSize: "16px", fontWeight: "600" }}>
                    No hidden events
                  </p>
                  <p style={{ fontSize: "14px" }}>All events are visible</p>
                </div>
              ) : (
                hiddenEvents.map((event) => (
                  <div key={event.event_id} style={cardStyle}>
                    <div style={{ marginBottom: "12px" }}>
                      <h3
                        style={{
                          margin: "0 0 4px",
                          fontSize: "15px",
                          fontWeight: "600",
                          color: "#1a1a1a",
                        }}
                      >
                        {event.title}
                      </h3>
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        📍 {event.place_name}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "12px",
                          color: "#9ca3af",
                        }}
                      >
                        By: {event.created_by} · Created:{" "}
                        {new Date(event.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => handleUnhideEvent(event.event_id)}
                        style={btnStyle("green")}
                      >
                        👁️ Restore Event
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.event_id)}
                        style={btnStyle("red")}
                      >
                        🗑️ Delete Permanently
                      </button>
                    </div>
                  </div>
                ))
              ))}

            {/* Banned Users Tab */}
            {activeTab === "banned-users" &&
              (bannedUsers.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 0",
                    color: "#9ca3af",
                  }}
                >
                  <p style={{ fontSize: "16px", fontWeight: "600" }}>
                    No banned users
                  </p>
                  <p style={{ fontSize: "14px" }}>No users have been banned</p>
                </div>
              ) : (
                bannedUsers.map((user) => (
                  <div key={user.id} style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      {user.profile_picture && (
                        <img
                          src={user.profile_picture}
                          alt=""
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      <div>
                        <h3
                          style={{
                            margin: "0 0 2px",
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#1a1a1a",
                          }}
                        >
                          {user.name}
                        </h3>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "#9ca3af",
                          }}
                        >
                          {user.email}
                        </p>
                      </div>
                    </div>
                    {user.ban_reason && (
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "#fef2f2",
                          borderRadius: "8px",
                          marginBottom: "12px",
                          fontSize: "13px",
                        }}
                      >
                        <strong style={{ color: "#dc2626" }}>Reason:</strong>{" "}
                        <span style={{ color: "#6b7280" }}>
                          {user.ban_reason}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => handleUnbanUser(user.id, user.name)}
                      style={btnStyle("green")}
                    >
                      ✓ Unban User
                    </button>
                  </div>
                ))
              ))}
          </>
        )}
      </div>

      {/* Ban User Modal */}
      {banModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setBanModal(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "400px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "#fef2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dc2626"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1a1a1a",
                  }}
                >
                  Ban {banModal.userName}
                </h3>
                <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
                  This will suspend their account and hide all their events
                </p>
              </div>
            </div>
            <textarea
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Enter the reason for banning this user..."
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                fontSize: "14px",
                fontFamily: "'Outfit', sans-serif",
                resize: "none",
                height: "100px",
                boxSizing: "border-box",
                outline: "none",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#dc2626")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              autoFocus
            />
            <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
              <button
                onClick={() => setBanModal(null)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  background: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmBanUser}
                disabled={!banReason.trim()}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: banReason.trim() ? "#dc2626" : "#e5e7eb",
                  color: banReason.trim() ? "white" : "#9ca3af",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: banReason.trim() ? "pointer" : "not-allowed",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unban User Modal */}
      {unbanModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setUnbanModal(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "380px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "#f0fdf4",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#16a34a"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: "16px",
                fontWeight: "700",
                color: "#1a1a1a",
              }}
            >
              Unban {unbanModal.userName}?
            </h3>
            <p
              style={{ margin: "0 0 20px", fontSize: "14px", color: "#6b7280" }}
            >
              This user will regain full access to the app.
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setUnbanModal(null)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  background: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmUnbanUser}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#16a34a",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Unban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Event Modal */}
      {deleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setDeleteModal(null)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "380px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dc2626"
                strokeWidth="2"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: "16px",
                fontWeight: "700",
                color: "#1a1a1a",
              }}
            >
              Delete Event?
            </h3>
            <p
              style={{ margin: "0 0 20px", fontSize: "14px", color: "#6b7280" }}
            >
              This action is permanent and cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={() => setDeleteModal(null)}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  background: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteEvent}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "10px",
                  border: "none",
                  background: "#dc2626",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

window.isAdmin = isAdmin;
window.checkDailyEventLimit = checkDailyEventLimit;

// Real-time event list updater
window.startEventListUpdater = (loadUserEventsCallback) => {
  if (window.eventListUpdateInterval) {
    clearInterval(window.eventListUpdateInterval);
  }

  window.eventListUpdateInterval = setInterval(() => {
    // Reload and re-sort events
    if (typeof loadUserEventsCallback === "function") {
      loadUserEventsCallback();
    }
  }, 5000); // Update every 5 seconds
};

// Stop the updater when profile panel closes
window.stopEventListUpdater = () => {
  if (window.eventListUpdateInterval) {
    clearInterval(window.eventListUpdateInterval);
    window.eventListUpdateInterval = null;
  }
};

export default function EventMeetupApp() {
  // Redirect static legal pages before any app logic or auth
  const _path = window.location.pathname.replace(/\/+$/, '');
  if (_path === '/terms' || _path === '/terms.html') {
    window.location.replace('/terms.html');
    return null;
  }
  if (_path === '/privacy' || _path === '/privacy.html') {
    window.location.replace('/privacy.html');
    return null;
  }

  // Check maintenance mode
  const isMaintenanceMode = MAINTENANCE_CONFIG.enabled;
  const [currentView, setCurrentView] = useState(null);
  const [isGuestView, setIsGuestView] = useState(false);
  const [guestEventId, setGuestEventId] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    profilePicture: null, // Preview (base64)
    profilePictureFile: null, // Actual file for upload
    description: "",
  });
  const [error, setError] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const saved = localStorage.getItem("selectedCategories");
    return saved ? JSON.parse(saved) : []; // Empty = show all
  });

  const [profilePanelInitialView, setProfilePanelInitialView] = useState('landing');
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState("");
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");

  // Online/Offline Status Detection
  const [isOnline, setIsOnline] = useState(true);

const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] =
    useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
    // Bridge: let notifications.js update the React-managed badge count
window._setNotificationCount = setNotificationCount;
  window._setNotificationsOpen = setIsNotificationsPanelOpen;
  window._setIsToolsMenuOpen = setIsToolsMenuOpen;
  window._setIsMobileSearchOpen = setIsMobileSearchOpen;
  window._setUserLocationFromBanner = (loc) => {
    setUserLocation(loc);
    window.currentUserLocation = loc;
  };

  // Simple function to get organizer data
  const getOrganizerData = async (creatorId) => {
    // If it's the current user, return their data immediately
    if (creatorId === currentUser?.uid) {
      return {
        name: currentUser.profile.name,
        profilePicture: currentUser.profile.profilePicture,
      };
    }

    // Otherwise fetch from database
    try {
      const { data, error } = await supabase
        .from("users")
        .select("name, profile_picture")
        .eq("uid", creatorId)
        .single();

      if (!error && data) {
        return {
          name: data.name,
          profilePicture: data.profile_picture,
        };
      }
    } catch (err) {
      console.error("Error fetching organizer:", err);
    }

    // Fallback
    return { name: "Unknown", profilePicture: null };
  };

  useEffect(() => {
    if (currentUser) {
      window.currentUser = currentUser;
    }
  }, [currentUser]);

  useEffect(() => {
    const isMapView = currentView === 'map' || currentView === 'guest-map';
    document.body.classList.toggle('map-active', isMapView);
    if (!isMapView) setIsMapReady(false);
    return () => document.body.classList.remove('map-active');
  }, [currentView]);

  useEffect(() => {
    window.setMapReady = () => setIsMapReady(true);
    // Safety fallback: never leave spinner up forever if something silently fails
    const fallback = setTimeout(() => {
      if (window.setMapReady) window.setMapReady();
    }, 10000);
    return () => {
      window.setMapReady = null;
      clearTimeout(fallback);
    };
  }, []);

  // Deep link handler - open event from shared URL
  // Supports both:
  //   /?event=<id>            (legacy)
  //   /event/<id>             (new path-based, matches Android intent filter)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryEventId = params.get("event");
    const pathMatch = (window.location.pathname || "").match(/\/event\/([^/?#]+)/);
    const sharedEventId = queryEventId || (pathMatch && pathMatch[1]);

    if (!sharedEventId) return;

    // If still loading auth, wait
    if (isLoadingAuth) return;

    // If user is logged in and on map, open event directly
    if (currentView === "map" && currentUser?.uid) {
      // Clean the URL without reloading
      window.history.replaceState({}, "", window.location.pathname);

      // Wait for map to be ready, then open event
      setTimeout(async () => {
        try {
          const supabaseClient = window.supabase || supabase;
          const { data: eventData, error } = await supabaseClient
            .from("events")
            .select("*")
            .eq("event_id", sharedEventId)
            .single();

          if (error || !eventData) {
            window.showToast("Event not found or has been deleted", "error");
            return;
          }

          const formattedEvent = {
            eventId: eventData.event_id,
            placeId: eventData.place_id,
            placeName: eventData.place_name,
            placeAddress: eventData.place_address,
            placePhotoUrl: eventData.place_photo_url,
            title: eventData.title,
            description: eventData.description,
            startDateTime: eventData.start_date_time,
            endDateTime: eventData.end_date_time,
            peopleLimit: eventData.people_limit,
            creatorId: eventData.creator_id,
            participants: eventData.participants,
            latitude: eventData.latitude,
            longitude: eventData.longitude,
            require_approve: eventData.require_approve,
            pending_requests: eventData.pending_requests || [],
          };

          const photoUrl =
            eventData.place_photo_url || "https://via.placeholder.com/200";
          await window.showEventDetailsPanel(formattedEvent, photoUrl);
        } catch (err) {
          console.error("❌ Deep link error:", err);
          window.showToast("Failed to load shared event", "error");
        }
      }, 1500);
      return;
    }

    // If user is not logged in and we have a shared event, show guest view
    // This is a fallback - primary handling is in auth handler
    if (
      !currentUser?.uid &&
      !isLoadingAuth &&
      currentView !== "guest-map" &&
      currentView !== null
    ) {
      window.guestMapInitialized = false; // Reset to ensure fresh init
      setGuestEventId(sharedEventId);
      setIsGuestView(true);
      setCurrentView("guest-map");
    }
  }, [currentView, currentUser?.uid, isLoadingAuth]);

  // Sync button positions and slider bottom on resize crossing 800px
useEffect(() => {
  const handleResize = () => {
    const slider = document.getElementById('rangeSliderPanel');
    const isOpen = slider?.dataset.snap && slider.dataset.snap !== 'closed';
    const isMobile = window.innerWidth < 800;

    if (slider && isOpen) {
      if (isMobile) {
        slider.style.bottom = 'calc(var(--footer-height, 46px) + 44px + 10px)';
      } else {
        slider.style.bottom = '46px';
      }
    }
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  // Listen for notifications
  useEffect(() => {
    if (!currentUser?.uid) return;

    const handleNotificationsLoaded = (e) => {
      setNotifications(e.detail.notifications);
    };

    window.addEventListener("notificationsLoaded", handleNotificationsLoaded);

    // Load notifications immediately
    window.loadNotifications();

    // Poll every 15 seconds
    const interval = setInterval(() => {
      window.loadNotifications();
      window.updateNotificationCount();
    }, 15000);

    return () => {
      window.removeEventListener(
        "notificationsLoaded",
        handleNotificationsLoaded,
      );
      clearInterval(interval);
    };
  }, [currentUser?.uid]);

  // Keep panel heights above the map footer + below the nav bar dynamically
  useEffect(() => {
    const update = () => {
      const footer = document.getElementById('map-footer');
      if (footer) {
        const h = footer.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--footer-height', `${Math.ceil(h)}px`);
      }
      const header = document.getElementById('map-header');
      if (header) {
        // Bottom edge of the nav bar relative to the viewport — fullscreen
        // panels start here so the nav bar (which sits above them) doesn't
        // cover their content.
        const bottom = header.getBoundingClientRect().bottom;
        document.documentElement.style.setProperty('--nav-bottom', `${Math.ceil(bottom)}px`);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    const footer = document.getElementById('map-footer');
    const header = document.getElementById('map-header');
    if (footer) ro.observe(footer);
    if (header) ro.observe(header);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [currentView]);

  const [hasUnreadChats, setHasUnreadChats] = useState(false);

  useEffect(() => {
    // Initial check
    setHasUnreadChats(window.hasUnreadChats || false);

    // Listen for changes
    const handleUnreadChange = () => {
      setHasUnreadChats(window.hasUnreadChats || false);
    };

    window.addEventListener("unreadChatsChanged", handleUnreadChange);

    return () => {
      window.removeEventListener("unreadChatsChanged", handleUnreadChange);
    };
  }, []);

  useEffect(() => {
    // Update online status in real-time
    const updateOnlineStatus = async () => {
      if (!currentUser?.uid) return;

      const status = navigator.onLine ? "online" : "offline";

      try {
        // Update status in Supabase
        await supabase
          .from("users")
          .update({
            online_status: status,
            last_seen: new Date().toISOString(),
          })
          .eq("id", currentUser.uid);

        setIsOnline(navigator.onLine);
      } catch (error) {
        console.error("Error updating online status:", error);
      }
    };

    // Initial status update
    updateOnlineStatus();

    // Listen for online/offline events
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    // Update status every 30 seconds while active
    const statusInterval = setInterval(updateOnlineStatus, 30000);

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateOnlineStatus();
      } else {
        // Mark as offline when tab/app goes to background
        if (currentUser?.uid) {
          supabase
            .from("users")
            .update({
              online_status: "offline",
              last_seen: new Date().toISOString(),
            })
            .eq("id", currentUser.uid);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Handle beforeunload (page close)
    const handleBeforeUnload = () => {
      if (currentUser?.uid) {
        // Use sendBeacon for reliable status update on page close
        const data = JSON.stringify({
          user_id: currentUser.uid,
          online_status: "offline",
          last_seen: new Date().toISOString(),
        });

        navigator.sendBeacon(
          `${supabase.supabaseUrl}/rest/v1/users?id=eq.${currentUser.uid}`,
          data,
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearInterval(statusInterval);
    };
  }, [currentUser]);

  // Define custom range values
  const rangeValues = [
    0.1, // 100m
    0.2, // 200m
    0.5, // 500m
    1, // 1km
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10, // 2-10km
    11,
    12,
    13,
    14,
    15,
    16,
    17,
    18,
    19,
    20, // 11-20km
  ];

  const [searchSelectedLocation, setSearchSelectedLocation] = useState(null);
  const searchPinRef = useRef(null);
  window.searchPinRef = searchPinRef;

  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);
  const [isChatsListPanelOpen, setIsChatsListPanelOpen] = useState(false);
  const [chatsListPanelAnimating, setChatsListPanelAnimating] = useState(""); // '', 'entering', 'visible', 'exiting'

  // Chats panel slide animation controller
  useEffect(() => {
    if (isChatsListPanelOpen) {
      setChatsListPanelAnimating("entering");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setChatsListPanelAnimating("visible");
        });
      });
    }
  }, [isChatsListPanelOpen]);

  const [profileBackground, setProfileBackground] = useState(() => {
    if (currentUser?.profile?.background) {
      return currentUser.profile.background;
    }
    return getRandomGradient();
  });

  // Fix Leaflet's default icon paths
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Store original event data for cancel
  let originalEventData = null;

  window.calculateEventStatus = calculateEventStatus;

  window.showGuestEventDetails = (eventData) => {
    window.dispatchEvent(
      new CustomEvent("openGuestEventPanel", { detail: eventData }),
    );
  };

  window.cleanupGuestMap = () => {
    if (window.guestMapRef) {
      try {
        window.guestMapRef.remove();
      } catch (e) {
        console.warn("Error removing guest map:", e);
      }
      window.guestMapRef = null;
    }
    window.guestMapInitialized = false;
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifTime.toLocaleDateString();
  };

  window.setIsProfilePanelOpen = setIsProfilePanelOpen;
  window.setIsChatsListPanelOpen = setIsChatsListPanelOpen;
  window.setChatsListPanelAnimating = setChatsListPanelAnimating;

  // Also check when app loads
  window.checkUnreadMessages();

  window.formatTimeAgo = formatTimeAgo;

const handleSearch = async () => {
    const query = searchQuery.trim();

    // Empty or too short — clear everything immediately
    if (!query || query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Serve from cache if query unchanged
    if (query === lastSearchQuery.current && cachedResults.current.length > 0) {
      setSearchResults(cachedResults.current);
      setIsSearching(false);
      return;
    }

    // Cancellation token — each search gets a unique ID
    // If a newer search starts, the old one sees its token is stale and bails out
    const searchId = Date.now();
    window._currentSearchId = searchId;

    const isStale = () => window._currentSearchId !== searchId;

    setIsSearching(true);
// Don't clear existing results — keep showing old ones until new ones arrive

    const dedupeAndSort = (results) => {
      const unique = [];
      results.forEach((r) => {
        const dup = unique.some((e) =>
          calculateDistance(parseFloat(r.lat), parseFloat(r.lon), parseFloat(e.lat), parseFloat(e.lon)) < 0.05
        );
        if (!dup) unique.push(r);
      });
      if (userLocation) {
        unique.sort((a, b) =>
          calculateDistance(userLocation.lat, userLocation.lng, parseFloat(a.lat), parseFloat(a.lon)) -
          calculateDistance(userLocation.lat, userLocation.lng, parseFloat(b.lat), parseFloat(b.lon))
        );
      }
      return unique;
    };

    const filterPOI = (results, source) => results.filter((r) => {
      if (source === "Overpass") return true;
      const cls = (r.class || "").toLowerCase();
      if (["amenity","shop","leisure","tourism","historic","attraction","sport"].includes(cls)) return true;
      if (["highway","boundary","place"].includes(cls)) return false;
      return true;
    });

    // Accumulated results across all sources
    let accumulated = [];

    const mergeAndShow = (newResults, source) => {
      if (isStale()) return;
      if (ignoreSearchResults.current) return;
      accumulated = dedupeAndSort([...accumulated, ...filterPOI(newResults, source)]);
      setSearchResults([...accumulated]);
      lastSearchQuery.current = query;
      cachedResults.current = [...accumulated];
    };

    try {
      // Phase 1: Photon — fast (~300-600ms), shows results immediately
      const words = query.split(/\s+/).filter(w => w.length >= 3);
      const photonQueries = [...new Set([query, ...words])];
      const photonResultSets = await Promise.all(photonQueries.map(q => searchPhoton(q)));
      if (!isStale()) {
        mergeAndShow(photonResultSets.flat(), "Photon");
        // If Photon found results, hide spinner now — Overpass will merge in silently
        if (accumulated.length > 0) setIsSearching(false);
      }

      // Phase 2: Overpass — slower but finds mid-word matches
      try {
        const overpassResults = await searchOverpass(query, userLocation);
        if (!isStale()) {
          mergeAndShow(overpassResults, "Overpass");
        }
      } catch (_) {
        // Overpass failed silently — Photon results still show
      }

    } catch (error) {
      console.error("❌ Search error:", error);
    } finally {
      if (!isStale()) setIsSearching(false);
    }
  };

  const searchNominatim = async (query) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(query)}&` +
          `format=json&` +
          `limit=15&` +
          `countrycodes=pl&` +
          `addressdetails=1`,
        {
          headers: {
            Accept: "application/json",
            "User-Agent": "LinkUpApp/1.0",
          },
        },
      );

      if (!response.ok) {
        console.warn("Nominatim request failed:", response.status);
        return [];
      }

      const data = await response.json();

      return data
        .filter((result) => {
          // Filter by distance if user location available
          if (userLocation) {
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              parseFloat(result.lat),
              parseFloat(result.lon),
            );
            return distance <= 20;
          }
          return true;
        })
        .map((result) => {
          // Extract address parts
          const addr = result.address || {};
          const street = addr.road || addr.street || "";
          const houseNumber = addr.house_number || "";
          const city = addr.city || addr.town || addr.village || "";
          const postcode = addr.postcode || "";

          // Combine street and number without comma
          const streetWithNumber =
            street && houseNumber
              ? `${street} ${houseNumber}`
              : street || houseNumber;
          const addressParts = [streetWithNumber, city, postcode].filter(
            Boolean,
          );

          return {
            lat: result.lat,
            lon: result.lon,
            display_name: result.name || result.display_name.split(",")[0],
            address: addressParts.join(", "),
            type: result.type || "place",
            class: result.class || "",
            source: "nominatim",
            tags: {
              osm_key: result.class,
              osm_value: result.type,
            },
          };
        });
    } catch (error) {
      console.error("❌ Nominatim error:", error);
      return [];
    }
  };

  // Start the real-time updates (only once)
  if (!window.statusUpdateInterval) {
    window.startRealtimeStatusUpdates();
  }

  const searchPhoton = async (query) => {
    try {
      let url = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=15&lang=en`;

      if (userLocation) {
        url += `&lat=${userLocation.lat}&lon=${userLocation.lng}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        console.warn("Photon request failed:", response.status);
        return [];
      }

      const data = await response.json();

      if (!data.features || data.features.length === 0) {
        return [];
      }

      return data.features
        .filter((feature) => {
          const country =
            feature.properties.country || feature.properties.countrycode;
          const isPoland =
            country === "Poland" ||
            country === "Polska" ||
            country === "PL" ||
            country === "pl";

          if (userLocation && isPoland) {
            const coords = feature.geometry.coordinates;
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              coords[1],
              coords[0],
            );
            return distance <= 20;
          }

          return isPoland;
        })
        .map((feature) => {
          const props = feature.properties;
          const coords = feature.geometry.coordinates;

          const name = props.name || "Unnamed";
          const street = props.street || "";
          const housenumber = props.housenumber || "";
          const city = props.city || props.town || props.village || "";
          const postcode = props.postcode || "";

          // Combine street and number without comma
          const streetWithNumber =
            street && housenumber
              ? `${street} ${housenumber}`
              : street || housenumber;
          const addressParts = [streetWithNumber, city, postcode].filter(
            Boolean,
          );
          const displayName =
            addressParts.length > 0
              ? `${name}, ${addressParts.join(", ")}`
              : name;

          const type = props.type || props.osm_value || "place";

          return {
            lat: coords[1].toString(),
            lon: coords[0].toString(),
            display_name: name,
            address: addressParts.join(", "),
            type: type,
            class: props.osm_key || "",
            source: "photon",
            tags: props,
          };
        });
    } catch (error) {
      console.error("❌ Photon error:", error);
      return [];
    }
  };

const searchOverpass = async (query, userLoc) => {
    try {
      const searchLat = userLoc?.lat || 52.2297;
      const searchLng = userLoc?.lng || 21.0122;
      const radius = 20000;

      // Category keyword → OSM tag map (English + Polish)
      const CATEGORY_MAP = {
        // Cinema
        cinema: 'amenity=cinema', kino: 'amenity=cinema',
        // Restaurant / food
        restaurant: 'amenity=restaurant', restauracja: 'amenity=restaurant',
        'fast food': 'amenity=fast_food', fastfood: 'amenity=fast_food',
        mcdonalds: 'amenity=fast_food', kfc: 'amenity=fast_food',
        // Cafe
        cafe: 'amenity=cafe', kawiarnia: 'amenity=cafe', kawa: 'amenity=cafe',
        // Bar / pub
        bar: 'amenity=bar', pub: 'amenity=pub', piwo: 'amenity=pub',
        // Park
        park: 'leisure=park',
        // Gym / sports
        gym: 'leisure=fitness_centre', silownia: 'leisure=fitness_centre',
        fitness: 'leisure=fitness_centre',
        sport: 'leisure=sports_centre', sports: 'leisure=sports_centre',
        // Shop
        shop: 'shop=convenience', sklep: 'shop=supermarket',
        supermarket: 'shop=supermarket', market: 'shop=supermarket',
        // Pharmacy
        pharmacy: 'amenity=pharmacy', apteka: 'amenity=pharmacy',
        // Hospital / doctor
        hospital: 'amenity=hospital', szpital: 'amenity=hospital',
        doctor: 'amenity=doctors', lekarz: 'amenity=doctors',
        // Fuel
        fuel: 'amenity=fuel', petrol: 'amenity=fuel', stacja: 'amenity=fuel',
        benzyna: 'amenity=fuel',
        // Parking
        parking: 'amenity=parking', parkowanie: 'amenity=parking',
        // Hotel
        hotel: 'tourism=hotel', hostel: 'tourism=hostel',
        // Museum
        museum: 'tourism=museum', muzeum: 'tourism=museum',
        // Theatre
        theatre: 'amenity=theatre', theater: 'amenity=theatre',
        teatr: 'amenity=theatre',
        // Nightclub
        nightclub: 'amenity=nightclub', club: 'amenity=nightclub',
        dyskoteka: 'amenity=nightclub', impreza: 'amenity=nightclub',
        // Beach
        beach: 'natural=beach', plaza: 'natural=beach', plaża: 'natural=beach',
        // ATM / bank
        atm: 'amenity=atm', bankomat: 'amenity=atm',
        bank: 'amenity=bank',
        // School / library
        library: 'amenity=library', biblioteka: 'amenity=library',
        school: 'amenity=school', szkola: 'amenity=school', szkoła: 'amenity=school',
        // Playground
        playground: 'leisure=playground', plac: 'leisure=playground',
      };

      const lowerQuery = query.toLowerCase().trim();

      // Check if the query matches a known category
      const categoryTag = CATEGORY_MAP[lowerQuery];

      let overpassQuery;

      if (categoryTag) {
        // Category search — find all places of this type nearby, no name filter
        const [tagKey, tagValue] = categoryTag.split('=');
        overpassQuery = `
        [out:json][timeout:10];
        (
          node["${tagKey}"="${tagValue}"](around:${radius},${searchLat},${searchLng});
          way["${tagKey}"="${tagValue}"](around:${radius},${searchLat},${searchLng});
        );
        out center 25;
        `;
      } else {
        // Name substring search — find anything with this in its name
        overpassQuery = `
        [out:json][timeout:10];
        (
          node["name"~".*${query}",i](around:${radius},${searchLat},${searchLng});
          way["name"~".*${query}",i](around:${radius},${searchLat},${searchLng});
        );
        out center 25;
        `;
      }

      const response = await fetch("https://overpass-api.de/api/interpreter", {
        method: "POST",
        body: overpassQuery,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      if (!response.ok) return [];

      const data = await response.json();
      if (!data.elements || data.elements.length === 0) return [];

      return data.elements
        .map((element) => {
          const lat = element.lat || element.center?.lat;
          const lon = element.lon || element.center?.lon;
          if (!lat || !lon) return null;

          if (userLoc) {
            const distance = calculateDistance(userLoc.lat, userLoc.lng, lat, lon);
            if (distance > 20) return null;
          }

          const name = element.tags?.name || "Unnamed";
          const type = detectPlaceCategory(element.tags, name);
          const street = element.tags?.["addr:street"] || "";
          const housenumber = element.tags?.["addr:housenumber"] || "";
          const city = element.tags?.["addr:city"] || "";
          const postcode = element.tags?.["addr:postcode"] || "";
          const streetWithNumber = street && housenumber ? `${street} ${housenumber}` : street || housenumber;
          const addressParts = [streetWithNumber, city, postcode].filter(Boolean);
          const address = addressParts.length > 0 ? addressParts.join(", ") : type;

          return {
            lat: lat.toString(),
            lon: lon.toString(),
            display_name: name,
            address,
            type,
            source: "overpass",
            tags: element.tags,
          };
        })
        .filter((r) => r !== null);
    } catch (error) {
      console.error("❌ Overpass error:", error);
      return [];
    }
  };

  // Handle clicking a search result - Close on map click
  const handleResultClick = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    // Move map view
    if (googleMapRef.current) {
      googleMapRef.current.setView([lat, lng], 17);
    }

    // Remove previous pin
    if (searchPinRef.current) {
      searchPinRef.current.remove();
      searchPinRef.current = null;
    }

    setSearchResults([]);

    // Stop any ongoing search
    setIsSearching(false);

    lastSelectionTime.current = Date.now();

    // Ignore any pending search results
    ignoreSearchResults.current = true;

    // Blur the input to prevent auto-focus
    document.activeElement?.blur();

    // Extract place info from result

    const placeName =
      result.tags?.name || result.display_name.split(",")[0] || "Unnamed Place";

    // Extract proper address
    let placeAddress = "";

    // Check if result.address exists and is a STRING
    if (result.address && typeof result.address === "string") {
      placeAddress = result.address;
    }
    // If no address field, try extracting from display_name
    else if (result.display_name) {
      const parts = result.display_name.split(",").map((p) => p.trim());

      if (parts.length > 1) {
        // Join everything except the first part
        placeAddress = parts.slice(1).join(", ");
      } else {
        placeAddress = result.display_name;
      }
    }

    const placeType = detectPlaceCategory(result.tags, placeName);
    const placeEmoji = getPOIIcon(placeType);

    // Check if there are already events at this location
    const existingEventsHere = (window.allEvents || []).filter(ev => {
      if (ev.archived) return false;
      if (!ev.latitude || !ev.longitude) return false;
      return Math.abs(ev.latitude - lat) < 0.0001 && Math.abs(ev.longitude - lng) < 0.0001;
    });

    if (existingEventsHere.length > 0) {
      // Events already exist here — just fly to location, user can interact with the marker
      setSearchQuery('');
      setSearchResults([]);
      return;
    }

    const customPlaceId =
      result.source === "overpass"
        ? `osm_${result.tags?.id || Date.now()}`
        : `place_${Date.now()}`;

    // Get modern SVG icon
    const modernIconSVG = getModernPlaceIcon(placeType);

    // Create a pin with modern icon
    const pinIcon = L.divIcon({
      className: "search-pin-marker",
      html: modernIconSVG,
      iconSize: [48, 48],
      iconAnchor: [24, 48],
    });

    searchPinRef.current = L.marker([lat, lng], {
      icon: pinIcon,
      zIndexOffset: 500,
    }).addTo(googleMapRef.current);

    // Photo URL for event creation
    const photoUrl =
      result.tags?.image ||
      `data:image/svg+xml,${encodeURIComponent(modernIconSVG)}`;

    // Create popup content
    const popupContent = `
    <div style="min-width: 220px; font-family: 'Roboto', Arial, sans-serif;">
      <div style="display: flex; align-items: start; gap: 12px; margin-bottom: 12px;">
        <div style="flex-shrink: 0;">
          ${modernIconSVG}
        </div>
        <div style="flex: 1; min-width: 0;">
          <h3 style="margin: 0 0 4px 0; font-size: 15px; font-weight: 600; color: #202124; line-height: 1.3;">${placeName}</h3>
          <p style="margin: 0; font-size: 11px; color: #5f6368; text-transform: capitalize;">${placeType.replace(/_/g, " ")}</p>
        </div>
      </div>
      ${
        placeAddress !== placeName
          ? `
        <p style="margin: 0 0 12px 0; font-size: 12px; color: #5f6368; display: flex; align-items: start; gap: 4px;">
          <span style="flex-shrink: 0;">📍</span>
          <span style="line-height: 1.4;">${placeAddress}</span>
        </p>
      `
          : ""
      }
      <button 
  onclick="window.handleLinkUp('${customPlaceId}', '${placeName.replace(/'/g, "\\'")}', '${photoUrl}', 'https://www.google.com/maps/search/?api=1&query=${lat},${lng}', '${placeAddress.replace(/'/g, "\\'")}', ${lat}, ${lng}, '${placeEmoji}', '${placeType}')"
        style="
          width: 100%; 
          background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
          color: white; 
          border: none; 
          padding: 10px 16px; 
          border-radius: 8px; 
          font-size: 14px; 
          font-weight: 600; 
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          transition: all 0.2s;
        "
        onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 12px rgba(0,0,0,0.3)'"
        onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 8px rgba(0,0,0,0.2)'"
      >
        Link Up
      </button>
    </div>
  `;

    // Bind popup and open it immediately
    searchPinRef.current
      .bindPopup(popupContent, {
        maxWidth: 280,
        className: "modern-popup",
        closeOnClick: true,
      })
      .openPopup();

    // Listen for popup close and remove the pin
    searchPinRef.current.on("popupclose", () => {
      if (searchPinRef.current) {
        searchPinRef.current.remove();
        searchPinRef.current = null;
      }
    });

    const handleMapClick = () => {
      if (searchPinRef.current) {
        searchPinRef.current.closePopup();
      }
    };

    googleMapRef.current.once("click", handleMapClick);

    // Store location globally for event creation
    window.currentPlaceLocation = { lat, lng };
  };

  // Reverse geocode to detect place type at coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?` +
          `lat=${lat}&lon=${lng}&` +
          `format=json&` +
          `addressdetails=1&` +
          `extratags=1`,
        {
          headers: {
            Accept: "application/json",
            "User-Agent": "LinkUpApp/1.0",
          },
        },
      );

      if (!response.ok) return null;

      const data = await response.json();

      // Detect place type from OSM data
      const tags = data.extratags || {};
      const osmType = data.type || "";
      const category = data.category || "";

      let placeType = "place";

      // Try to detect from amenity
      if (tags.amenity) {
        placeType = tags.amenity;
      } else if (category === "amenity" && osmType) {
        placeType = osmType;
      } else if (tags.shop) {
        placeType = tags.shop;
      } else if (tags.leisure) {
        placeType = tags.leisure;
      } else if (tags.tourism) {
        placeType = tags.tourism;
      }

      // Use smart detection
      const detectedType = detectPlaceCategory(tags, data.display_name);

      return {
        name: data.name || data.display_name.split(",")[0],
        address: data.display_name,
        type: detectedType,
        emoji: getPOIIcon(detectedType),
        tags: tags,
      };
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return null;
    }
  };

  useEffect(() => {
    if (currentUser?.profile?.background) {
      setProfileBackground(currentUser.profile.background);
    } else {
      const randomGradient = getRandomGradient();
      setProfileBackground(randomGradient);

      if (currentUser?.uid) {
        updateUserProfile(currentUser.uid, { background: randomGradient });
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const handleBackgroundUpdate = async (e) => {
      const newColor = e.detail.color;
      setProfileBackground(newColor);

      // Also update currentUser state
      if (currentUser) {
        setCurrentUser({
          ...currentUser,
          profile: {
            ...currentUser.profile,
            background: newColor,
          },
        });

        // Update global state
        if (window.currentAppUser) {
          window.currentAppUser = {
            ...window.currentAppUser,
            profile: {
              ...window.currentAppUser.profile,
              background: newColor,
            },
          };
        }
      }
    };

    window.addEventListener("updateProfileBackground", handleBackgroundUpdate);

    return () => {
      window.removeEventListener(
        "updateProfileBackground",
        handleBackgroundUpdate,
      );
    };
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      window.currentAppUser = currentUser;
      window.currentUser = currentUser;
    }
  }, [currentUser]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
    @keyframes pinBounce {
      0% { transform: translateY(-100px) scale(0); }
      60% { transform: translateY(0) scale(1.1); }
      80% { transform: translateY(-10px) scale(0.95); }
      100% { transform: translateY(0) scale(1); }
    }
  `;
    document.head.appendChild(style);

    return () => style.remove();
  }, []);

  // Get icon emoji based on POI type
const getPOIIcon = (type) => {
    const categoryMap = {
      restaurant: '🍽️', cafe: '🍽️', bar: '🍽️', pub: '🍽️',
      fast_food: '🍽️', food_court: '🍽️', ice_cream: '🍽️', bakery: '🍽️',
      nightclub: '🌙', club: '🌙',
      park: '🌿', garden: '🌿', playground: '🌿', beach: '🌿',
      peak: '🌿', waterfall: '🌿', nature: '🌿',
      sports_centre: '⚡', fitness_centre: '⚡', gym: '⚡',
      stadium: '⚡', swimming_pool: '⚡', pitch: '⚡', sports: '⚡',
      supermarket: '🛍️', convenience: '🛍️', mall: '🛍️',
      marketplace: '🛍️', shop: '🛍️', clothes: '🛍️',
      museum: '🎭', gallery: '🎭', theatre: '🎭', cinema: '🎭',
      library: '🎭', attraction: '🎭', hotel: '🎭', monument: '🎭',
    };
    return categoryMap[type] || '📍';
  };

  // Global function to create event at POI
  window.createEventAtPOI = (osmId, name, address, lat, lng, type) => {
    const icon = getPOIIcon(type);

    window.handleLinkUp(
      osmId,
      name,
      `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50" y="50" font-size="60" text-anchor="middle" dominant-baseline="central">${icon}</text></svg>`)}`,
      "",
      address,
      lat,
      lng,
      "",
      type,
    );
  };

  // Store selected categories on window so loadExistingEvents can access
  window.selectedCategories = selectedCategories || [];

  const [isAtUserLocation, setIsAtUserLocation] = useState(true);
  const lastKnownPositionRef = useRef(null);

  const [searchRadius, setSearchRadius] = useState(() => {
    const saved = localStorage.getItem("searchRadius");
    return saved ? parseFloat(saved) : 2;
  });

  const [totalEventsCount, setTotalEventsCount] = useState(0);
  // Helper function to format distance display
  const formatDistance = (km) => {
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    return `${km}km`;
  };

  // Helper function to get slider index from km value
  const getSliderIndex = (km) => {
    const index = rangeValues.findIndex((val) => val === km);
    return index === -1 ? 4 : index; // Default to 2km if not found
  };

  // Helper function to get km value from slider index
  const getKmFromIndex = (index) => {
    return rangeValues[index] || 2;
  };

  const [isPinMode, setIsPinMode] = useState(false);
  const [pinLocation, setPinLocation] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [selectedPlacePhoto, setSelectedPlacePhoto] = useState("");

  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);
  const eventMarkersRef = useRef([]);
  window.eventMarkersRef = eventMarkersRef;
  const ignoreSearchResults = useRef(false);
  const lastSelectionTime = useRef(0);
  const lastSearchQuery = useRef("");
  const cachedResults = useRef([]);
  const [mapZoom, setMapZoom] = useState(17);


  // Initialize global flag
  window.openedFromList = false;

// Auto-search as user types (debounced) — only when actively typing
useEffect(() => {
    if (!window._userIsTypingSearch) return;
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      window._currentSearchId = Date.now();
      return;
    }

    const delaySearch = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => {
      clearTimeout(delaySearch);
    };
  }, [searchQuery]);

  // Close filters panel and exit pin mode on outside click
useEffect(() => {
  const handleOutsideClick = (e) => {
    // --- Filters panel ---
    const slider = document.getElementById('rangeSliderPanel');
    if (slider && slider.dataset.snap && slider.dataset.snap !== 'closed') {
      const isInsideSlider = slider.contains(e.target);
      const isFilterBtn = e.target.closest('[data-filter-btn]');
      if (!isInsideSlider && !isFilterBtn) {
        const startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
        const startY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
        const closeIfClick = (upEvent) => {
          document.removeEventListener('mouseup', closeIfClick);
          document.removeEventListener('touchend', closeIfClick);
          const endX = upEvent.clientX ?? upEvent.changedTouches?.[0]?.clientX ?? startX;
          const endY = upEvent.clientY ?? upEvent.changedTouches?.[0]?.clientY ?? startY;
          if (Math.abs(endX - startX) < 8 && Math.abs(endY - startY) < 8) {
            slider.dataset.snap = 'closed';
            slider.style.transform = 'translateX(-50%) translateY(100%)';
            slider.style.bottom = '0';
            if (window.rangeCircle) { window.rangeCircle.remove(); window.rangeCircle = null; }
          }
        };
        document.addEventListener('mouseup', closeIfClick);
        document.addEventListener('touchend', closeIfClick);
      }
    }

// --- Pin mode ---
if (isPinMode) {
  const pinOverlay = document.getElementById('pin-mode-overlay');
  const pinConfirmBar = document.getElementById('pin-confirm-bar');
  const isPinBtn = e.target.closest('[data-pin-btn]');
  const isInsideOverlay = pinOverlay?.contains(e.target);
  const isInsideBar = pinConfirmBar?.contains(e.target);
  if (!isInsideOverlay && !isInsideBar && !isPinBtn) {
    // Track mousedown position, only exit if mouse didn't move (real click, not drag)
    const startX = e.clientX;
    const startY = e.clientY;
const onMouseUp = (upEvent) => {
  // If mouseup is on a button, don't close — let the button's onClick handle it
  if (upEvent.target.closest('button')) {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('touchend', onTouchEnd);
    return;
  }
  const dx = Math.abs(upEvent.clientX - startX);
  const dy = Math.abs(upEvent.clientY - startY);
  if (dx < 5 && dy < 5) {
    setIsPinMode(false);
    setPinLocation(null);
  }
  document.removeEventListener('mouseup', onMouseUp);
  document.removeEventListener('touchend', onTouchEnd);
};
const onTouchEnd = (touchEvent) => {
  if (touchEvent.target.closest('button')) {
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('touchend', onTouchEnd);
    return;
  }
  const touch = touchEvent.changedTouches[0];
  const dx = Math.abs(touch.clientX - startX);
  const dy = Math.abs(touch.clientY - startY);
  if (dx < 5 && dy < 5) {
    setIsPinMode(false);
    setPinLocation(null);
  }
  document.removeEventListener('mouseup', onMouseUp);
  document.removeEventListener('touchend', onTouchEnd);
};
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onTouchEnd);
  }
}
  };

  document.addEventListener('mousedown', handleOutsideClick);
  return () => document.removeEventListener('mousedown', handleOutsideClick);
}, [isPinMode]);

  // Close search results when clicking outside
  useEffect(() => {
const handleClickOutside = (e) => {
      const searchContainer = document.querySelector("[data-search-container]");
      const mobileSearchContainer = document.querySelector(".mobile-search-btn");
      if (
        searchContainer && !searchContainer.contains(e.target) &&
        (!mobileSearchContainer || !mobileSearchContainer.contains(e.target))
      ) {
        // Don't clear — just hide visually by blurring, keep cache intact
        // Results restore on next focus via handleSearchClick
        ignoreSearchResults.current = true;
        setSearchResults([]);
      }
    };

    if (searchResults.length > 0) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchResults]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
@keyframes slowPing {
  0% { 
    transform: scale(0.3); 
    opacity: 1.5; 
  }
  40% { 
    transform: scale(0.6); 
    opacity: 1; 
  }
  70% { 
    transform: scale(0.9); 
    opacity: 0.5; 
  }
  100% { 
    transform: scale(1.2); 
    opacity: 0; 
  }
}
    
    /* Prevent countdown flickering */
    [id^="countdown_"] span {
      will-change: contents;
      backface-visibility: hidden;
      -webkit-font-smoothing: antialiased;
    }
    
    /* Leaflet custom marker styles */
    .custom-user-marker,
    .custom-event-marker,
    .poi-marker {
      background: none !important;
      border: none !important;
    }
    
    /* Leaflet popup styles */
    .leaflet-popup-content-wrapper {
      border-radius: 12px;
      padding: 8px;
    }
    
    .leaflet-popup-content {
      margin: 8px;
    }
  `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    console.log('[geo-trigger] currentView=', currentView,
      'isNative=', window.Capacitor?.isNativePlatform?.(),
      'hasGeolocation=', !!Geolocation);
    if (currentView === "map") {
      console.log('[geo-trigger] calling getCurrentLocation()');
      getCurrentLocation();
    }
  }, [currentView]);

  useEffect(() => {
    if (currentView === "map" && mapRef.current) {
      // Prevent double initialization
      if (window.mapInitializing || googleMapRef.current) {
        return;
      }

      window.mapInitializing = true;

      // Initialize map immediately — will fly to real location once GPS resolves
      setTimeout(() => {
        initMap();
        window.mapInitializing = false;
      }, 100);

return () => {
  // Cleanup interval when component unmounts or view changes
  if (window.markerColorUpdateInterval) {
    clearInterval(window.markerColorUpdateInterval);
  }

  // Clean up geolocation watch
  if (window._geoWatchId !== undefined) {
    if (window.Capacitor?.isNativePlatform?.()) {
      Geolocation.clearWatch({ id: window._geoWatchId }).catch(() => {});
    } else {
      navigator.geolocation.clearWatch(window._geoWatchId);
    }
    window._geoWatchId = undefined;
  }

  // IMPORTANT: Clean up map on unmount
  if (googleMapRef.current) {
    googleMapRef.current.remove();
    googleMapRef.current = null;
  }
};
    }

    // Update range circle center if it exists
    if (window.rangeCircle && userLocation) {
      window.rangeCircle.setLatLng([userLocation.lat, userLocation.lng]);
    }
  }, [currentView, userLocation]);

  // Auto-cleanup finished events every minute
  useEffect(() => {
    if (currentView === "map" && !window._userBanned) {
      const cleanupInterval = setInterval(async () => {
        // Skip cleanup if tab is hidden or map is not ready
        if (document.hidden || !window.leafletMap || window.isLoadingEvents) {
          return;
        }

        try {
          await loadExistingEvents(searchRadius);
        } catch (e) {
          return;
        }
      }, 60000); // Every minute

      return () => {
        clearInterval(cleanupInterval);
      };
    }
  }, [currentView, searchRadius, selectedCategories]);

  // Keep window.selectedCategories in sync
  useEffect(() => {
    window.selectedCategories = selectedCategories || [];
  }, [selectedCategories]);

  // Keep window.searchRadius in sync
  useEffect(() => {
    window.searchRadius = searchRadius;
  }, [searchRadius]);

  // Pause marker operations when tab is hidden, refresh when visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (window.markerClusterGroup) {
          window.markerClusterGroup.options.animate = false;
          window.markerClusterGroup.options.animateAddingMarkers = false;
        }
      } else {
        setTimeout(() => {
          if (window.markerClusterGroup) {
            window.markerClusterGroup.options.animate = true;
            window.markerClusterGroup.options.animateAddingMarkers = false;
          }
        }, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Cleanup profile panel events every minute
  useEffect(() => {
    if (!currentUser?.uid) return;

    const profileCleanupInterval = setInterval(() => {
      // Reload user events (this will filter out 24h+ old events)
      if (window.loadUserEvents) {
        window.loadUserEvents();
      }
    }, 60000); // Every minute

    return () => {
      clearInterval(profileCleanupInterval);
    };
  }, [currentUser?.uid]);

  // Listen for event removal
  useEffect(() => {
    const handleEventRemoved = (e) => {
      // Reload markers to update the count
      if (currentView === "map") {
        loadExistingEvents(searchRadius);
      }
    };

    window.addEventListener("eventRemoved", handleEventRemoved);

    return () => {
      window.removeEventListener("eventRemoved", handleEventRemoved);
    };
  }, [currentView]);

  useEffect(() => {
if (isPinMode && googleMapRef.current) {
  const center = googleMapRef.current.getCenter();
  setPinLocation({ lat: center.lat, lng: center.lng });
}
  }, [isPinMode]);

  useEffect(() => {
    // Check current user on mount
    const initAuth = async () => {
      try {
        // Check for password recovery BEFORE anything else
        const hash = window.location.hash || "";
        const searchParams = window.location.search || "";
        const fullUrl = hash + searchParams;

        // Mark this tab as the recovery tab if URL contains recovery token
        if (fullUrl.includes("type=recovery")) {
          window._isRecoveryTab = true;
          window._isPasswordRecovery = true;
        }

        // Check for error in URL (expired/invalid link) - only if it's a Supabase auth error
        if (
          hash.includes("error=access_denied") ||
          hash.includes("error_code=otp_expired")
        ) {
          const hashParams = new URLSearchParams(hash.substring(1));
          const errorDesc =
            hashParams.get("error_description") ||
            "Authentication link is invalid or has expired";
          // Sign out any existing session so user doesn't get redirected to map
          await logoutUser();
          setCurrentUser(null);
          setFirebaseUser(null);
          window.currentAppUser = null;
          setError(errorDesc.replace(/\+/g, " "));
          // Clean the URL
          window.history.replaceState(null, "", window.location.pathname);
          navigateTo("login");
          setIsLoadingAuth(false);
          return;
        }

        const user = await getCurrentUser();

        if (user) {
          setFirebaseUser(user);

          // Get user profile
          const { success, profile } = await getUserProfile(user.id);

          // Don't navigate to map if this is a recovery tab
          if (window._isPasswordRecovery) {
            setIsLoadingAuth(false);
            return;
          }

          if (success && profile) {
            // Check if user is banned
            if (profile.banned) {
              window._userBanned = true;
              setCurrentUser({
                email: user.email,
                uid: user.id,
                profile: {
                  name: profile.name,
                  profilePicture: profile.profile_picture,
                },
                hasProfile: true,
                banned: true,
                banReason: profile.ban_reason,
              });
              navigateTo("banned");
              setIsLoadingAuth(false);
              return;
            }
            setCurrentUser({
              email: user.email,
              uid: user.id,
              profile: {
                name: profile.name,
                profilePicture: profile.profile_picture,
                background: profile.background,
                description: profile.description,
                gender: profile.gender,
              },
              hasProfile: true,
            });

            window.currentAppUser = {
              email: user.email,
              uid: user.id,
              profile: {
                name: profile.name,
                profilePicture: profile.profile_picture,
                background: profile.background,
                description: profile.description,
                gender: profile.gender,
                dateOfBirth: profile.date_of_birth,
              },
            };

            navigateTo("map");
            window.setupGlobalMessageListener();
          } else {
            // Check if this is a Google user and pre-fill their data
            const googleMeta = user.user_metadata;
            if (googleMeta?.avatar_url || googleMeta?.full_name) {
              setIsGoogleUser(true);
              setProfileData((prev) => ({
                ...prev,
                name: googleMeta.full_name || googleMeta.name || "",
                profilePicture:
                  googleMeta.avatar_url || googleMeta.picture || null,
              }));
            }

            setCurrentUser({
              email: user.email,
              uid: user.id,
              hasProfile: false,
            });
            navigateTo("profile-setup");
          }
        } else {
          setCurrentUser(null);
          setFirebaseUser(null);

          // Check if there's a shared event URL - go directly to guest view
          // Supports both ?event=<id> (legacy) and /event/<id> (new)
          const params = new URLSearchParams(window.location.search);
          const queryEventId = params.get("event");
          const pathMatch = (window.location.pathname || "").match(/\/event\/([^/?#]+)/);
          const sharedEventId = queryEventId || (pathMatch && pathMatch[1]);
          if (sharedEventId) {
            // Reset map initialization flag
            window.guestMapInitialized = false;
            setGuestEventId(sharedEventId);
            setIsGuestView(true);
            navigateTo("guest-map");
          } else {
            navigateTo("landing");
          }
        }
      } catch (error) {
        console.error("❌ Auth error:", error);
        setCurrentUser(null);
        setFirebaseUser(null);
        navigateTo("login");
      } finally {
        // CRITICAL: Always set loading to false
        setIsLoadingAuth(false);
      }
    };

    initAuth();

    // Listen to auth changes
    const { data: authListener } = onAuthChange(async (user, event) => {
      // Block everything if in password recovery mode
      if (window._isPasswordRecovery && event !== "PASSWORD_RECOVERY") {
        // Only allow through if user explicitly finished recovery
        if (
          event === "SIGNED_IN" ||
          event === "INITIAL_SESSION" ||
          event === "TOKEN_REFRESHED"
        ) {
          return;
        }
      }

      // Handle password recovery redirect - only if THIS tab initiated the recovery
      if (event === "PASSWORD_RECOVERY") {
        if (window._isRecoveryTab) {
          window._isPasswordRecovery = true;
          setIsPasswordRecovery(true);
          navigateTo("reset-password");
          setIsLoadingAuth(false);
        } else {
        }
        return;
      }

      if (user) {
        // Don't redirect to map if we're in password recovery mode
        if (window._isPasswordRecovery) {
          return;
        }
        setFirebaseUser(user);

        const { success, profile } = await getUserProfile(user.id);

        if (success && profile) {
          // Check if user is banned
          if (profile.banned) {
            window._userBanned = true;
            setCurrentUser({
              email: user.email,
              uid: user.id,
              profile: {
                name: profile.name,
                profilePicture: profile.profile_picture,
              },
              hasProfile: true,
              banned: true,
              banReason: profile.ban_reason,
            });
            navigateTo("banned");
            return;
          }

          setCurrentUser({
            email: user.email,
            uid: user.id,
            profile: {
              name: profile.name,
              profilePicture: profile.profile_picture,
              background: profile.background,
              description: profile.description,
              dateOfBirth: profile.date_of_birth,
              gender: profile.gender,
            },
            hasProfile: true,
          });

          window.currentAppUser = {
            email: user.email,
            uid: user.id,
            profile: {
              name: profile.name,
              profilePicture: profile.profile_picture,
              background: profile.background,
              description: profile.description,
              gender: profile.gender,
              dateOfBirth: profile.date_of_birth,
            },
          };

          // Native Google OAuth has no page reload, so the SIGNED_IN event must
          // drive navigation into the app for an existing profiled user.
          if (window._pendingOAuthLogin) {
            window._pendingOAuthLogin = false;
            navigateTo("map");
          }

          // If events were already loaded with the correct gender, don't trigger reload
          if (window.lastLoadedGender === profile.gender) {
          } else if (window.eventsLoadedForSession) {
            // Events were loaded but with wrong/no gender - force reload
            window.lastLoadedGender = null;
          }
        } else {
          // No profile found - could be a new Google OAuth user

          // Check if this is a Google user and pre-fill their data
          const googleMeta = user.user_metadata;
          if (googleMeta?.avatar_url || googleMeta?.full_name) {
            setIsGoogleUser(true);
            setProfileData((prev) => ({
              ...prev,
              name: googleMeta.full_name || googleMeta.name || "",
              profilePicture:
                googleMeta.avatar_url || googleMeta.picture || null,
            }));
          }

          setCurrentUser({
            email: user.email,
            uid: user.id,
            hasProfile: false,
          });
          setFirebaseUser(user);
          window._pendingOAuthLogin = false;
          navigateTo("profile-setup");
        }
      } else {
        // No session (initial load or logout)
        setCurrentUser(null);
        setFirebaseUser(null);
        window.currentAppUser = null;
        window.lastLoadedGender = null;
        window.eventsLoadedForSession = false;

        // If a shared event link is in the URL, show it on the guest map
        // instead of landing/login (mirrors initAuth so this event doesn't get
        // overridden when the auth listener fires on load).
        const params = new URLSearchParams(window.location.search);
        const queryEventId = params.get("event");
        const pathMatch = (window.location.pathname || "").match(/\/event\/([^/?#]+)/);
        const sharedEventId = queryEventId || (pathMatch && pathMatch[1]);
        if (sharedEventId) {
          window.guestMapInitialized = false;
          setGuestEventId(sharedEventId);
          setIsGuestView(true);
          navigateTo("guest-map");
        } else {
          navigateTo("landing");
        }
      }
    });

    // Real-time listener for ban status changes
    const banChannel = supabase
      .channel("user-ban-listener")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "users",
        },
        (payload) => {
          const currentUid = window.currentAppUser?.uid;
          if (
            payload.new?.banned === true &&
            currentUid &&
            payload.new?.id === currentUid
          ) {
            window._userBanned = true;

            // Unsubscribe from all other channels to stop errors
            supabase.getChannels().forEach((ch) => {
              if (ch.topic !== "user-ban-listener") {
                supabase.removeChannel(ch);
              }
            });

            setCurrentUser((prev) => ({
              ...prev,
              banned: true,
              banReason: payload.new.ban_reason,
            }));
            navigateTo("banned");
          }
        },
      )
      .subscribe();

    return () => {
      authListener?.subscription?.unsubscribe();
      supabase.removeChannel(banChannel);
    };
  }, []);

  // Render Turnstile CAPTCHA widgets
  useEffect(() => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      return;
    }

    // Skip Turnstile on native Capacitor app (Android/iOS) — WebViews fail bot detection
    const isNative = !!(window.Capacitor?.isNativePlatform?.());
    if (isNative) {
      setCaptchaToken('native-bypass');
      return;
    }

    if (currentView !== "login" && currentView !== "register") return;

    const renderWidget = (containerId) => {
      const container = document.getElementById(containerId);
      if (!container || container.dataset.rendered === "true") return;

      container.innerHTML = "";

      window.turnstile.render(container, {
        sitekey: siteKey,
        theme: "light",
        size: "flexible",
        callback: (token) => {
          setCaptchaToken(token);
        },
        "expired-callback": () => {
          setCaptchaToken(null);
        },
        "error-callback": () => {
          setCaptchaToken(null);
        },
      });

      container.dataset.rendered = "true";
    };

    const targetId =
      currentView === "login" ? "turnstile-login" : "turnstile-register";

    // Wait for Turnstile script to load
    const tryRender = () => {
      if (window.turnstile) {
        renderWidget(targetId);
      } else {
        setTimeout(tryRender, 500);
      }
    };

    // Delay to ensure DOM container exists
    const timer = setTimeout(tryRender, 300);

    return () => clearTimeout(timer);
  }, [currentView]);

  const initMap = () => {
    // Safety check: If map already exists, remove it first
    if (googleMapRef.current) {
      try {
        googleMapRef.current.remove();
        googleMapRef.current = null;
      } catch (e) {
        console.error("Error removing existing map:", e);
      }
    }

    // Safety check: Make sure container exists and is empty
    if (!mapRef.current) {
      console.error("❌ Map container not found");
      return;
    }

    // Clear any existing Leaflet artifacts
    mapRef.current.innerHTML = "";

    // Create Leaflet map — use real location if available, otherwise world view
    const center = userLocation ? [userLocation.lat, userLocation.lng] : [20, 0];
    const zoom = userLocation ? 17 : 2;
    const map = L.map(mapRef.current, {
      center,
      zoom,
      zoomControl: false,
    });

    // Expose map to window for global access
    window.leafletMap = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    // Force Leaflet to recalculate dimensions after WebView layout completes
    setTimeout(() => { map.invalidateSize(); }, 300);
    setTimeout(() => { map.invalidateSize(); }, 1000);

// Move attribution to top-right corner always
setTimeout(() => {
  const attrControl = document.querySelector('.leaflet-bottom.leaflet-right .leaflet-control-attribution');
  const topRight = document.querySelector('.leaflet-top.leaflet-right');
  if (attrControl && topRight) {
    topRight.appendChild(attrControl);
  }
}, 600);

    googleMapRef.current = map; // Keep the same ref name for compatibility

    // Continuously cache user position for instant findMe response.
    // On native, only start the watch once permission is granted — otherwise
    // we get OS-PLUG-GLOC-0003 errors during the initial permission flow.
    if (window.Capacitor?.isNativePlatform?.()) {
      (async () => {
        try {
          const status = await Geolocation.checkPermissions();
          if (status.location !== 'granted' && status.coarseLocation !== 'granted') {
            // Defer: another effect will request permission. We'll re-attempt
            // once userLocation is set (which only happens post-permission).
            return;
          }
          const id = await Geolocation.watchPosition(
            { enableHighAccuracy: false, timeout: 30000 },
            (pos, err) => {
              if (err || !pos) return;
              lastKnownPositionRef.current = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
              };
            }
          );
          window._geoWatchId = id;
        } catch (e) {
          console.warn('[geo-watch] failed:', e?.message || e);
        }
      })();
    } else if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          lastKnownPositionRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        },
        () => {},
        { enableHighAccuracy: false, maximumAge: 10000, timeout: 30000 }
      );
      window._geoWatchId = watchId;
    }

    // Handle map clicks - ONLY close range slider, NO place detection
    map.on("click", async (e) => {
      // Close range slider if open
      if (e.originalEvent.detail === 1) {
        // Single click only
        const slider = document.getElementById("rangeSliderPanel");
        if (slider) {
          const currentTransform = slider.style.transform || "";
          if (slider.dataset.snap && slider.dataset.snap !== 'closed') {
            slider.dataset.snap = 'closed';
            slider.style.bottom = '0';
            slider.style.transform = "translateX(-50%) translateY(100%)";
            if (window.rangeCircle) {
              window.rangeCircle.remove();
              window.rangeCircle = null;
            }
          }
        }
      }
    });

    // Track zoom changes
    map.on("zoomend", () => {
      const zoom = map.getZoom();
      setMapZoom(zoom);
      window.currentMapZoom = zoom;

      // Update add button visibility based on zoom level
      const addButtons = document.querySelectorAll(".add-event-button");
      addButtons.forEach((button) => {
        if (zoom >= 15) {
          button.style.opacity = "1";
          button.style.pointerEvents = "auto";
        } else {
          button.style.opacity = "0";
          button.style.pointerEvents = "none";
        }
      });
});

    // Track if map center matches user location
    map.on('movestart', () => {
      setIsAtUserLocation(false);
      const btn = document.getElementById('map-btn-locate');
      if (btn) {
        btn.style.background = 'white';
        btn.style.border = '1px solid #e5e7eb';
        btn.querySelector('svg').style.stroke = '#9ca3af';
      }
    });

    // Create user marker (only if location is already known — otherwise the
    // dedicated effect below will create it once GPS resolves).
    if (userLocation) createCustomMarker();

    // Load events - wait for user profile to be ready
    if (searchRadius) {
      // Set flag to prevent real-time subscription from triggering during initial load
      window.initialMapLoad = true;
      window.eventsLoadedForSession = false;
      let waitAttempts = 0;
      const maxWaitAttempts = 10; // Max ~3 seconds (10 * 300ms)

      const loadEventsWhenReady = () => {
        // Check if user gender is available
        const userGender =
          window.currentAppUser?.profile?.gender ||
          currentUser?.profile?.gender;
        waitAttempts++;

        if (userGender || !currentUser || waitAttempts >= maxWaitAttempts) {
          // Gender available, no user (guest), or timeout reached - load events now
          window.eventsLoadedForSession = true;
          window.lastLoadedGender = userGender || null;
          loadExistingEvents(searchRadius);

          // Clear initial load flag after completion
          setTimeout(() => {
            window.initialMapLoad = false;
          }, 1000);
        } else {
          // User exists but no gender yet - wait and retry
          setTimeout(loadEventsWhenReady, 300);
        }
      };

      // Start checking after a brief delay to let auth state settle
      setTimeout(loadEventsWhenReady, 100);
    }
  };

const loadExistingEvents = async (radiusKm = searchRadius) => {
    if (window.isLoadingEvents) {
      if (window.setMapReady) window.setMapReady();
      return;
    }

    const location = userLocation || window.currentUserLocation;
    if (!location || !radiusKm || radiusKm <= 0) {
      if (window.setMapReady) window.setMapReady();
      return;
    }

    window.isLoadingEvents = true;

    try {
      const { success, events } = await getAllEvents();
      if (!success) { console.error("Failed to load events from Supabase"); return; } // finally still runs

      const now = new Date();
      const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

      // --- Build desired eventsByPlace (same logic as before) ---
      const eventsByPlace = {};
      const qualifiedEvents = [];

      for (const eventData of events) {
        if (eventData.endDateTime) {
          const endDate = new Date(eventData.endDateTime);
          const timeSinceEnd = now - endDate;
          if (timeSinceEnd >= thirtyDaysInMs) {
            if (Math.floor(timeSinceEnd / (24 * 60 * 60 * 1000)) >= 30) {
              await deleteEvent(eventData.eventId);
            }
            continue;
          }
          if (timeSinceEnd >= twentyFourHoursInMs) {
            if (!eventData.archived) await updateEvent(eventData.eventId, { archived: true });
            continue;
          }
        }

        const isOwnEvent = eventData.creatorId === currentUser?.uid;
        const isParticipant = eventData.participants?.includes(currentUser?.uid);
        const alwaysShow = isOwnEvent; // only creator always sees their event regardless of filters

        const userGender = window.currentAppUser?.profile?.gender || currentUser?.profile?.gender;
        const eventGenderPref = eventData.genderPreference || eventData.gender_preference || ["man", "woman", "different"];
        const hasGenderRestriction = !(eventGenderPref.includes("man") && eventGenderPref.includes("woman") && eventGenderPref.includes("different"));
        if (hasGenderRestriction && !userGender && !alwaysShow) continue;
        if (userGender && !alwaysShow && !eventGenderPref.includes(userGender)) continue;

        const userDob = window.currentAppUser?.profile?.dateOfBirth;
        const eventAgeMin = eventData.ageMin || 18;
        const eventAgeMax = eventData.ageMax || 99;
        const hasAgeRestriction = !(eventAgeMin === 18 && eventAgeMax === 99);
        if (!alwaysShow && hasAgeRestriction) {
          if (!userDob) continue; // age unknown — hide age-restricted events
          const birthDate = new Date(userDob);
          let userAge = now.getFullYear() - birthDate.getFullYear();
          const monthDiff = now.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) userAge--;
          if (userAge < eventAgeMin || userAge > eventAgeMax) continue;
        }

        const activeCategories = window.selectedCategories || [];
        if (activeCategories.length > 0 && !alwaysShow) {
          const eventCategories = Array.isArray(eventData.category) ? eventData.category : [eventData.category || "entertainment"];
          if (!eventCategories.some(c => activeCategories.includes(c))) continue;
        }

        if (location && eventData.latitude && eventData.longitude) {
          const distance = calculateDistance(location.lat, location.lng, eventData.latitude, eventData.longitude);
          if (distance > radiusKm && !alwaysShow) continue;
        }

        const lat = eventData.latitude;
        const lng = eventData.longitude;
        const coordKey = lat && lng ? `${Math.round(lat * 10000)}_${Math.round(lng * 10000)}` : eventData.placeId;
        let groupKey = coordKey;
        for (const existingKey of Object.keys(eventsByPlace)) {
          const sample = eventsByPlace[existingKey][0];
          if (sample?.latitude && sample?.longitude) {
            if (Math.abs(sample.latitude - lat) < 0.0001 && Math.abs(sample.longitude - lng) < 0.0001) {
              groupKey = existingKey;
              break;
            }
          }
        }
        if (!eventsByPlace[groupKey]) eventsByPlace[groupKey] = [];
        eventsByPlace[groupKey].push(eventData);
        qualifiedEvents.push(eventData);
      }

      window.allEvents = qualifiedEvents;

      // --- Sort each group and pick primary event ---
      const desiredMarkers = {}; // groupKey -> { primary, total, sorted }
      for (const [groupKey, eventsAtPlace] of Object.entries(eventsByPlace)) {
        const ongoing = [], upcoming = [], finished = [];
        eventsAtPlace.forEach(event => {
          const status = window.calculateEventStatus(event.startDateTime, event.endDateTime);
          if (status.hasEnded) finished.push(event);
          else if (status.hasStarted && status.endDate) ongoing.push(event);
          else upcoming.push(event);
        });
        const sort = (arr, fn) => arr.sort(fn);
        sort(ongoing, (a, b) => window.calculateEventStatus(a.startDateTime, a.endDateTime).endDate - window.calculateEventStatus(b.startDateTime, b.endDateTime).endDate);
        sort(upcoming, (a, b) => window.calculateEventStatus(a.startDateTime, a.endDateTime).startDate - window.calculateEventStatus(b.startDateTime, b.endDateTime).startDate);
        sort(finished, (a, b) => window.calculateEventStatus(b.startDateTime, b.endDateTime).endDate - window.calculateEventStatus(a.startDateTime, a.endDateTime).endDate);
        const sorted = [...ongoing, ...upcoming, ...finished];
        if (sorted.length === 0) continue;
        const primary = sorted[0];

        // Count only qualified events at this location for the badge
        const trueTotal = qualifiedEvents.filter(ev => {
          if (ev.archived) return false;
          if (!ev.latitude || !ev.longitude) return false;
          const sample = eventsAtPlace[0];
          return Math.abs((ev.latitude || 0) - (sample.latitude || 0)) < 0.0001
              && Math.abs((ev.longitude || 0) - (sample.longitude || 0)) < 0.0001;
        }).length;

        desiredMarkers[groupKey] = { primary, total: trueTotal, sorted };
      }

      // --- Ensure cluster group exists ---
      const map = window.leafletMap || googleMapRef.current;
      if (!window.markerClusterGroup) {
        // Inject the cluster pulse keyframes ONCE (was duplicated inside every
        // cluster icon's HTML — wasteful with many clusters).
        if (!document.getElementById('cluster-pulse-style')) {
          const s = document.createElement('style');
          s.id = 'cluster-pulse-style';
          s.textContent = '@keyframes clusterPulse{0%,100%{box-shadow:0 4px 12px rgba(0,0,0,0.3);transform:scale(1)}50%{box-shadow:0 4px 20px rgba(30,30,30,0.5),0 0 0 8px rgba(30,30,30,0.1);transform:scale(1.05)}}';
          document.head.appendChild(s);
        }
        window.markerClusterGroup = L.markerClusterGroup({
          maxClusterRadius: 80,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          // ── Performance for hundreds of markers ──────────────────────────
          chunkedLoading: true,           // add markers in chunks, don't block UI
          chunkInterval: 100,             // ms of work per chunk
          chunkDelay: 16,                 // yield ~1 frame between chunks
          removeOutsideVisibleBounds: true, // keep only on-screen markers in the DOM
          animateAddingMarkers: false,    // skip add animations (cheaper)
          iconCreateFunction: function (cluster) {
            let totalEvents = 0;
            cluster.getAllChildMarkers().forEach(marker => { totalEvents += marker.eventCount || 1; });
            return L.divIcon({
              html: `<div style="background:rgba(30,30,30,0.9);color:white;border-radius:50%;width:40px;height:40px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;font-family:'Outfit',sans-serif;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.3);animation:clusterPulse 2s ease-in-out infinite;">${totalEvents}</div>`,
              className: "marker-cluster",
              iconSize: L.point(40, 40),
            });
          },
        });
        if (map) map.addLayer(window.markerClusterGroup);
      }

      // --- DIFF: figure out existing markers keyed by placeId ---
      const existingByPlaceId = {};
      for (const marker of eventMarkersRef.current) {
        if (!marker) continue;
        const pid = marker.placeId;
        if (pid) existingByPlaceId[pid] = marker;
      }

      const desiredPlaceIds = new Set();
      for (const { primary } of Object.values(desiredMarkers)) {
        desiredPlaceIds.add(primary.placeId);
      }

      // Collect changes, then apply in bulk — addLayers()/removeLayers() let
      // markercluster compute clustering once instead of per marker (much
      // faster with hundreds of markers).
      const toRemove = [];
      const toAdd = [];
      const removedSet = new Set();

      // REMOVE markers no longer needed
      for (const [pid, marker] of Object.entries(existingByPlaceId)) {
        if (!desiredPlaceIds.has(pid)) {
          toRemove.push(marker);
          removedSet.add(marker);
        }
      }

      // ADD or UPDATE markers
      for (const { primary, total, sorted } of Object.values(desiredMarkers)) {
        const pid = primary.placeId;
        const existingMarker = existingByPlaceId[pid];

        if (!existingMarker) {
          const photoUrl = primary.placePhotoUrl || primary.placeIcon;
          const marker = createEventMarker(primary, photoUrl, 0, total, sorted);
          if (marker) {
            toAdd.push(marker);
            eventMarkersRef.current.push(marker);
          }
        } else {
          const currentCount = existingMarker.eventCount || 1;
          if (currentCount !== total) {
            toRemove.push(existingMarker);
            removedSet.add(existingMarker);
            const photoUrl = primary.placePhotoUrl || primary.placeIcon;
            const newMarker = createEventMarker(primary, photoUrl, 0, total, sorted);
            if (newMarker) {
              toAdd.push(newMarker);
              eventMarkersRef.current.push(newMarker);
            }
          }
          // If count unchanged, leave marker completely untouched
        }
      }

      if (toRemove.length) {
        window.markerClusterGroup.removeLayers(toRemove);
        eventMarkersRef.current = eventMarkersRef.current.filter(m => !removedSet.has(m));
      }
      if (toAdd.length) {
        window.markerClusterGroup.addLayers(toAdd);
      }

      setTotalEventsCount(Object.values(eventsByPlace).reduce((sum, evs) => sum + evs.length, 0));

    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      window.isLoadingEvents = false;
      if (window.setMapReady) window.setMapReady();
    }
  };
  window.loadExistingEvents = loadExistingEvents;
  // Reload events when user gender becomes available (one-time after login)
  useEffect(() => {
    const userGender = currentUser?.profile?.gender;

    if (currentView === "map" && userGender && googleMapRef.current) {
      // Only reload if events were already loaded WITHOUT this gender
      // Skip if initMap is still loading (eventsLoadedForSession will be false during load)
      if (
        window.eventsLoadedForSession &&
        window.lastLoadedGender !== userGender
      ) {
        window.lastLoadedGender = userGender;

        setTimeout(() => {
          window.isLoadingEvents = false;
          loadExistingEvents(searchRadius);
        }, 200);
      }
    }
  }, [currentUser?.profile?.gender, currentView]);

  useEffect(() => {
    const openEventFromId = (eventId) => {
      if (!eventId) return;
      const ev = (window.allEvents || []).find(e => e.eventId === eventId);
      if (ev && window.showEventDetailsPanel) window.showEventDetailsPanel(ev, ev.placePhotoUrl || '');
    };
    window._openEventFromNotification = openEventFromId;

    // Shared event deep link (linksy://event/<id> or https://linksy.community/event/<id>).
    // Auth-aware: logged-in users open the event on their map; guests are routed
    // to the guest map which loads the event details — instead of the login page.
    const openSharedEvent = (eventId) => {
      if (!eventId) return;
      const isLoggedIn = !!(window.currentAppUser?.uid || window.currentUser?.uid);
      if (isLoggedIn) {
        const ev = (window.allEvents || []).find(e => e.eventId === eventId);
        if (ev && window.showEventDetailsPanel) {
          window.showEventDetailsPanel(ev, ev.placePhotoUrl || '');
        } else {
          // Not loaded yet — open once events arrive.
          window._pendingSharedEventId = eventId;
        }
      } else {
        window.guestMapInitialized = false;
        setGuestEventId(eventId);
        setIsGuestView(true);
        navigateTo("guest-map");
      }
    };
    window._openSharedEvent = openSharedEvent;

    return () => {
      window._openEventFromNotification = null;
      window._openSharedEvent = null;
    };
  }, []);

  // Real-time subscription for new and updated events
  useEffect(() => {
    if (!currentUser) return;

    const supabaseClient = window.supabase || supabase;

    // Set up notification subscription
    if (window.setupNotificationSubscription) {
      window.setupNotificationSubscription();
    }
    if (window.registerPushSubscription) {
      window.registerPushSubscription();
    }

    const eventsSubscription = supabaseClient
      .channel("public-events")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "events",
        },
        async (payload) => {
          // Skip during initial map load to prevent double refresh
          if (window.initialMapLoad) {
            return;
          }

          if (payload.new.creator_id === currentUser.uid) {
            return;
          }
          await loadExistingEvents(searchRadius);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "events",
        },
async (payload) => {
          if (window.skipRealtimeUpdate === payload.new.event_id) {
            return;
          }

// Before refreshing marker, check if current user still qualifies to see this event
          // If not, remove marker immediately (handles the case where gender/age changed before participant array is updated)
          const _cuCheck = window.currentAppUser || window.currentUser;
          const _newPartsCheck = payload.new?.participants || [];
          const _isOrganizer = payload.new?.creator_id === _cuCheck?.uid;

if (_cuCheck && !_isOrganizer) {
            const _userGender = _cuCheck?.profile?.gender;
            const _newGender = payload.new?.gender_preference || ['man', 'woman', 'different'];
            const _newAgeMin = payload.new?.age_min || 18;
            const _newAgeMax = payload.new?.age_max || 99;

            let _qualifies = true;

            // Check gender
            if (_userGender) {
              const _hasGenderRestriction = !(_newGender.includes('man') && _newGender.includes('woman') && _newGender.includes('different'));
              if (_hasGenderRestriction && !_newGender.includes(_userGender)) _qualifies = false;
            }

// Check age
            const _userDob = _cuCheck?.profile?.dateOfBirth;
            const _hasAgeRestriction = !(_newAgeMin === 18 && _newAgeMax === 99);
            if (_hasAgeRestriction) {
              if (!_userDob) {
                _qualifies = false;
              } else {
                const _birthDate = new Date(_userDob);
                let _userAge = new Date().getFullYear() - _birthDate.getFullYear();
                const _monthDiff = new Date().getMonth() - _birthDate.getMonth();
                if (_monthDiff < 0 || (_monthDiff === 0 && new Date().getDate() < _birthDate.getDate())) _userAge--;
                if (_userAge < _newAgeMin || _userAge > _newAgeMax) _qualifies = false;
              }
            }

if (!_qualifies) {
              // Remove disqualified event from cache entirely
              if (window.allEvents) {
                window.allEvents = window.allEvents.filter(e => e.eventId !== payload.new.event_id);
              }
              // Close panel if it's open for this event
              const _p2 = document.getElementById('eventDetailsPanel');
              if (_p2 && _p2.dataset.eventId === payload.new.event_id) {
                _p2.style.right = '-400px';
                setTimeout(() => _p2.remove(), 300);
              }
              // Rebuild marker for remaining events at this location (or remove if none left)
              if (window.refreshSingleEventMarker) {
                await window.refreshSingleEventMarker(payload.new.event_id, payload.new.event_id);
              }
              // Refresh events list panel if open at this location
              const _listPanel = document.getElementById('eventsListPanel');
              if (_listPanel) {
                const _lat = payload.new.latitude;
                const _lng = payload.new.longitude;
                if (window.showEventsListPanel) {
                  await window.showEventsListPanel(payload.new.place_id, payload.new.place_name, payload.new.place_address, _lat, _lng);
                }
              }
              return;
            }
          }

          if (window.refreshSingleEventMarker) {
            await window.refreshSingleEventMarker(payload.new.event_id);
          }

          // Full re-render of event details panel if it's open for this event
          const eventDetailsPanel = document.getElementById("eventDetailsPanel");
          if (
            eventDetailsPanel &&
            eventDetailsPanel.dataset.eventId === payload.new.event_id
          ) {
            const supabaseClient = window.supabase || supabase;
            const { data: freshData } = await supabaseClient
              .from("events")
              .select("*")
              .eq("event_id", payload.new.event_id)
              .single();

if (freshData) {
              const panel = document.getElementById('eventDetailsPanel');
              if (!panel) return;

              // Title
              const titleEl = panel.querySelector('[data-event-title]');
              if (titleEl) titleEl.textContent = freshData.title;

              // Description
              const descEl = panel.querySelector('[data-event-description]');
              if (descEl) descEl.textContent = freshData.description || '';

              // Start date
              const startEl = panel.querySelector('[data-start-value]');
              if (startEl && window.formatDateTime) startEl.textContent = window.formatDateTime(freshData.start_date_time);

              // End date
              const endEl = panel.querySelector('[data-end-value]');
              if (endEl && window.formatDateTime) endEl.textContent = window.formatDateTime(freshData.end_date_time);

              // Participant count
              const countEl = panel.querySelector('[data-participants-count]');
              if (countEl) {
                const n = freshData.participants?.length || 0;
                countEl.textContent = freshData.people_limit ? `${n} / ${freshData.people_limit}` : `${n} / ∞`;
              }

              // Category badges
              const catEl = panel.querySelector('[data-category-badges]');
              if (catEl) {
const catLabels = {
                  food_drink:{icon:'🍽️',label:'Food & Drink'}, nightlife:{icon:'🌙',label:'Nightlife'},
                  outdoors:{icon:'🌿',label:'Outdoors'}, active:{icon:'⚡',label:'Active'},
                  shopping:{icon:'🛍️',label:'Shopping'}, entertainment:{icon:'🎭',label:'Entertainment'},
                  // Legacy
                  restaurant:{icon:'🍽️',label:'Food & Drink'}, cafe:{icon:'🍽️',label:'Food & Drink'},
                  bar:{icon:'🍽️',label:'Food & Drink'}, fast_food:{icon:'🍽️',label:'Food & Drink'},
                  park:{icon:'🌿',label:'Outdoors'}, sports_centre:{icon:'⚡',label:'Active'},
                  gym:{icon:'⚡',label:'Active'}, shop:{icon:'🛍️',label:'Shopping'},
                  cinema:{icon:'🎭',label:'Entertainment'}, nightclub:{icon:'🌙',label:'Nightlife'},
                  beach:{icon:'🌿',label:'Outdoors'}, attraction:{icon:'🎭',label:'Entertainment'},
                  hotel:{icon:'🎭',label:'Entertainment'}, museum:{icon:'🎭',label:'Entertainment'},
                  place:{icon:'🎭',label:'Entertainment'},
                };
                const cats = Array.isArray(freshData.category) ? freshData.category : freshData.category ? [freshData.category] : ['entertainment'];
                catEl.innerHTML = cats.map(c => {
                  const info = catLabels[c] || catLabels.entertainment;
                  return `<span style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f1f3f4;border-radius:16px;font-size:12px;color:#202124;font-weight:500;"><span>${info.icon}</span>${info.label}</span>`;
                }).join('');
              }

              // Gender badge
              const genderEl = panel.querySelector('[data-gender-badge]');
              if (genderEl && window.getGenderPreferenceIcon) {
                const genderInfo = window.getGenderPreferenceIcon(freshData.gender_preference);
                genderEl.style.background = `${genderInfo.color}15`;
                genderEl.innerHTML = `<span style="font-size:14px;">${genderInfo.icon}</span><span style="font-size:12px;font-weight:500;color:${genderInfo.color};">${genderInfo.label}</span>`;
              }

              // Age badge
              const ageEl = panel.querySelector('[data-age-badge]');
              const ageMin = freshData.age_min || 18;
              const ageMax = freshData.age_max || 99;
              if (ageEl) {
                if (ageMin === 18 && ageMax === 99) {
                  ageEl.style.display = 'none';
                } else {
                  ageEl.style.display = 'inline-flex';
                  ageEl.innerHTML = `<span>🕵</span> Age ${ageMin} - ${ageMax}`;
                }
              }

const currentUser = window.currentAppUser || window.currentUser;

              // Requires approval banner
              const approvalEl = panel.querySelector('[data-requires-approval]');
              if (approvalEl) {
                const isParticipant = freshData.participants?.includes(currentUser?.uid);
                const isOrganizer = freshData.creator_id === currentUser?.uid;
                approvalEl.style.display = (freshData.require_approve && !isOrganizer && !isParticipant) ? 'flex' : 'none';
              }

              // Participant avatars row
              if (window.updateEventDetailsPanel) {
                window.updateEventDetailsPanel(freshData.event_id);
              }

// Read BEFORE updating snapshot
              const wasParticipant = window._openEventData?._was_participant;
              const isNowParticipant = freshData.participants?.includes(currentUser?.uid);

              // Update snapshot for next comparison
              window._openEventData = {
                gender_preference: JSON.stringify(freshData.gender_preference || ["man","woman","different"]),
                category: JSON.stringify(Array.isArray(freshData.category) ? freshData.category : freshData.category ? [freshData.category] : ["entertainment"]),
                age_min: freshData.age_min || 18,
                age_max: freshData.age_max || 99,
                require_approve: freshData.require_approve || false,
                people_limit: freshData.people_limit,
                pending_participants: JSON.stringify(freshData.pending_participants || []),
                _was_participant: isNowParticipant,
              };

              // Close panel if current user was removed
              if (wasParticipant && !isNowParticipant) {
                panel.style.right = '-400px';
                setTimeout(() => panel.remove(), 300);
              }
            }
          }
        },
      )
      .subscribe((status) => {});

    return () => {
      supabaseClient.removeChannel(eventsSubscription);
    };
  }, [currentUser, searchRadius]);

  // Real-time subscription for deleted events
  useEffect(() => {
    if (!currentUser) return;

    const supabaseClient = window.supabase || supabase;

    const deletionSubscription = supabaseClient
      .channel("event-deletions")
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "events",
        },
        async (payload) => {
          const deletedEventId = payload.old.event_id;

          // Skip if we're currently deleting this event locally
          if (
            window.deletingOwnEvents &&
            window.deletingOwnEvents.has(deletedEventId)
          ) {
            return;
          }

          const deletedPlaceId = payload.old.place_id;

          // Check how many events remain at this location
          const supabaseClient = window.supabase || supabase;
          const { data: remainingEvents } = await supabaseClient
            .from("events")
            .select("event_id")
            .eq("place_id", deletedPlaceId);

          // Find and remove/update the marker
          if (eventMarkersRef && eventMarkersRef.current) {
            const markerIndex = eventMarkersRef.current.findIndex((marker) => {
              if (!marker) return false;
              const markerElement = marker.getElement?.() || marker._element;
              if (!markerElement) return false;

              const markerPlaceId =
                markerElement.querySelector?.("[data-place-id]")?.dataset
                  ?.placeId;
              return markerPlaceId === deletedPlaceId;
            });

            if (markerIndex !== -1) {
              const marker = eventMarkersRef.current[markerIndex];

              if (!remainingEvents || remainingEvents.length === 0) {
                // Last event at this location - remove marker
                if (marker.remove) marker.remove();
                eventMarkersRef.current.splice(markerIndex, 1);
              } else {
                // Other events remain - refresh marker

                // Remove old marker
                if (marker.remove) marker.remove();
                eventMarkersRef.current.splice(markerIndex, 1);

                // Fetch remaining event data and recreate marker
                const { data: eventDetails } = await supabaseClient
                  .from("events")
                  .select("*")
                  .eq("place_id", deletedPlaceId);

                if (eventDetails && eventDetails.length > 0) {
                  // Format events
                  const formattedEvents = eventDetails.map((e) => ({
                    eventId: e.event_id,
                    title: e.title,
                    description: e.description,
                    placeId: e.place_id,
                    placeName: e.place_name,
                    placeAddress: e.place_address,
                    placePhotoUrl: e.place_photo_url,
                    latitude: e.latitude,
                    longitude: e.longitude,
                    startDateTime: e.start_date_time,
                    endDateTime: e.end_date_time,
                    peopleLimit: e.people_limit,
                    creatorId: e.creator_id,
                    createdBy: e.created_by,
                    participants: e.participants,
                    customLabel: e.custom_label,
                  }));

                  // Sort events (ongoing first, then upcoming, then finished)
                  const now = new Date();
                  const ongoing = [];
                  const upcoming = [];
                  const finished = [];

                  formattedEvents.forEach((event) => {
                    const startDate = new Date(event.startDateTime);
                    const endDate = event.endDateTime
                      ? new Date(event.endDateTime)
                      : null;
                    const hasStarted = now >= startDate;
                    const hasEnded = endDate && now >= endDate;

                    if (hasEnded) {
                      finished.push(event);
                    } else if (hasStarted && endDate) {
                      ongoing.push(event);
                    } else {
                      upcoming.push(event);
                    }
                  });

                  ongoing.sort(
                    (a, b) => new Date(a.endDateTime) - new Date(b.endDateTime),
                  );
                  upcoming.sort(
                    (a, b) =>
                      new Date(a.startDateTime) - new Date(b.startDateTime),
                  );
                  finished.sort(
                    (a, b) => new Date(b.endDateTime) - new Date(a.endDateTime),
                  );

                  const sortedEvents = [...ongoing, ...upcoming, ...finished];
                  const primaryEvent = sortedEvents[0];

                  if (window.createEventMarker && primaryEvent) {
                    const newMarker = window.createEventMarker(
                      primaryEvent,
                      primaryEvent.placePhotoUrl,
                      0,
                      sortedEvents.length,
                      sortedEvents,
                    );

                    if (newMarker) {
                      if (window.markerClusterGroup) {
                        window.markerClusterGroup.addLayer(newMarker);
                      }

                      eventMarkersRef.current.push(newMarker);
                    }
                  }
                }
              }
            }
          }

          // Close event details panel if this event is open
          const eventDetailsPanel =
            document.getElementById("eventDetailsPanel");
          if (
            eventDetailsPanel &&
            eventDetailsPanel.dataset.eventId === deletedEventId
          ) {
            eventDetailsPanel.style.right = "-400px";
            setTimeout(() => eventDetailsPanel.remove(), 300);
          }

          // Reload user's event lists
          if (window.loadUserEvents) {
            window.loadUserEvents();
          }
        },
      )
      .subscribe();

    return () => {
      supabaseClient.removeChannel(deletionSubscription);
    };
  }, [currentUser]);

  useEffect(() => {
    const handleEventJoined = async (event) => {
      // Reload user events panel
      if (window.loadUserEvents) window.loadUserEvents();

      // Only refresh marker if not skipped (skip during join to prevent duplicates)
      if (
        !event.detail?.skipMarkerRefresh &&
        event.detail?.eventId &&
        window.refreshSingleEventMarker
      ) {
        await window.refreshSingleEventMarker(event.detail.eventId);
      } else if (event.detail?.skipMarkerRefresh) {
      }
    };

    window.addEventListener("eventJoined", handleEventJoined);

    return () => {
      window.removeEventListener("eventJoined", handleEventJoined);
    };
  }, []);

window.refreshSingleEventMarker = async (eventId, excludeEventId = null) => {
    try {
      const supabaseClient = window.supabase || supabase;
      const now = new Date();

      // Fetch just the one event to get fresh participants/data and location
      const { data: freshData, error } = await supabaseClient
        .from("events")
        .select("*")
        .eq("event_id", eventId)
        .single();

      if (error || !freshData) return;

      const lat = freshData.latitude;
      const lng = freshData.longitude;

      // Update this event in cache
      const freshFormatted = {
        eventId: freshData.event_id,
        title: freshData.title,
        description: freshData.description,
        placeId: freshData.place_id,
        placeName: freshData.place_name,
        placeAddress: freshData.place_address,
        placePhotoUrl: freshData.place_photo_url,
        placeIcon: freshData.place_icon,
        latitude: freshData.latitude,
        longitude: freshData.longitude,
        startDateTime: freshData.start_date_time,
        endDateTime: freshData.end_date_time,
        timezone: freshData.timezone,
        peopleLimit: freshData.people_limit,
        creatorId: freshData.creator_id,
        createdBy: freshData.created_by,
        participants: freshData.participants,
        customLabel: freshData.custom_label,
        requireApprove: freshData.require_approve || false,
        pendingRequests: freshData.pending_requests || [],
        genderPreference: freshData.gender_preference || ["man", "woman", "different"],
        archived: freshData.archived || false,
        createdAt: freshData.created_at,
        category: Array.isArray(freshData.category) ? freshData.category : freshData.category ? [freshData.category] : ["place"],
        ageMin: freshData.age_min || 18,
        ageMax: freshData.age_max || 99,
      };

      if (window.allEvents) {
        const idx = window.allEvents.findIndex(e => e.eventId === eventId);
        // Only update cache if this event isn't being excluded (excluded = user no longer qualifies)
        if (!excludeEventId || excludeEventId !== eventId) {
          if (idx !== -1) window.allEvents[idx] = freshFormatted;
          else window.allEvents.push(freshFormatted);
        } else {
          // Remove it from cache entirely
          if (idx !== -1) window.allEvents.splice(idx, 1);
        }
      }

      // Count ALL non-archived active events at this location that user qualifies to see
      const _cu = window.currentAppUser || window.currentUser;
      const _userGender = _cu?.profile?.gender;
      const _userDob = _cu?.profile?.dateOfBirth;

      const activeEvents = (window.allEvents || []).filter(e => {
        if (e.eventId === excludeEventId) return false;
        if (e.archived) return false;
        if (!e.latitude || !e.longitude) return false;
        if (Math.abs(e.latitude - lat) > 0.0001 || Math.abs(e.longitude - lng) > 0.0001) return false;
        if (e.endDateTime) {
          const timeSinceEnd = now - new Date(e.endDateTime);
          if (timeSinceEnd > 24 * 60 * 60 * 1000) return false;
        }
        // Apply same visibility rules as loadExistingEvents
        const isOwnEvent = e.creatorId === _cu?.uid;
        const alwaysShow = isOwnEvent;
        if (!alwaysShow) {
          const eventGenderPref = e.genderPreference || ["man", "woman", "different"];
          const hasGenderRestriction = !(eventGenderPref.includes("man") && eventGenderPref.includes("woman") && eventGenderPref.includes("different"));
          if (hasGenderRestriction && !_userGender) return false;
          if (_userGender && !eventGenderPref.includes(_userGender)) return false;
          const eventAgeMin = e.ageMin || 18;
          const eventAgeMax = e.ageMax || 99;
          const hasAgeRestriction = !(eventAgeMin === 18 && eventAgeMax === 99);
          if (hasAgeRestriction) {
            if (!_userDob) return false; // age unknown — hide age-restricted events
            const birthDate = new Date(_userDob);
            let userAge = now.getFullYear() - birthDate.getFullYear();
            const monthDiff = now.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) userAge--;
            if (userAge < eventAgeMin || userAge > eventAgeMax) return false;
          }
        }
        return true;
      });

      if (activeEvents.length === 0) {
        // Remove marker by coordinates
        eventMarkersRef.current = eventMarkersRef.current.filter(m => {
          if (!m) return true;
          const mLat = m.lat ?? m.getLatLng?.()?.lat;
          const mLng = m.lng ?? m.getLatLng?.()?.lng;
          if (mLat === undefined || mLng === undefined) return true;
          if (Math.abs(mLat - lat) > 0.0001 || Math.abs(mLng - lng) > 0.0001) return true;
          if (window.markerClusterGroup) window.markerClusterGroup.removeLayer(m);
          if (m.remove) m.remove();
          return false;
        });
        return;
      }
      // Sort active events
      const ongoing = [], upcoming = [], finished = [];
      activeEvents.forEach(e => {
        const status = window.calculateEventStatus(e.startDateTime, e.endDateTime);
        if (status.hasEnded) finished.push(e);
        else if (status.hasStarted && status.endDate) ongoing.push(e);
        else upcoming.push(e);
      });
      ongoing.sort((a, b) => new Date(a.endDateTime) - new Date(b.endDateTime));
      upcoming.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));
      const sortedEvents = [...ongoing, ...upcoming, ...finished];
      const primaryEvent = sortedEvents[0];
      const totalEvents = sortedEvents.length;

      // Remove old marker by coordinates, falling back to Leaflet getLatLng()
      eventMarkersRef.current = eventMarkersRef.current.filter(m => {
        if (!m) return true;
        // Use stored lat/lng, or fall back to Leaflet's getLatLng()
        const mLat = m.lat ?? m.getLatLng?.()?.lat;
        const mLng = m.lng ?? m.getLatLng?.()?.lng;
        if (mLat === undefined || mLng === undefined) return true;
        if (Math.abs(mLat - lat) > 0.0001 || Math.abs(mLng - lng) > 0.0001) return true;
        if (window.markerClusterGroup) window.markerClusterGroup.removeLayer(m);
        if (m.remove) m.remove();
        return false;
      });

      // Create new marker
      const photoUrl = primaryEvent.placePhotoUrl || primaryEvent.placeIcon;
      const marker = createEventMarker(primaryEvent, photoUrl, 0, totalEvents, sortedEvents);
      if (marker) {
        if (window.markerClusterGroup) window.markerClusterGroup.addLayer(marker);
        eventMarkersRef.current.push(marker);
      }
    } catch (err) {
      console.error("❌ Error refreshing marker:", err);
    }
  };

  window.focusCurrentTime = focusCurrentTime;

  // Open event details from events list
  window.openEventDetailsFromList = async (placeId, startDateTime) => {
    // Use cached events first — avoid network round trip
    let events = window.allEvents;
    if (!events || events.length === 0) {
      const { getAllEvents } = await import("./supabase/events");
      const { success, fetched } = await getAllEvents();
      if (!success) return;
      events = fetched;
      window.allEvents = events;
    }

    const foundEvent = events.find(
      (event) =>
        event.placeId === placeId && event.startDateTime === startDateTime,
    );

    if (!foundEvent) return;

    const foundPhotoUrl = foundEvent?.placePhotoUrl || "";

    window.openedFromList = true;
    window.showEventDetailsPanel(foundEvent, foundPhotoUrl);
  };

  window.CATEGORY_MAP = CATEGORY_MAP;
  window.normalizeCategory = normalizeCategory;
  window.EVENT_CATEGORIES = EVENT_CATEGORIES;

  // Listen for event creation
  useEffect(() => {
    const handleEventCreated = (e) => {
      const eventData = e.detail;

      if (window.cleanupEventIntervals) window.cleanupEventIntervals();

      // Cache is already updated before dispatch — just ensure no duplicate
      if (window.allEvents) {
        const alreadyExists = window.allEvents.some(ev => ev.eventId === eventData.eventId);
        if (!alreadyExists) window.allEvents = [...window.allEvents, eventData];
      }

      const placeId = eventData.placeId;

      // Find all events at this location from cache
      const eventsAtPlace = (window.allEvents || [eventData]).filter((event) => {
        if (event.archived) return false;
        if (event.placeId === placeId) return true;
        const latDiff = Math.abs((event.latitude || 0) - (eventData.latitude || 0));
        const lngDiff = Math.abs((event.longitude || 0) - (eventData.longitude || 0));
        return latDiff < 0.0001 && lngDiff < 0.0001;
      });

      // Remove existing marker for this place (if any) then create updated one
      if (window.markerClusterGroup && eventMarkersRef.current) {
        eventMarkersRef.current = eventMarkersRef.current.filter((m) => {
          if (!m) return true;
          if (m.placeId !== placeId) return true;
          window.markerClusterGroup.removeLayer(m);
          if (m.remove) m.remove();
          return false;
        });
        document.querySelectorAll(`[data-place-id="${placeId}"]`).forEach(el => {
          const wrapper = el.closest('.leaflet-marker-icon, .leaflet-interactive');
          if (wrapper) wrapper.remove(); else el.remove();
        });

        // Pick the best primary event (non-archived, sorted by status)
        const primary = eventsAtPlace[0];
        const photoUrl = primary.placePhotoUrl || primary.placeIcon || '';
        const newMarker = createEventMarker(primary, photoUrl, 0, eventsAtPlace.length, eventsAtPlace);
        if (newMarker) {
          window.markerClusterGroup.addLayer(newMarker);
          eventMarkersRef.current.push(newMarker);
        }
      }

      // Open panel
      if (eventsAtPlace.length <= 1) {
        window.openedFromList = false;
        window.showEventDetailsPanel(eventData, eventData.placePhotoUrl || eventData.placeIcon || '');
      } else {
        window.showEventsListPanel(
          placeId,
          eventData.placeName,
          eventData.placeAddress,
          eventData.latitude,
          eventData.longitude,
        ).then(() => setTimeout(() => window.highlightNewEvent(eventData), 500));
      }
    };

    window.addEventListener("eventCreated", handleEventCreated);

    return () => {
      window.removeEventListener("eventCreated", handleEventCreated);
    };
  }, [currentUser, searchRadius, userLocation]);

  const createCustomMarker = () => {
    const map = googleMapRef.current;

    const markerHtml = `
    <div style="position: relative; width: 64px; height: 64px;">
      <!-- Outer pulse ring -->
      <div style="
        position: absolute;
        inset: -20px;
        background: rgba(66, 133, 244, 0.4);
        border-radius: 50%;
        animation: slowPing 2.5s ease-in-out infinite;
      "></div>
      <!-- Middle pulse ring -->
      <div style="
        position: absolute;
        inset: -10px;
        background: rgba(66, 133, 244, 0.5);
        border-radius: 50%;
        animation: slowPing 2.5s ease-in-out infinite 0.3s;
      "></div>
      <div style="
        position: relative;
        z-index: 10;
        width: 64px;
        height: 64px;
        border-radius: 50%;
        overflow: hidden;
        border: 3px solid rgba(0, 61, 163, 0.4);
        box-shadow: 0 10px 25px rgba(40, 110, 227, 0.3);
        background: #00a3b7;
      ">
        ${
          currentUser?.profile?.profilePicture
            ? `<img src="${currentUser.profile.profilePicture}" style="width: 100%; height: 100%; object-fit: cover;" />`
            : `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: white; font-size: 32px;">👤</div>`
        }
      </div>
    </div>
  `;

    const userIcon = L.divIcon({
      className: "custom-user-marker",
      html: markerHtml,
      iconSize: [64, 64],
      iconAnchor: [32, 32],
    });

    markerRef.current = L.marker([userLocation.lat, userLocation.lng], {
      icon: userIcon,
      zIndexOffset: 10000,
    }).addTo(map);
  };

  // Native: location often resolves AFTER initMap runs (and vice-versa).
  // Watch both userLocation and map readiness; create or move the marker as
  // soon as both are available.
  useEffect(() => {
    if (!userLocation) return;
    if (!googleMapRef.current && !window.leafletMap) return;
    console.log('[marker effect] userLocation=', userLocation, 'mapReady=', isMapReady);
    if (markerRef.current) {
      try {
        markerRef.current.setLatLng([userLocation.lat, userLocation.lng]);
      } catch (e) { console.warn('marker setLatLng:', e); }
    } else {
      try { createCustomMarker(); }
      catch (e) { console.warn('createCustomMarker:', e); }
    }
  }, [userLocation, isMapReady, currentUser?.profile?.profilePicture]);

const updateRangeCircle = (radiusKm) => {
  const slider = document.getElementById("rangeSliderPanel");
  if (!slider) return;

  const isSliderOpen = slider.dataset.snap && slider.dataset.snap !== 'closed';

  if (!isSliderOpen) {
    if (window.rangeCircle) {
      window.rangeCircle.remove();
      window.rangeCircle = null;
    }
    return;
  }

  const map = googleMapRef.current;
  if (!map || !userLocation) return;

  const currentCenter = [userLocation.lat, userLocation.lng];

  if (!window.rangeCircle) {
    window.rangeCircle = L.circle(currentCenter, {
      radius: radiusKm * 1000,
      color: "#2c2c2c",
      weight: 2,
      opacity: 0.8,
      fillColor: "#000000",
      fillOpacity: 0.1,
    }).addTo(map);
  } else {
    window.rangeCircle.setLatLng(currentCenter);
    window.rangeCircle.setRadius(radiusKm * 1000);
  }

  // Zoom map so the circle keeps a consistent size on screen as the radius
  // changes — fit it into the visible window between the top nav and the part
  // of the viewport the sheet covers. On mobile the sheet opens to the "half"
  // snap (≈265px visible); use that deterministic value rather than a live
  // measurement, which would be wrong while the sheet is still sliding in
  // (and would center the avatar too low).
  {
    const isMobile = window.innerWidth <= 800;
    let coveredBottom;
    if (isMobile && slider.dataset.snap === 'half') {
      coveredBottom = 280;
    } else {
      const rect = slider.getBoundingClientRect();
      coveredBottom = Math.max(0, window.innerHeight - rect.top);
    }
    const navHeight = isMobile ? 68 : 80;
    const bounds = window.rangeCircle.getBounds();
    map.fitBounds(bounds, {
      paddingTopLeft: [40, navHeight + 8],
      paddingBottomRight: [40, coveredBottom + 8],
      animate: true,
      duration: 0.4,
    });
  }
};

  const createEventMarker = (
    eventData,
    photoUrl,
    eventIndex = 0,
    totalEvents = 1,
    allEventsAtThisPlace = [],
  ) => {
    // Only create marker for the first event; others are just counted
    if (eventIndex > 0) {
      return null;
    }

    const getEventStatusColor = (event) => {
      // USE MASTER STATUS CALCULATOR
      const status = window.calculateEventStatus(
        event.startDateTime,
        event.endDateTime,
      );
      return status.statusColor;
    };

    const shouldShowPulse = (event) => {
      const status = window.calculateEventStatus(
        event.startDateTime,
        event.endDateTime,
      );
      return status.shouldPulse;
    };

    // Get colors and pulse status for first event
    const firstEventColor = getEventStatusColor(eventData);
    const firstEventShowPulse = shouldShowPulse(eventData);

    // Get colors and pulse status for second event
    const secondEvent =
      allEventsAtThisPlace && allEventsAtThisPlace.length > 1
        ? allEventsAtThisPlace[1]
        : eventData;

    const secondEventColor =
      totalEvents > 1 ? getEventStatusColor(secondEvent) : firstEventColor;
    const secondEventShowPulse =
      totalEvents > 1 ? shouldShowPulse(secondEvent) : firstEventShowPulse;

    // NOW you can add debug logging AFTER all variables are calculated
    if (totalEvents > 1 && allEventsAtThisPlace && allEventsAtThisPlace[1]) {
    }

    const showCountBadge = totalEvents > 1;
    const additionalCount = totalEvents - 1;

    const eventMarkerHtml = `
    <div style="position: relative; width: 64px; height: 64px;" data-place-id="${eventData.placeId}">
      <!-- Add Event Button (standard places only) -->
      ${(eventData.placeId && !eventData.placeId.startsWith('custom_')) ? `
      <div 
        id="addEventBtn_${eventData.placeId}"
        class="add-event-button"
        style="
          position: absolute;
          left: -56px;
          top: 8px;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 2px solid rgba(158, 158, 158, 0.6);
          background: rgba(245, 245, 245, 0.95);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          z-index: 100;
          opacity: 1;
        "
        onmouseover="this.style.transform='scale(1.08)'; this.style.boxShadow='0 4px 12px rgba(0,0,0,0.2)'; this.style.background='rgba(245, 245, 245, 1)'"
        onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 2px 8px rgba(0,0,0,0.15)'; this.style.background='rgba(245, 245, 245, 0.95)'"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(117, 117, 117, 0.85)" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </div>
      ` : ''}
          
      <!-- Additional Events Count Circle -->
      ${
        showCountBadge
          ? `
        <div style="position: absolute; right: -35px; top: 0; width: 64px; height: 64px;">
<!-- Second event outer pulse -->
<div data-marker-type="second-pulse-outer" style="
  position: absolute;
  inset: -15px;
  background: ${secondEventColor};
  opacity: 0.5;
  border-radius: 50%;
  animation: slowPing 2.5s ease-in-out infinite;
  display: ${secondEventShowPulse ? "block" : "none"};
"></div>

<!-- Second event middle pulse -->
<div data-marker-type="second-pulse-middle" style="
  position: absolute;
  inset: -8px;
  background: ${secondEventColor};
  opacity: 0.6;
  border-radius: 50%;
  animation: slowPing 2.5s ease-in-out infinite 0.3s;
  display: ${secondEventShowPulse ? "block" : "none"};
"></div>

<!-- Second event inner pulse -->
<div data-marker-type="second-pulse" style="
  position: absolute;
  inset: 0;
  background: ${secondEventColor};
  opacity: 0.7;
  border-radius: 50%;
  animation: slowPing 2.5s ease-in-out infinite 0.6s;
  display: ${secondEventShowPulse ? "block" : "none"};
"></div>
          
          <div data-marker-type="second-circle" style="
            position: relative;
            width: 64px;
            height: 64px;
            border-radius: 50%;
            border: 3px solid ${secondEventColor};
            background: rgba(245, 245, 245, 0.95);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding-right: 8px;
            gap: 1px;
            z-index: 5;
          ">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(117, 117, 117, 0.85)" stroke-width="2.5" stroke-linecap="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span style="
              font-size: 16px;
              font-weight: 600;
              color: rgba(117, 117, 117, 0.95);
              font-family: 'Roboto', Arial, sans-serif;
              line-height: 1;
            ">${additionalCount}</span>
          </div>
        </div>
      `
          : ""
      }

<!-- Countdown Badge (only for upcoming/starting soon events) -->
${(() => {
  const cStatus = window.calculateEventStatus(
    eventData.startDateTime,
    eventData.endDateTime,
  );
  if (cStatus.hasStarted || cStatus.hasEnded) return "";

  const cDiff = cStatus.startDate - cStatus.now;
  if (cDiff <= 0 || cDiff > 30 * 24 * 60 * 60 * 1000) return "";

  // Check if user has badge (organizer/participant) to adjust positioning
  const currentUserId = window.currentAppUser?.uid;
  const hasBadge =
    allEventsAtThisPlace?.some((e) => e.creatorId === currentUserId) ||
    allEventsAtThisPlace?.some(
      (e) =>
        e.participants?.includes(currentUserId) &&
        e.creatorId !== currentUserId,
    );
  const countdownOffset = hasBadge ? 45 : 39;

  return `
    <div data-marker-type="countdown" data-marker-countdown data-event-start="${eventData.startDateTime}" style="
      position: absolute;
      bottom: calc(100% + ${countdownOffset}px);
      left: 50%;
      transform: translateX(-50%);
      background: white;
      border: 2px solid ${firstEventColor};
      border-radius: 8px 8px 0 0;
      border-bottom: none;
      padding: 4px 12px;
      white-space: nowrap;
      box-shadow: 0 -2px 6px rgba(0,0,0,0.15);
      display: flex;
      align-items: center;
      gap: 0;
      z-index: 2;
      pointer-events: none;
    ">
      <span data-cd="days" style="font-size: 14px; font-weight: 500; color: #202124; min-width: 20px; text-align: center;">--</span>
      <span style="font-size: 14px; font-weight: 500; color: #202124; margin: 0 3px;">:</span>
      <span data-cd="hours" style="font-size: 14px; font-weight: 500; color: #202124; min-width: 20px; text-align: center;">--</span>
      <span style="font-size: 14px; font-weight: 500; color: #202124; margin: 0 3px;">:</span>
      <span data-cd="minutes" style="font-size: 14px; font-weight: 500; color: #202124; min-width: 20px; text-align: center;">--</span>
      <span style="font-size: 14px; font-weight: 500; color: #202124; margin: 0 3px;">:</span>
      <span data-cd="seconds" style="font-size: 14px; font-weight: 500; color: #202124; min-width: 20px; text-align: center;">--</span>
    </div>
  `;
})()}
          
<!-- Place Name Label -->
<div data-marker-type="label" style="
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  background: white;
  border: 2px solid ${firstEventColor};
  border-radius: 8px;
  padding: 4px 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
">

<!-- Organizer Crown Badge OR Participant Checkmark Badge -->
${(() => {
  const currentUserId = window.currentAppUser?.uid;

  // Check ALL events at this location (same placeId)
  const eventsAtThisPlace = allEventsAtThisPlace.filter(
    (e) => e.placeId === eventData.placeId,
  );

  // Check if user is organizer or participant in ANY event at this location
  const isOrganizerOfAny = eventsAtThisPlace.some(
    (e) => e.creatorId === currentUserId,
  );
  const isParticipantOfAny = eventsAtThisPlace.some(
    (e) =>
      e.participants?.includes(currentUserId) && e.creatorId !== currentUserId,
  );

  const hasBadge = isOrganizerOfAny || isParticipantOfAny;

  // Badge HTML
  let badgeHTML = "";
  if (isOrganizerOfAny) {
    badgeHTML = `
      <div style="
        width: 25px;
        height: 25px;
        background: #e1e1e1fa;
        border: 2px solid rgba(0, 158, 177, 1);
        border-radius: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
        flex-shrink: 0;
      ">
        <span style="font-size: 16px; line-height: 1; margin-top: -2px;">👑</span>
      </div>
    `;
  } else if (isParticipantOfAny) {
    badgeHTML = `
      <div style="
        width: 25px;
        height: 25px;
        background: white;
        border: 2px solid rgba(52, 168, 83, 1);
        border-radius: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
        flex-shrink: 0;
      ">
        <span style="font-size: 16px; line-height: 1; color: green;">✓</span>
      </div>
    `;
  }

  return `
    ${badgeHTML}
    <span style="
      font-size: 13px;
      font-weight: 600;
      color: #202124;
      font-family: 'Roboto', Arial, sans-serif;
      ${hasBadge ? "padding-left: 8px; border-left: 1px solid #a9a9a9;" : ""}
    ">${eventData.placeName}</span>
  `;
})()}
  
  <!-- Participant Count Container -->
  <div style="
    display: flex;
    align-items: center;
    gap: 4px;
    padding-left: 8px;
    border-left: 1px solid #a9a9a9;
  ">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
<span style="
  font-size: 12px;
  font-weight: 600;
  color: #5f6368;
  font-family: 'Roboto', Arial, sans-serif;
">
  ${(() => {
    const participantCount = eventData.participants?.length || 1;
    const limit = eventData.peopleLimit;

    if (limit === null || limit === undefined) {
      return `${participantCount} / ∞`;
    } else {
      return `${participantCount} / ${limit}`;
    }
  })()}
</span>
  </div>
</div>
      
<!-- Outer pulse ring -->
<div data-marker-type="first-pulse-outer" style="
  position: absolute;
  inset: -15px;
  background: ${firstEventColor};
  opacity: 0.5;
  border-radius: 50%;
  animation: slowPing 2.5s ease-in-out infinite;
  display: ${firstEventShowPulse ? "block" : "none"};
"></div>

<!-- Middle pulse ring -->
<div data-marker-type="first-pulse-middle" style="
  position: absolute;
  inset: -8px;
  background: ${firstEventColor};
  opacity: 0.6;
  border-radius: 50%;
  animation: slowPing 2.5s ease-in-out infinite 0.3s;
  display: ${firstEventShowPulse ? "block" : "none"};
"></div>

<!-- Inner pulse ring -->
<div data-marker-type="first-pulse" style="
  position: absolute;
  inset: 0;
  background: ${firstEventColor};
  opacity: 0.7;
  border-radius: 50%;
  animation: slowPing 2.5s ease-in-out infinite 0.6s;
  display: ${firstEventShowPulse ? "block" : "none"};
"></div>
      
      <!-- Event Circle -->
      <div data-marker-type="first-circle" style="
        position: relative;
        z-index: 10; 
        width: 64px;
        height: 64px;
        border-radius: 50%;
        overflow: hidden;
        border: 3px solid ${firstEventColor};
        box-shadow: 0 10px 25px rgba(0,0,0,0.3);
        background: white;
      ">
        ${
          photoUrl && !photoUrl.includes("gstatic.com/mapfiles/place_api/icons")
            ? `<img src="${photoUrl}" style="width: 100%; height: 100%; object-fit: cover;" />`
            : `<div style="width: 100%; height: 100%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; font-size: 28px;">📍</div>`
        }
      </div>
    </div>
  `;

    // Create Leaflet marker (replace the Google Maps marker creation)
    const eventMarkerDiv = document.createElement("div");
    eventMarkerDiv.innerHTML = eventMarkerHtml;
    eventMarkerDiv.setAttribute(
      "data-event-ids",
      JSON.stringify(allEventsAtThisPlace.map((e) => e.eventId)),
    );
    eventMarkerDiv.dataset.placeId = eventData.placeId;
    eventMarkerDiv.classList.add("event-marker-container");

    const eventIcon = L.divIcon({
      className: "custom-event-marker",
      html: eventMarkerDiv.outerHTML,
      iconSize: [64, 64],
      iconAnchor: [32, 64],
    });

    // Calculate z-index based on event status priority
    const now = new Date();
    const startDate = new Date(eventData.startDateTime);
    const endDate = eventData.endDateTime
      ? new Date(eventData.endDateTime)
      : null;

    const hasStarted = now >= startDate;
    const hasEnded = endDate && now >= endDate;
    const isOngoing = hasStarted && !hasEnded && endDate;

    // Priority: Ongoing (highest) > Starting soon > Finished > Future
    let zIndexPriority = 0;
    if (isOngoing) {
      zIndexPriority = 1000; // Ongoing - highest priority
    } else if (hasEnded) {
      zIndexPriority = 800; // Finished - high priority
    } else {
      // Upcoming events - priority based on how soon they start
      const hoursUntilStart = (startDate - now) / (1000 * 60 * 60);
      if (hoursUntilStart < 24) {
        zIndexPriority = 900; // Starting within 24 hours - very high priority
      } else {
        zIndexPriority = 500; // Future events - lower priority
      }
    }

    const eventMarker = L.marker([eventData.latitude, eventData.longitude], {
      icon: eventIcon,
      zIndexOffset: zIndexPriority - eventIndex,
    });

    eventMarker.eventCount = totalEvents;
    eventMarker.placeId = eventData.placeId;
    eventMarker.lat = eventData.latitude;
    eventMarker.lng = eventData.longitude;

    eventMarker.on("click", async (e) => {
      e.originalEvent.stopPropagation();

      const clickedElement = e.originalEvent.target;
      const addButton = clickedElement.closest(
        `#addEventBtn_${eventData.placeId}`,
      );

      if (addButton) {
        // Check if this is already a custom location
        if (eventData.placeId && eventData.placeId.startsWith("custom_")) {
          // Reuse the existing custom place ID
          window.handleLinkUp(
            eventData.placeId, // ← Reuse existing placeId
            eventData.placeName,
            eventData.placePhotoUrl,
            getGoogleMapsUrl(
              eventData.placeId,
              eventData.latitude,
              eventData.longitude,
            ),
            eventData.placeAddress,
            eventData.latitude,
            eventData.longitude,
            eventData.placeIcon || "📍",
            "place",
          );
        } else {
          // For normal places, use handleLinkUp directly
          window.handleLinkUp(
            eventData.placeId,
            eventData.placeName,
            eventData.placePhotoUrl,
            getGoogleMapsUrl(
              eventData.placeId,
              eventData.latitude,
              eventData.longitude,
            ),
            eventData.placeAddress,
            eventData.latitude,
            eventData.longitude,
            eventData.placeIcon || "📍",
            "place",
          );
        }
      } else {
        if (totalEvents > 1) {
await window.showEventsListPanel(
  eventData.placeId,
  eventData.placeName,
  eventData.placeAddress,
  eventData.latitude,
  eventData.longitude,
);
        } else {
          closeAllPanels();
          await new Promise((resolve) => setTimeout(resolve, 100));
          window.openedFromList = false;
          window.showEventDetailsPanel(eventData, photoUrl);
        }
      }
    });

    return eventMarker;
  };

  window.createEventMarker = createEventMarker;

  // Global marker countdown ticker - only set up once
  if (!window._markerCountdownInitialized) {
    window._markerCountdownInitialized = true;

    window.updateAllMarkerCountdowns = () => {
      document.querySelectorAll("[data-marker-countdown]").forEach((badge) => {
        const eventStart = badge.getAttribute("data-event-start");
        if (!eventStart) return;

        const daysEl = badge.querySelector('[data-cd="days"]');
        const hoursEl = badge.querySelector('[data-cd="hours"]');
        const minutesEl = badge.querySelector('[data-cd="minutes"]');
        const secondsEl = badge.querySelector('[data-cd="seconds"]');

        if (!daysEl || !hoursEl || !minutesEl || !secondsEl) return;

        const now = new Date();
        const eventTimeLocal = eventStart
          .replace(/[+-]\d{2}:\d{2}$/, "")
          .replace("Z", "");
        const eventDate = new Date(eventTimeLocal);
        const diff = eventDate.getTime() - now.getTime();

        if (diff <= 0) {
          if (!badge.dataset.hiding) {
            badge.dataset.hiding = "true";
            badge.style.transition = "opacity 1s ease";
            badge.style.opacity = "0";
            setTimeout(() => {
              badge.style.display = "none";
            }, 1000);
          }
          return;
        }

        daysEl.textContent = String(
          Math.floor(diff / (1000 * 60 * 60 * 24)),
        ).padStart(2, "0");
        hoursEl.textContent = String(
          Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        ).padStart(2, "0");
        minutesEl.textContent = String(
          Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        ).padStart(2, "0");
        secondsEl.textContent = String(
          Math.floor((diff % (1000 * 60)) / 1000),
        ).padStart(2, "0");
      });
    };

    // Tick every second
    window.globalMarkerCountdownInterval = setInterval(
      window.updateAllMarkerCountdowns,
      1000,
    );
  }

  const updateAllMarkerColors = async () => {
    if (!googleMapRef.current || eventMarkersRef.current.length === 0) return;

    const { getAllEvents } = await import("./supabase/events");
    const { success, events } = await getAllEvents();

    if (!success) return;
    window.allEvents = events;

    const now = new Date();

    eventMarkersRef.current.forEach((marker) => {
      if (!marker) return;

      // For Leaflet, access the HTML element
      const markerElement = marker.getElement();
      if (!markerElement) return;

      const placeId =
        markerElement.querySelector("[data-place-id]")?.dataset?.placeId;

      if (!placeId) return;

      // Filter events for this place
      const eventsAtPlace = events.filter((event) => event.placeId === placeId);

      if (eventsAtPlace.length === 0) return;

      // Sort events
      const ongoing = [];
      const upcoming = [];
      const finished = [];

      eventsAtPlace.forEach((event) => {
        const startDate = new Date(event.startDateTime);
        const endDate = event.endDateTime ? new Date(event.endDateTime) : null;

        const hasStarted = now >= startDate;
        const hasEnded = endDate && now >= endDate;

        if (hasEnded) {
          finished.push(event);
        } else if (hasStarted && endDate) {
          ongoing.push(event);
        } else {
          upcoming.push(event);
        }
      });

      ongoing.sort((a, b) => new Date(a.endDateTime) - new Date(b.endDateTime));
      upcoming.sort(
        (a, b) => new Date(a.startDateTime) - new Date(b.startDateTime),
      );
      finished.sort(
        (a, b) => new Date(b.endDateTime) - new Date(a.endDateTime),
      );

      const sortedEvents = [...ongoing, ...upcoming, ...finished];

      if (sortedEvents.length === 0) return;

      // Calculate status colors
      const getEventStatusColor = (event) => {
        const now = new Date();
        const startDate = new Date(event.startDateTime);
        const endDate = event.endDateTime ? new Date(event.endDateTime) : null;

        const hasStarted = now >= startDate;
        const hasEnded = endDate && now >= endDate;
        const isOngoing = hasStarted && !hasEnded && endDate;

        if (hasEnded) {
          return "#ea4335";
        } else if (isOngoing) {
          const totalDuration = endDate - startDate;
          const remainingPercentage = ((endDate - now) / totalDuration) * 100;

          if (remainingPercentage >= 80) {
            return "#34a853";
          } else if (remainingPercentage >= 30) {
            return "#fbbc04";
          } else {
            return "#ea4335";
          }
        } else {
          const hoursUntilStart = (startDate - now) / (1000 * 60 * 60);
          if (hoursUntilStart < 24) {
            return "#34a853";
          } else {
            return "#959393";
          }
        }
      };

      const firstEventColor = getEventStatusColor(sortedEvents[0]);
      const secondEventColor =
        sortedEvents.length > 1
          ? getEventStatusColor(sortedEvents[1])
          : firstEventColor;

      // Update first event circle border - USE markerElement instead of markerDiv
      const firstCircle = markerElement.querySelector(
        '[data-marker-type="first-circle"]',
      );
      if (firstCircle) {
        firstCircle.style.borderColor = firstEventColor;
      }

      // Update first event pulses (all three rings)
      const firstPulseOuter = markerElement.querySelector(
        '[data-marker-type="first-pulse-outer"]',
      );
      const firstPulseMiddle = markerElement.querySelector(
        '[data-marker-type="first-pulse-middle"]',
      );
      const firstPulse = markerElement.querySelector(
        '[data-marker-type="first-pulse"]',
      );

      const firstStartDate = new Date(sortedEvents[0].startDateTime);
      const firstEndDate = sortedEvents[0].endDateTime
        ? new Date(sortedEvents[0].endDateTime)
        : null;
      const firstHasStarted = now >= firstStartDate;
      const firstHasEnded = firstEndDate && now >= firstEndDate;
      const firstIsOngoing = firstHasStarted && !firstHasEnded;

      const firstShouldPulse = firstIsOngoing || !firstHasStarted;

      if (!firstShouldPulse) {
        if (firstPulseOuter) firstPulseOuter.style.display = "none";
        if (firstPulseMiddle) firstPulseMiddle.style.display = "none";
        if (firstPulse) firstPulse.style.display = "none";
      } else {
        if (firstPulseOuter) {
          firstPulseOuter.style.display = "block";
          firstPulseOuter.style.background = firstEventColor;
        }
        if (firstPulseMiddle) {
          firstPulseMiddle.style.display = "block";
          firstPulseMiddle.style.background = firstEventColor;
        }
        if (firstPulse) {
          firstPulse.style.display = "block";
          firstPulse.style.background = firstEventColor;
        }
      }

      // Update label border
      const label = markerElement.querySelector('[data-marker-type="label"]');
      if (label) {
        label.style.borderColor = firstEventColor;
      }

      // Update second event circle
      const secondCircle = markerElement.querySelector(
        '[data-marker-type="second-circle"]',
      );
      if (secondCircle) {
        secondCircle.style.borderColor = secondEventColor;
      }

      // Update second event pulses (all three rings)
      const secondPulseOuter = markerElement.querySelector(
        '[data-marker-type="second-pulse-outer"]',
      );
      const secondPulseMiddle = markerElement.querySelector(
        '[data-marker-type="second-pulse-middle"]',
      );
      const secondPulse = markerElement.querySelector(
        '[data-marker-type="second-pulse"]',
      );

      if (sortedEvents.length > 1) {
        const secondStartDate = new Date(sortedEvents[1].startDateTime);
        const secondEndDate = sortedEvents[1].endDateTime
          ? new Date(sortedEvents[1].endDateTime)
          : null;
        const secondHasStarted = now >= secondStartDate;
        const secondHasEnded = secondEndDate && now >= secondEndDate;
        const secondIsOngoing = secondHasStarted && !secondHasEnded;

        const secondShouldPulse = secondIsOngoing || !secondHasStarted;

        if (!secondShouldPulse) {
          if (secondPulseOuter) secondPulseOuter.style.display = "none";
          if (secondPulseMiddle) secondPulseMiddle.style.display = "none";
          if (secondPulse) secondPulse.style.display = "none";
        } else {
          if (secondPulseOuter) {
            secondPulseOuter.style.display = "block";
            secondPulseOuter.style.background = secondEventColor;
          }
          if (secondPulseMiddle) {
            secondPulseMiddle.style.display = "block";
            secondPulseMiddle.style.background = secondEventColor;
          }
          if (secondPulse) {
            secondPulse.style.display = "block";
            secondPulse.style.background = secondEventColor;
          }
        }
      } else {
        if (secondPulseOuter) secondPulseOuter.style.display = "none";
        if (secondPulseMiddle) secondPulseMiddle.style.display = "none";
        if (secondPulse) secondPulse.style.display = "none";
      }
    });
  };

  const closeAllPanels = () => {
    // Clean up real-time subscriptions
    window.cleanupEventRealtimeUpdates();

    // Clean up participant status subscription and interval
    if (window.participantStatusSubscription) {
      const supabaseClient = window.supabase || supabase;
      supabaseClient.removeChannel(window.participantStatusSubscription);
      window.participantStatusSubscription = null;
    }

    if (window.participantStatusInterval) {
      clearInterval(window.participantStatusInterval);
      window.participantStatusInterval = null;
    }

    // Clean up intervals
    if (window.cleanupEventIntervals) {
      window.cleanupEventIntervals();
    }

    // Close events list panel
    const listPanel = document.getElementById("eventsListPanel");
    if (listPanel) {
      listPanel.style.right = "-400px";
      setTimeout(() => listPanel.remove(), 300);
    }

    // Close event details panel
    const detailsPanel = document.getElementById("eventDetailsPanel");
    if (detailsPanel) {
      detailsPanel.style.right = "-400px";
      setTimeout(() => detailsPanel.remove(), 300);
    }

    // Reset the flag
    window.openedFromList = false;
  };

  // Make it globally available for onclick handlers
  window.closeAllPanels = closeAllPanels;

  const openCreateEventPanelWithCustomLocation = async (location) => {
    // Try to detect place type via reverse geocoding
    const placeInfo = await reverseGeocode(location.lat, location.lng);

    let placeName, placeAddress, placeEmoji, photoUrl;

    if (placeInfo) {
      placeName = placeInfo.name;
      placeAddress = placeInfo.address;
      placeEmoji = placeInfo.emoji;

      // Use modern icon based on detected type
      const modernIconSVG = getModernPlaceIcon(placeInfo.type);
      photoUrl = `data:image/svg+xml,${encodeURIComponent(modernIconSVG)}`;
    } else {
      placeName = "Custom Location";
      placeAddress = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
      placeEmoji = "📍";
      photoUrl = "/assets/images/custom-pin.png";
    }

    const customPlaceId = `custom_${Date.now()}`;

    // Store location globally for the form submission
    window.currentPlaceLocation = { lat: location.lat, lng: location.lng };

    // Call the existing handleLinkUp function with detected type
    window.handleLinkUp(
      customPlaceId,
      placeName,
      photoUrl,
      `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`,
      placeAddress,
      location.lat,
      location.lng,
      placeEmoji,
      placeInfo?.type || "place", // Use detected type
    );
  };

  const getCurrentLocation = async () => {
    const applyLocation = (lat, lng) => {
      if (!isFinite(lat) || !isFinite(lng)) return;
      const location = { lat, lng };
      setUserLocation(location);
      window.currentUserLocation = location;
      if (window.leafletMap) {
        window.leafletMap.setView([lat, lng], 17);
        // Invalidate after setView so tiles reload correctly after permission dialog resume
        setTimeout(() => window.leafletMap?.invalidateSize(), 100);
        setTimeout(() => window.leafletMap?.invalidateSize(), 600);
      }
    };

    if (window.Capacitor?.isNativePlatform?.()) {
      // Native Android — check current permission state. If already granted,
      // call getCurrentPosition. If not granted, dispatch event so the UI
      // banner can request permission from a user-gesture context (some
      // Android OEMs require this).
      console.log('[geo] checking permission state...');
      let perm;
      try { perm = await Geolocation.checkPermissions(); }
      catch (e) { console.warn('[geo] checkPermissions error:', e?.message || e); }
      console.log('[geo] checkPermissions →', perm);

      setTimeout(() => window.leafletMap?.invalidateSize(), 300);

      const granted = perm && (perm.location === 'granted' || perm.coarseLocation === 'granted');
      if (!granted) {
        console.warn('[geo] permission not granted, asking user via banner');
        window.dispatchEvent(new CustomEvent('locationPermissionNeeded'));
        return;
      }

      try {
        console.log('[geo] calling Geolocation.getCurrentPosition...');
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 60000,
        });
        console.log('[geo] got position:', pos?.coords?.latitude, pos?.coords?.longitude);
        applyLocation(pos.coords.latitude, pos.coords.longitude);
      } catch (e) {
        console.warn('[geo] getCurrentPosition failed:', e?.message || e);
        try {
          const pos2 = await Geolocation.getCurrentPosition({
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 300000,
          });
          applyLocation(pos2.coords.latitude, pos2.coords.longitude);
        } catch (e2) {
          console.warn('[geo] low-accuracy also failed:', e2?.message || e2);
          window.dispatchEvent(new CustomEvent('locationPermissionNeeded'));
        }
        setTimeout(() => window.leafletMap?.invalidateSize(), 500);
      }
    } else {
      // Web — use standard browser geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => applyLocation(position.coords.latitude, position.coords.longitude),
          () => {},
          { timeout: 15000, maximumAge: 60000, enableHighAccuracy: false },
        );
      }
    }
  };

  const navigateTo = (view) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentView(view);
      setIsAnimating(false);
    }, 300);
  };

  const handleLogin = async () => {
    setError("");
    if (isAuthLoading) return;

    if (!loginEmail || !loginPassword) {
      setError("Please fill in all fields");
      return;
    }

    try {
      setIsAuthLoading(true);

      const isNative = !!(window.Capacitor?.isNativePlatform?.());
      if (!captchaToken && !isNative) {
        setError(
          "Security verification not ready. Please wait a moment and try again.",
        );
        setIsAuthLoading(false);
        return;
      }
      const { success, user, error } = await loginUser(
        loginEmail,
        loginPassword,
      );

      if (!success && error === "Email not confirmed") {
        setError(
          "Please verify your email address first. Check your inbox for the confirmation link.",
        );
        return;
      }

      if (success && user) {
        setFirebaseUser(user);

        // Check if user has profile
        const { success: profileSuccess, profile } = await getUserProfile(
          user.id,
        );

        if (profileSuccess && profile) {
          setCurrentUser({
            email: user.email,
            uid: user.id,
            profile: {
              name: profile.name,
              profilePicture: profile.profile_picture,
              background: profile.background,
              description: profile.description,
              dateOfBirth: profile.date_of_birth,
              gender: profile.gender,
            },
            hasProfile: true,
          });

          window.currentAppUser = {
            email: user.email,
            uid: user.id,
            profile: {
              name: profile.name,
              profilePicture: profile.profile_picture,
              background: profile.background,
              description: profile.description,
              gender: profile.gender,
              dateOfBirth: profile.date_of_birth,
            },
          };

          navigateTo("map");
        } else {
          setCurrentUser({
            email: user.email,
            uid: user.id,
            hasProfile: false,
          });
          navigateTo("profile-setup");
        }

        // After user logs in successfully
        window.setupGlobalMessageListener();
      } else {
        setError(error || "Login failed");
      }
    } catch (error) {
      setError("Login failed. Please check your credentials.");
      console.error("Login error:", error);
    } finally {
      setIsAuthLoading(false);
      setCaptchaToken(null);
      // Reset Turnstile widget
      const loginContainer = document.getElementById("turnstile-login");
      if (loginContainer && window.turnstile) {
        loginContainer.dataset.rendered = "false";
        loginContainer.innerHTML = "";
        const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        if (siteKey) {
          window.turnstile.render(loginContainer, {
            sitekey: siteKey,
            theme: "light",
            size: "flexible",
            callback: (token) => setCaptchaToken(token),
            "expired-callback": () => setCaptchaToken(null),
            "error-callback": () => setCaptchaToken(null),
          });
          loginContainer.dataset.rendered = "true";
        }
      }
    }
  };

  const handleLogOut = () => {
    window.showLogoutConfirm();
  };

  window.performLogout = async () => {
    try {
      // Set flag to prevent any map operations
      window.isLoggingOut = true;

      // Stop all intervals FIRST
      if (window.markerColorUpdateInterval) {
        clearInterval(window.markerColorUpdateInterval);
        window.markerColorUpdateInterval = null;
      }

      if (window.globalMarkerCountdownInterval) {
        clearInterval(window.globalMarkerCountdownInterval);
        window.globalMarkerCountdownInterval = null;
      }

      if (window.participantStatusInterval) {
        clearInterval(window.participantStatusInterval);
        window.participantStatusInterval = null;
      }

      if (window.cleanupEventIntervals) {
        window.cleanupEventIntervals();
      }

      if (window.statusUpdateInterval) {
        clearInterval(window.statusUpdateInterval);
        window.statusUpdateInterval = null;
      }

      if (window.markerCountdownObserver) {
        window.markerCountdownObserver.disconnect();
        window.markerCountdownObserver = null;
      }
      window._markerCountdownInitialized = false;

      // Small delay to let pending operations complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Now safely remove the map
      if (googleMapRef.current) {
        try {
          googleMapRef.current.remove();
        } catch (e) {
          console.warn("Error removing map:", e);
        }
        googleMapRef.current = null;
      }

      // Also clear window.leafletMap reference
      window.leafletMap = null;
      window.markerClusterGroup = null;

      if (markerRef.current) {
        try {
          markerRef.current.remove();
        } catch (e) {
          console.warn("Error removing marker:", e);
        }
        markerRef.current = null;
      }

      if (eventMarkersRef.current) {
        eventMarkersRef.current.forEach((marker) => {
          try {
            if (marker) marker.remove();
          } catch (e) {}
        });
        eventMarkersRef.current = [];
      }

      // Clean up realtime subscriptions
      if (window.cleanupEventRealtimeUpdates) {
        window.cleanupEventRealtimeUpdates();
      }

      // Clean up participant status subscription
      if (window.participantStatusSubscription) {
        const supabaseClient = window.supabase || supabase;
        supabaseClient.removeChannel(window.participantStatusSubscription);
        window.participantStatusSubscription = null;
      }

      await logoutUser();

      setCurrentUser(null);
      setFirebaseUser(null);
      window.currentAppUser = null;

      setIsProfilePanelOpen(false);
      window.closeAllPanels();
      window.closeUserProfilePanel();
      window.closeAllParticipantsPanel();

      if (window.cleanupAllSubscriptions) {
        window.cleanupAllSubscriptions();
      }

      // Clean up notification subscription
      if (window.notificationSubscription) {
        const supabaseClient = window.supabase || supabase;
        supabaseClient.removeChannel(window.notificationSubscription);
        window.notificationSubscription = null;
      }

      // Reset logout flag
      window.isLoggingOut = false;

      navigateTo("landing");
    } catch (error) {
      console.error("❌ Logout error:", error);
      window.isLoggingOut = false;
      window.showToast("Failed to logout. Please try again.", "error");
    }
  };

  const handleRegister = async () => {
    setError("");
    if (isAuthLoading) return;

    if (!registerEmail || !registerPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (registerPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const passwordError = validatePassword(registerPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!termsAccepted) {
      setError("You must accept the Terms & Conditions and Privacy Policy");
      return;
    }

    try {
      setIsAuthLoading(true);

      const isNative = !!(window.Capacitor?.isNativePlatform?.());
      if (!captchaToken && !isNative) {
        setError(
          "Security verification not ready. Please wait a moment and try again.",
        );
        setIsAuthLoading(false);
        return;
      }
      const { success, user, error } = await registerUser(
        registerEmail,
        registerPassword,
      );

      if (success && user) {
        // With email confirmation enabled, user needs to verify email first
        // Don't navigate to profile-setup yet
        setEmailConfirmationSent(true);
      } else {
        if (error && error.includes("already exists")) {
          setError("ACCOUNT_EXISTS");
        } else {
          setError(error || "Registration failed");
        }
      }
    } catch (error) {
      setError("Registration failed. Please try again.");
      console.error("Registration error:", error);
    } finally {
      setIsAuthLoading(false);
      setCaptchaToken(null);
      // Reset Turnstile widget
      const regContainer = document.getElementById("turnstile-register");
      if (regContainer && window.turnstile) {
        regContainer.dataset.rendered = "false";
        regContainer.innerHTML = "";
        const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        if (siteKey) {
          window.turnstile.render(regContainer, {
            sitekey: siteKey,
            theme: "light",
            size: "flexible",
            callback: (token) => setCaptchaToken(token),
            "expired-callback": () => setCaptchaToken(null),
            "error-callback": () => setCaptchaToken(null),
          });
          regContainer.dataset.rendered = "true";
        }
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      window.showToast(
        "Please upload an image file (JPEG, PNG, or WebP)",
        "error",
      );
      e.target.value = ""; // Reset input
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      window.showToast(
        "Image is too large. Please upload an image smaller than 2MB.",
        "error",
      );
      e.target.value = ""; // Reset input
      return;
    }

    // Store the actual file for upload
    setProfileData({ ...profileData, profilePictureFile: file });

    // Create preview for UI
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileData((prev) => ({ ...prev, profilePicture: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const [isCreating, setIsCreating] = useState(false);

  const handleCreateProfile = async () => {
    setError("");
    setIsCreating(true);

    if (!profileData.name || !profileData.name.trim()) {
      setError("Please enter your name");
      setIsCreating(false);
      return;
    }

    if (!profileData.profilePicture) {
      setError("Please select a profile picture");
      setIsCreating(false);
      return;
    }

    // Google users must accept terms (since they skip the register page)
    if (isGoogleUser && !termsAccepted) {
      setError("You must accept the Terms & Conditions and Privacy Policy");
      setIsCreating(false);
      return;
    }

    if (!dateOfBirth) {
      setError("Please enter your date of birth");
      setIsCreating(false);
      return;
    }

    // Validate minimum age (18 years)
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    const actualAge =
      monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    if (actualAge < 18) {
      setError("You must be at least 18 years old to use Linksy");
      setIsCreating(false);
      return;
    }

    if (!profileData.description || !profileData.description.trim()) {
      setError("Please enter a description about yourself");
      setIsCreating(false);
      return;
    }

    if (!profileData.gender) {
      setError("Please select your gender");
      setIsCreating(false);
      return;
    }

    try {
      let profilePictureUrl;

      // Google users may have a URL already (from Google avatar) without a file
      if (profileData.profilePictureFile) {
        // User uploaded a custom file (or Google user who changed their photo)
        const file = profileData.profilePictureFile;

        const {
          success: uploadSuccess,
          url,
          error: uploadError,
        } = await uploadProfilePicture(firebaseUser.id, file);

        if (!uploadSuccess) {
          setError(uploadError || "Failed to upload image");
          setIsCreating(false);
          return;
        }

        profilePictureUrl = url;
      } else if (isGoogleUser && profileData.profilePicture) {
        // Google user keeping their Google avatar - use the URL directly
        profilePictureUrl = profileData.profilePicture;
      } else {
        setError("Please select a profile picture");
        setIsCreating(false);
        return;
      }

      // Create profile in Supabase
      const { success, profile, error } = await createUserProfile(
        firebaseUser.id,
        {
          name: profileData.name,
          profilePicture: profilePictureUrl,
          background: getRandomGradient(),
          description: profileData.description || "",
          email: firebaseUser.email,
          dateOfBirth: dateOfBirth,
          gender: profileData.gender,
        },
      );

      if (success && profile) {
        const updatedProfileData = {
          name: profile.name,
          profilePicture: profile.profile_picture,
          background: profile.background,
          description: profile.description,
          dateOfBirth: profile.date_of_birth,
          gender: profileData.gender,
        };

        setCurrentUser({
          ...currentUser,
          profile: updatedProfileData,
          hasProfile: true,
        });

        window.currentAppUser = {
          email: firebaseUser.email,
          uid: firebaseUser.id,
          profile: {
            name: profile.name,
            profilePicture: profile.profile_picture,
            background: profile.background,
            description: profile.description,
            gender: profileData.gender,
            dateOfBirth: profile.date_of_birth,
          },
        };

        navigateTo("map");
      } else {
        setError(error || "Failed to create profile");
      }
    } catch (error) {
      setError("Failed to create profile. Please try again.");
      console.error("Profile creation error:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // In the JSX, add loading overlay:
  {
    isCreating && (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(255,255,255,0.95)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div style={{ position: "relative", width: "64px", height: "64px" }}>
          {/* Same animation as loading auth */}
        </div>
      </div>
    );
  }

  // Show maintenance page if enabled
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  const slideAnimation = isAnimating
    ? "opacity-0 translate-x-full"
    : "opacity-100 translate-x-0";

  if (isLoadingAuth || currentView === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex items-center justify-center">
        <style>{`
        @keyframes pinBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pinPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
        @keyframes peoplePulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes ripple {
          0% { transform: translateX(-50%) scale(0.8); opacity: 0.6; }
          100% { transform: translateX(-50%) scale(2.5); opacity: 0; }
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        <div className="text-center">
          <div
            style={{
              position: "relative",
              width: "80px",
              height: "80px",
              margin: "0 auto 20px",
            }}
          >
            {/* Ripple effects */}
            <div
              style={{
                position: "absolute",
                bottom: "-8px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "50px",
                height: "12px",
                background:
                  "radial-gradient(ellipse, rgba(66,133,244,0.4) 0%, transparent 70%)",
                borderRadius: "50%",
                animation: "ripple 1.5s ease-out infinite",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "-8px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "50px",
                height: "12px",
                background:
                  "radial-gradient(ellipse, rgba(66,133,244,0.4) 0%, transparent 70%)",
                borderRadius: "50%",
                animation: "ripple 1.5s ease-out 0.75s infinite",
              }}
            />

            {/* Main pin SVG */}
            <svg
              viewBox="0 0 60 80"
              style={{
                width: "100%",
                height: "100%",
                animation:
                  "pinBounce 1s ease-in-out infinite, pinPulse 2s ease-in-out infinite",
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.15))",
              }}
            >
              {/* Pin body outline */}
              <path
                d="M30 2 C12 2 2 15 2 30 C2 50 30 78 30 78 C30 78 58 50 58 30 C58 15 48 2 30 2 Z"
                fill="none"
                stroke="#2c2c2c"
                strokeWidth="3"
              />

              {/* Inner fill */}
              <path
                d="M30 5 C14 5 5 16 5 29 C5 47 30 73 30 73 C30 73 55 47 55 29 C55 16 46 5 30 5 Z"
                fill="white"
              />

              {/* People group with pulse */}
              <g style={{ animation: "peoplePulse 1.5s ease-in-out infinite" }}>
                {/* Center person */}
                <circle cx="30" cy="24" r="6" fill="#2c2c2c" />
                <ellipse cx="30" cy="40" rx="9" ry="7" fill="#2c2c2c" />

                {/* Left person */}
                <circle cx="16" cy="27" r="4.5" fill="#2c2c2c" />
                <ellipse cx="16" cy="40" rx="6.5" ry="5" fill="#2c2c2c" />

                {/* Right person */}
                <circle cx="44" cy="27" r="4.5" fill="#2c2c2c" />
                <ellipse cx="44" cy="40" rx="6.5" ry="5" fill="#2c2c2c" />
              </g>
            </svg>
          </div>
          <p
            style={{
              color: "#5f6368",
              fontSize: "16px",
              fontFamily: "'Outfit', sans-serif",
              fontWeight: "400",
              animation: "fadeInUp 0.5s ease-out 0.3s backwards",
            }}
          >
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // ── Web is Android-first ─────────────────────────────────────────────────
  // Keep the landing page and guest event preview on the web, but anyone who
  // tries to log in or create an account in a browser is sent to the app
  // download page instead.
  {
    const isWeb = !window.Capacitor?.isNativePlatform?.();
    const authViews = ["login", "register", "forgot-password", "reset-password", "profile-setup"];
    if (isWeb && authViews.includes(currentView)) {
      return <DownloadAppPage />;
    }
  }

  if (currentView === "landing") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col">
        <DevBadge />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          @keyframes pulse-out {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          @keyframes pin-bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
          @keyframes pop-in {
            0% { transform: scale(0); opacity: 0; }
            70% { transform: scale(1.2); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes people-fade {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          @keyframes shadow-pulse {
            0%, 100% { rx: 35; opacity: 0.4; }
            50% { rx: 30; opacity: 0.6; }
          }
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fade-in {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes slide-in-left {
            0% { opacity: 0; transform: translateX(-30px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          @keyframes slide-in-right {
            0% { opacity: 0; transform: translateX(30px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .logo-wrapper {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .pulse-ring {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 3px solid #4285f4;
            border-radius: 1.5rem;
            pointer-events: none;
          }
          .pulse-ring-1 { animation: pulse-out 2.5s ease-out infinite; }
          .pulse-ring-2 { animation: pulse-out 2.5s ease-out 1.25s infinite; }
          .logo-container { animation: float 3s ease-in-out infinite; }
          .main-pin { animation: pin-bounce 2s ease-in-out infinite; transform-origin: center bottom; }
          .ground-shadow { animation: shadow-pulse 2s ease-in-out infinite; }
          .people-group { animation: people-fade 3s ease-in-out infinite; }
          .event-pin-1 { animation: pop-in 0.5s ease-out 0.3s backwards; transform-origin: center bottom; }
          .event-pin-2 { animation: pop-in 0.5s ease-out 0.6s backwards; transform-origin: center bottom; }
          .event-pin-3 { animation: pop-in 0.5s ease-out 0.9s backwards; transform-origin: center bottom; }
          .event-pin-4 { animation: pop-in 0.5s ease-out 1.2s backwards; transform-origin: center bottom; }
          .fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
          .fade-in-up-delay-1 { animation: fade-in-up 0.8s ease-out 0.2s forwards; opacity: 0; }
          .fade-in-up-delay-2 { animation: fade-in-up 0.8s ease-out 0.4s forwards; opacity: 0; }
          .fade-in-up-delay-3 { animation: fade-in-up 0.8s ease-out 0.6s forwards; opacity: 0; }
          .slide-in-left { animation: slide-in-left 0.6s ease-out forwards; }
          .slide-in-right { animation: slide-in-right 0.6s ease-out forwards; }
          .linksy-font { font-family: 'Outfit', sans-serif; }
          .gradient-text {
            background: linear-gradient(135deg, #4285f4, #34a853, #fbbc04, #ea4335);
            background-size: 200% auto;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: shimmer 3s linear infinite;
          }
          .feature-card {
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.8);
            transition: all 0.3s ease;
          }
          .feature-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          }
          .pwa-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
        `}</style>

        {/* Header */}
        <header className="w-full py-6 px-8">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            {/* Linksy Logo Text */}
            <button className="flex items-center gap-1 linksy-font hover:opacity-70 transition-opacity">
              <span className="text-2xl font-bold text-gray-800">L</span>
              <svg
                viewBox="0 0 40 55"
                className="w-5 h-7 -mx-0.5"
                style={{ marginBottom: "-4px" }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#2c2c2c"
                  strokeWidth="2.5"
                />
                {/* Center person (front, larger) */}
                <circle cx="20" cy="14" r="4" fill="#2c2c2c" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#2c2c2c" />
                {/* Left person */}
                <circle cx="11" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#2c2c2c" />
                {/* Right person */}
                <circle cx="29" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#2c2c2c" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">nksy</span>
            </button>

            {/* Desktop nav buttons */}
            <div className="hidden sm:flex gap-3">
              <button
                onClick={() => navigateTo("login")}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors linksy-font"
              >
                Login
              </button>
              <button
                onClick={() => navigateTo("register")}
                className="px-6 py-2 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all font-medium linksy-font"
              >
                Get Started
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          <div className="max-w-4xl w-full text-center">
            {/* Animated Logo */}
            <div className="fade-in-up mb-8">
              <div
                className="logo-wrapper"
                style={{ position: "relative", display: "inline-block" }}
              >
                <div className="pulse-ring pulse-ring-1"></div>
                <div className="pulse-ring pulse-ring-2"></div>

                <div
                  className="w-40 h-40 sm:w-48 sm:h-48 mx-auto bg-white rounded-3xl shadow-xl flex items-center justify-center overflow-hidden logo-container border-2 border-gray-200"
                  style={{ position: "relative", zIndex: 1 }}
                >
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    <rect x="0" y="0" width="200" height="200" fill="#ffffff" />
                    <path
                      d="M 0 50 Q 50 45, 100 50 T 200 50"
                      stroke="#f0f0f0"
                      strokeWidth="1"
                      fill="none"
                    />
                    <path
                      d="M 0 100 Q 50 95, 100 100 T 200 100"
                      stroke="#f0f0f0"
                      strokeWidth="1"
                      fill="none"
                    />
                    <path
                      d="M 0 150 Q 50 145, 100 150 T 200 150"
                      stroke="#f0f0f0"
                      strokeWidth="1"
                      fill="none"
                    />
                    <path
                      d="M 50 0 Q 45 50, 50 100 T 50 200"
                      stroke="#f0f0f0"
                      strokeWidth="1"
                      fill="none"
                    />
                    <path
                      d="M 100 0 Q 95 50, 100 100 T 100 200"
                      stroke="#f0f0f0"
                      strokeWidth="1"
                      fill="none"
                    />
                    <path
                      d="M 150 0 Q 145 50, 150 100 T 150 200"
                      stroke="#f0f0f0"
                      strokeWidth="1"
                      fill="none"
                    />

                    <ellipse
                      className="ground-shadow"
                      cx="100"
                      cy="170"
                      rx="35"
                      ry="8"
                      fill="#e0e0e0"
                    />

                    <g className="main-pin">
                      <path
                        d="M100 30 C65 30 45 55 45 85 C45 120 100 170 100 170 C100 170 155 120 155 85 C155 55 135 30 100 30 Z"
                        fill="none"
                        stroke="#2c2c2c"
                        strokeWidth="4"
                      />
                      <circle
                        cx="100"
                        cy="80"
                        r="35"
                        fill="none"
                        stroke="#2c2c2c"
                        strokeWidth="3"
                      />
                      <g className="people-group">
                        <circle cx="100" cy="88" r="8" fill="#2c2c2c" />
                        <circle cx="100" cy="72" r="6" fill="#2c2c2c" />
                        <circle cx="82" cy="90" r="6" fill="#2c2c2c" />
                        <circle cx="82" cy="78" r="5" fill="#2c2c2c" />
                        <circle cx="118" cy="90" r="6" fill="#2c2c2c" />
                        <circle cx="118" cy="78" r="5" fill="#2c2c2c" />
                      </g>
                    </g>

                    <g className="event-pin-1">
                      <path
                        d="M28 18 C21 18 17 25 17 32 C17 40 28 53 28 53 C28 53 39 40 39 32 C39 25 35 18 28 18 Z"
                        fill="#ea4335"
                      />
                      <circle cx="28" cy="30" r="4" fill="white" />
                    </g>
                    <g className="event-pin-2">
                      <path
                        d="M172 18 C165 18 161 25 161 32 C161 40 172 53 172 53 C172 53 183 40 183 32 C183 25 179 18 172 18 Z"
                        fill="#34a853"
                      />
                      <circle cx="172" cy="30" r="4" fill="white" />
                    </g>
                    <g className="event-pin-3">
                      <path
                        d="M28 135 C21 135 17 142 17 149 C17 157 28 170 28 170 C28 170 39 157 39 149 C39 142 35 135 28 135 Z"
                        fill="#4285f4"
                      />
                      <circle cx="28" cy="147" r="4" fill="white" />
                    </g>
                    <g className="event-pin-4">
                      <path
                        d="M172 135 C165 135 161 142 161 149 C161 157 172 170 172 170 C172 170 183 157 183 149 C183 142 179 135 172 135 Z"
                        fill="#fbbc04"
                      />
                      <circle cx="172" cy="147" r="4" fill="white" />
                    </g>
                  </svg>
                </div>
              </div>
            </div>

            {/* Tagline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 linksy-font fade-in-up-delay-1">
              Find your people
            </h1>
            <p className="text-lg sm:text-xl text-gray-500 mb-10 max-w-xl mx-auto linksy-font fade-in-up-delay-2">
              Discover events and connect with people in your area. Real-time.
              Location-based. Instant.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 fade-in-up-delay-3">
              <button
                onClick={() => navigateTo("register")}
                className="px-8 py-4 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 active:scale-95 transition-all font-semibold text-lg shadow-lg hover:shadow-xl linksy-font"
              >
                Get Started — It's Free
              </button>
              <button
                onClick={() => navigateTo("login")}
                className="px-8 py-4 bg-white text-gray-700 rounded-2xl hover:bg-gray-50 active:scale-95 transition-all font-semibold text-lg shadow-md hover:shadow-lg border-2 border-gray-200 linksy-font"
              >
                I have an account
              </button>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-16">
              <div className="feature-card rounded-2xl p-6 border border-gray-100 shadow-lg slide-in-left">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ea4335"
                    strokeWidth="2"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-800 mb-2 linksy-font">
                  No App Store
                </h3>
                <p className="text-gray-500 text-sm linksy-font">
                  Skip the downloads. Works instantly in your browser.
                </p>
              </div>

              <div className="feature-card rounded-2xl p-6 border border-gray-100 shadow-lg fade-in-up">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#34a853"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-800 mb-2 linksy-font">
                  Real-time Events
                </h3>
                <p className="text-gray-500 text-sm linksy-font">
                  See what's happening around you, right now.
                </p>
              </div>

              <div className="feature-card rounded-2xl p-6 border border-gray-100 shadow-lg slide-in-right">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4285f4"
                    strokeWidth="2"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-800 mb-2 linksy-font">
                  Instant Connect
                </h3>
                <p className="text-gray-500 text-sm linksy-font">
                  Chat with event participants immediately.
                </p>
              </div>
            </div>

            {/* PWA Install Banner */}
            <div className="pwa-badge rounded-2xl p-6 text-white max-w-lg mx-auto fade-in-up-delay-3">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-bold linksy-font">On Mobile?</p>
                  <p className="text-white/80 text-sm linksy-font">
                    Install directly from your browser — tap Share, then "Add to
                    Home Screen"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full py-8 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              {/* Logo */}
              <div className="flex items-center gap-1 linksy-font">
                <span className="text-lg font-bold text-gray-400">L</span>
                <svg
                  viewBox="0 0 40 55"
                  className="w-4 h-5 -mx-0.5"
                  style={{ marginBottom: "-3px" }}
                >
                  <path
                    d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2.5"
                  />
                  {/* Center person */}
                  <circle cx="20" cy="14" r="4" fill="#9ca3af" />
                  <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#9ca3af" />
                  {/* Left person */}
                  <circle cx="11" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="11" cy="24" rx="4" ry="3" fill="#9ca3af" />
                  {/* Right person */}
                  <circle cx="29" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="29" cy="24" rx="4" ry="3" fill="#9ca3af" />
                </svg>
                <span className="text-lg font-bold text-gray-400">nksy</span>
              </div>

              {/* Links */}
              <div className="flex gap-3 text-sm linksy-font">
                <a href="https://linksy.community/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Terms</a>
                <span style={{ color: '#9ca3af' }}>·</span>
                <a href="https://linksy.community/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Privacy</a>
              </div>

              {/* Email */}
              <div className="flex items-center gap-1">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span className="text-sm text-gray-400 linksy-font">
                  info@linksy.community
                </span>
              </div>

              {/* Copyright */}
              <p className="text-sm text-gray-400 linksy-font">
                © {new Date().getFullYear()} Linksy
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === "login") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col">
        <DevBadge />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          .linksy-font { font-family: 'Outfit', sans-serif; }
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
.input-modern {
            transition: all 0.3s ease;
            background: #f8fafc;
          }
          .input-modern:focus {
            background: white;
            box-shadow: 0 0 0 4px rgba(66, 133, 244, 0.1);
          }
          /* Hide native browser password reveal icon */
          input[type="password"]::-ms-reveal,
          input[type="password"]::-ms-clear {
            display: none;
          }
          input[type="password"]::-webkit-credentials-auto-fill-button,
          input[type="password"]::-webkit-textfield-decoration-container {
            display: none !important;
          }
          input::-ms-reveal {
            display: none;
          }
        `}</style>

        {/* Header */}
        <header className="w-full py-6 px-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => navigateTo("landing")}
              className="flex items-center gap-1 linksy-font hover:opacity-70 transition-opacity"
            >
              <span className="text-2xl font-bold text-gray-800">L</span>
              <svg
                viewBox="0 0 40 55"
                className="w-5 h-7 -mx-0.5"
                style={{ marginBottom: "-4px" }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#2c2c2c"
                  strokeWidth="2.5"
                />
                {/* Center person (front, larger) */}
                <circle cx="20" cy="14" r="4" fill="#2c2c2c" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#2c2c2c" />
                {/* Left person */}
                <circle cx="11" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#2c2c2c" />
                {/* Right person */}
                <circle cx="29" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#2c2c2c" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">nksy</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div
            className={`max-w-md w-full transition-all duration-300 ${slideAnimation}`}
          >
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100 fade-in-up">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 linksy-font">
                  Welcome Back
                </h1>
                <p className="text-gray-500 linksy-font">
                  Sign in to continue to Linksy
                </p>
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm linksy-font flex items-center gap-3"
                  style={{ marginTop: "10px" }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      flexShrink: 0,
                      width: "18px",
                      height: "18px",
                      overflow: "visible",
                    }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Email
                  </label>
                  <div className="relative">
                    <Mail
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      style={{
                        position: "absolute",
                        right: "14px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {showLoginPassword ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Forgot Password */}
                <div style={{ textAlign: "right", marginTop: "-4px" }}>
                  <button
                    onClick={() => {
                      setError("");
                      setResetPasswordEmail(loginEmail);
                      setResetPasswordSent(false);
                      navigateTo("forgot-password");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#1a73e8",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: "pointer",
                      fontFamily: "'Outfit', sans-serif",
                      marginTop: "10px",
                    }}
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Turnstile CAPTCHA — hidden on native Capacitor app */}
                {!window.Capacitor?.isNativePlatform?.() && (
                <div
                  id="turnstile-login"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "15px",
                  }}
                ></div>
                )}

                <button
                  onClick={handleLogin}
                  disabled={isAuthLoading}
                  className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold flex items-center justify-center gap-2 mt-2 shadow-lg hover:shadow-xl linksy-font"
                  style={{
                    opacity: isAuthLoading ? 0.7 : 1,
                    cursor: isAuthLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isAuthLoading ? (
                    <>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-400 linksy-font">
                      or
                    </span>
                  </div>
                </div>

                <button
                  onClick={async () => {
                    setError("");
                    const { success, error } = await signInWithGoogle();
                    if (!success) {
                      setError(error || "Google sign-in failed");
                    }
                  }}
                  className="w-full bg-white text-gray-700 py-4 px-6 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all font-medium border-2 border-gray-200 linksy-font flex items-center justify-center gap-3"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </button>

                <button
                  onClick={() => {
                    navigateTo("register");
                    setError("");
                  }}
                  className="w-full bg-white text-gray-700 py-4 px-6 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all font-semibold border-2 border-gray-200 linksy-font"
                >
                  Create an Account
                </button>

                <button
                  onClick={() => {
                    navigateTo("landing");
                    setError("");
                  }}
                  className="w-full text-gray-500 py-3 hover:text-gray-700 transition-all font-medium linksy-font flex items-center justify-center gap-2"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full py-6 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-1 linksy-font">
                <span className="text-lg font-bold text-gray-400">L</span>
                <svg
                  viewBox="0 0 40 55"
                  className="w-4 h-5 -mx-0.5"
                  style={{ marginBottom: "-3px" }}
                >
                  <path
                    d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2.5"
                  />
                  <circle cx="20" cy="14" r="4" fill="#9ca3af" />
                  <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#9ca3af" />
                  <circle cx="11" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="11" cy="24" rx="4" ry="3" fill="#9ca3af" />
                  <circle cx="29" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="29" cy="24" rx="4" ry="3" fill="#9ca3af" />
                </svg>
                <span className="text-lg font-bold text-gray-400">nksy</span>
              </div>
              <div className="flex gap-3 text-sm linksy-font">
                <a href="https://linksy.community/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Terms</a>
                <span style={{ color: '#9ca3af' }}>·</span>
                <a href="https://linksy.community/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Privacy</a>
              </div>
              <div className="flex items-center gap-1">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span className="text-sm text-gray-400 linksy-font">
                  support@linksy.community
                </span>
              </div>
              <p className="text-sm text-gray-400 linksy-font">
                © {new Date().getFullYear()} Linksy
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === "register") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col">
        <DevBadge />

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          .linksy-font { font-family: 'Outfit', sans-serif; }
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
.input-modern {
            transition: all 0.3s ease;
            background: #f8fafc;
          }
          .input-modern:focus {
            background: white;
            box-shadow: 0 0 0 4px rgba(66, 133, 244, 0.1);
          }
          input[type="password"]::-ms-reveal,
          input[type="password"]::-ms-clear {
            display: none;
          }
          input[type="password"]::-webkit-credentials-auto-fill-button,
          input[type="password"]::-webkit-textfield-decoration-container {
            display: none !important;
          }
          input::-ms-reveal {
            display: none;
          }
        `}</style>

        {/* Header */}
        <header className="w-full py-6 px-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => navigateTo("landing")}
              className="flex items-center gap-1 linksy-font hover:opacity-70 transition-opacity"
            >
              <span className="text-2xl font-bold text-gray-800">L</span>
              <svg
                viewBox="0 0 40 55"
                className="w-5 h-7 -mx-0.5"
                style={{ marginBottom: "-4px" }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#2c2c2c"
                  strokeWidth="2.5"
                />
                {/* Center person (front, larger) */}
                <circle cx="20" cy="14" r="4" fill="#2c2c2c" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#2c2c2c" />
                {/* Left person */}
                <circle cx="11" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#2c2c2c" />
                {/* Right person */}
                <circle cx="29" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#2c2c2c" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">nksy</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div
            className={`max-w-md w-full transition-all duration-300 ${slideAnimation}`}
          >
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100 fade-in-up">
              {/* Header */}
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 linksy-font">
                  {emailConfirmationSent
                    ? "Check your email"
                    : "Create Account"}
                </h1>
                <p className="text-gray-500 linksy-font">
                  {emailConfirmationSent
                    ? ""
                    : "Join Linksy and find your people"}
                </p>
              </div>

              {emailConfirmationSent && (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      background: "#f0fdf4",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="2"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <p
                    style={{
                      fontSize: "15px",
                      color: "#5f6368",
                      lineHeight: "1.6",
                      margin: "0 0 24px 0",
                      fontFamily: "'Outfit', sans-serif",
                    }}
                  >
                    We've sent a verification link to
                    <br />
                    <strong style={{ color: "#202124" }}>
                      {registerEmail}
                    </strong>
                    <br />
                    <br />
                    Click the link in the email to activate your account. Check
                    your spam folder if you don't see it.
                  </p>
                  <button
                    onClick={() => {
                      setEmailConfirmationSent(false);
                      setRegisterEmail("");
                      setRegisterPassword("");
                      setConfirmPassword("");
                      setTermsAccepted(false);
                      navigateTo("login");
                    }}
                    className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold linksy-font"
                  >
                    Go to Sign In
                  </button>
                </div>
              )}

              {!emailConfirmationSent && (
                <>
                  {error && (
                    <div
                      className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm linksy-font flex items-center gap-3"
                      style={{ marginTop: "10px" }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          flexShrink: 0,
                          width: "18px",
                          height: "18px",
                          overflow: "visible",
                        }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {error === "ACCOUNT_EXISTS" ? (
                        <span>
                          An account with this email already exists.{" "}
                          <button
                            onClick={() => {
                              setError("");
                              setLoginEmail(registerEmail);
                              navigateTo("login");
                            }}
                            style={{
                              color: "#1a73e8",
                              fontWeight: "600",
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              fontSize: "inherit",
                              fontFamily: "inherit",
                            }}
                          >
                            Sign in instead
                          </button>
                        </span>
                      ) : (
                        error
                      )}
                    </div>
                  )}

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                        Email
                      </label>
                      <div className="relative">
                        <Mail
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                          size={20}
                        />
                        <input
                          type="email"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                        Password
                      </label>
                      <div className="relative">
                        <Lock
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                          size={20}
                        />
                        <input
                          type={showRegisterPassword ? "text" : "password"}
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowRegisterPassword(!showRegisterPassword)
                          }
                          style={{
                            position: "absolute",
                            right: "14px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {showRegisterPassword ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9ca3af"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9ca3af"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <Lock
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                          size={20}
                        />
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          style={{
                            position: "absolute",
                            right: "14px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {showConfirmPassword ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9ca3af"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#9ca3af"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Terms & Conditions Checkbox */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="checkbox"
                        id="termsCheckbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        style={{
                          width: "18px",
                          height: "18px",
                          marginTop: "2px",
                          accentColor: "#202124",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      />
                      <label
                        htmlFor="termsCheckbox"
                        style={{
                          fontSize: "13px",
                          color: "#5f6368",
                          lineHeight: "1.5",
                          cursor: "pointer",
                          fontFamily: "'Outfit', sans-serif",
                        }}
                      >
                        I agree to the{" "}
                        <a
                          href="https://linksy.community/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1a73e8",
                            textDecoration: "underline",
                            fontWeight: "600",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://linksy.community/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1a73e8",
                            textDecoration: "underline",
                            fontWeight: "600",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>

                    {/* Turnstile CAPTCHA — hidden on native Capacitor app */}
                    {!window.Capacitor?.isNativePlatform?.() && (
                    <div
                      id="turnstile-register"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "15px",
                      }}
                    ></div>
                    )}

                    <button
                      onClick={handleRegister}
                      disabled={isAuthLoading}
                      className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold flex items-center justify-center gap-2 mt-2 shadow-lg hover:shadow-xl linksy-font"
                      style={{
                        opacity: isAuthLoading ? 0.7 : 1,
                        cursor: isAuthLoading ? "not-allowed" : "pointer",
                      }}
                    >
                      {isAuthLoading ? (
                        <>
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              border: "2px solid rgba(255,255,255,0.3)",
                              borderTop: "2px solid white",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          Creating account...
                        </>
                      ) : (
                        <>
                          Create Account
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>

                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-gray-400 linksy-font">
                          or
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        setError("");
                        const { success, error } = await signInWithGoogle();
                        if (!success) {
                          setError(error || "Google sign-in failed");
                        }
                      }}
                      className="w-full bg-white text-gray-700 py-4 px-6 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all font-medium border-2 border-gray-200 linksy-font flex items-center justify-center gap-3"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Continue with Google
                    </button>

                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-gray-400 linksy-font">
                          already have an account?
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        navigateTo("login");
                        setError("");
                      }}
                      className="w-full bg-white text-gray-700 py-4 px-6 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all font-semibold border-2 border-gray-200 linksy-font"
                    >
                      Sign In Instead
                    </button>

                    <button
                      onClick={() => {
                        navigateTo("landing");
                        setError("");
                      }}
                      className="w-full text-gray-500 py-3 hover:text-gray-700 transition-all font-medium linksy-font flex items-center justify-center gap-2"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      Back to Home
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full py-6 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-1 linksy-font">
                <span className="text-lg font-bold text-gray-400">L</span>
                <svg
                  viewBox="0 0 40 55"
                  className="w-4 h-5 -mx-0.5"
                  style={{ marginBottom: "-3px" }}
                >
                  <path
                    d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2.5"
                  />
                  <circle cx="20" cy="14" r="4" fill="#9ca3af" />
                  <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#9ca3af" />
                  <circle cx="11" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="11" cy="24" rx="4" ry="3" fill="#9ca3af" />
                  <circle cx="29" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="29" cy="24" rx="4" ry="3" fill="#9ca3af" />
                </svg>
                <span className="text-lg font-bold text-gray-400">nksy</span>
              </div>
              <div className="flex gap-3 text-sm linksy-font">
                <a href="https://linksy.community/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Terms</a>
                <span style={{ color: '#9ca3af' }}>·</span>
                <a href="https://linksy.community/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Privacy</a>
              </div>
              <div className="flex items-center gap-1">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span className="text-sm text-gray-400 linksy-font">
                  support@linksy.community
                </span>
              </div>
              <p className="text-sm text-gray-400 linksy-font">
                © {new Date().getFullYear()} Linksy
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === "forgot-password") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col">
        <DevBadge />
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          .linksy-font { font-family: 'Outfit', sans-serif; }
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
          .input-modern {
            transition: all 0.3s ease;
            background: #f8fafc;
          }
          .input-modern:focus {
            background: white;
            box-shadow: 0 0 0 4px rgba(66, 133, 244, 0.1);
          }
          input[type="password"]::-ms-reveal,
          input[type="password"]::-ms-clear {
            display: none;
          }
          input[type="password"]::-webkit-credentials-auto-fill-button,
          input[type="password"]::-webkit-textfield-decoration-container {
            display: none !important;
          }
          input::-ms-reveal {
            display: none;
          }
        `}</style>

        <header className="w-full py-6 px-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => navigateTo("landing")}
              className="flex items-center gap-1 linksy-font hover:opacity-70 transition-opacity"
            >
              <span className="text-2xl font-bold text-gray-800">L</span>
              <svg
                viewBox="0 0 40 55"
                className="w-5 h-7 -mx-0.5"
                style={{ marginBottom: "-4px" }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#2c2c2c"
                  strokeWidth="2.5"
                />
                <circle cx="20" cy="14" r="4" fill="#2c2c2c" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#2c2c2c" />
                <circle cx="11" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#2c2c2c" />
                <circle cx="29" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#2c2c2c" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">nksy</span>
            </button>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100 fade-in-up">
              <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 linksy-font">
                  {resetPasswordSent ? "Check your email" : "Reset Password"}
                </h1>
              </div>

              {resetPasswordSent ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      background: "#f0fdf4",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#22c55e"
                      strokeWidth="2"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <p
                    style={{
                      fontSize: "15px",
                      color: "#5f6368",
                      lineHeight: "1.6",
                      margin: "0 0 24px 0",
                      fontFamily: "'Outfit', sans-serif",
                    }}
                  >
                    We've sent a password reset link to
                    <br />
                    <strong style={{ color: "#202124" }}>
                      {resetPasswordEmail}
                    </strong>
                    <br />
                    <br />
                    Click the link in the email to set a new password.
                  </p>
                  <button
                    onClick={() => {
                      setResetPasswordSent(false);
                      navigateTo("login");
                    }}
                    className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold linksy-font"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  {error && (
                    <div
                      className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm linksy-font flex items-center gap-3"
                      style={{ marginTop: "10px" }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          flexShrink: 0,
                          width: "18px",
                          height: "18px",
                          overflow: "visible",
                        }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {error}
                    </div>
                  )}

                  <p
                    style={{
                      fontSize: "14px",
                      color: "#5f6368",
                      lineHeight: "1.6",
                      marginBottom: "24px",
                      fontFamily: "'Outfit', sans-serif",
                    }}
                  >
                    Enter the email address associated with your account and
                    we'll send you a link to reset your password.
                  </p>

                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                        Email
                      </label>
                      <div className="relative">
                        <Mail
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                          size={20}
                        />
                        <input
                          type="email"
                          value={resetPasswordEmail}
                          onChange={(e) =>
                            setResetPasswordEmail(e.target.value)
                          }
                          className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        setError("");
                        if (isAuthLoading) return;
                        if (!resetPasswordEmail) {
                          setError("Please enter your email address");
                          return;
                        }
                        if (
                          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetPasswordEmail)
                        ) {
                          setError("Please enter a valid email address");
                          return;
                        }
                        try {
                          setIsAuthLoading(true);
                          const { success, error: resetError } =
                            await resetPassword(resetPasswordEmail);
                          if (success) {
                            setResetPasswordSent(true);
                          } else {
                            setError(
                              resetError || "Failed to send reset email",
                            );
                          }
                        } catch (err) {
                          setError("Something went wrong. Please try again.");
                        } finally {
                          setIsAuthLoading(false);
                        }
                      }}
                      className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold flex items-center justify-center gap-2 mt-2 shadow-lg hover:shadow-xl linksy-font"
                      style={{
                        opacity: isAuthLoading ? 0.7 : 1,
                        cursor: isAuthLoading ? "not-allowed" : "pointer",
                      }}
                    >
                      {isAuthLoading ? (
                        <>
                          <div
                            style={{
                              width: "20px",
                              height: "20px",
                              border: "2px solid rgba(255,255,255,0.3)",
                              borderTop: "2px solid white",
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Reset Link
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => {
                        setError("");
                        navigateTo("login");
                      }}
                      className="w-full text-gray-500 py-3 hover:text-gray-700 transition-all font-medium linksy-font flex items-center justify-center gap-2"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      Back to Sign In
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>

        <footer className="w-full py-6 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm text-gray-400 linksy-font">
              © {new Date().getFullYear()} Linksy
            </p>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === "reset-password") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col">
        <DevBadge />
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          .linksy-font { font-family: 'Outfit', sans-serif; }
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
          .input-modern {
            transition: all 0.3s ease;
            background: #f8fafc;
          }
          .input-modern:focus {
            background: white;
            box-shadow: 0 0 0 4px rgba(66, 133, 244, 0.1);
          }
          input[type="password"]::-ms-reveal,
          input[type="password"]::-ms-clear {
            display: none;
          }
          input[type="password"]::-webkit-credentials-auto-fill-button,
          input[type="password"]::-webkit-textfield-decoration-container {
            display: none !important;
          }
          input::-ms-reveal {
            display: none;
          }
        `}</style>

        <header className="w-full py-6 px-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => navigateTo("landing")}
              className="flex items-center gap-1 linksy-font hover:opacity-70 transition-opacity"
            >
              <span className="text-2xl font-bold text-gray-800">L</span>
              <svg
                viewBox="0 0 40 55"
                className="w-5 h-7 -mx-0.5"
                style={{ marginBottom: "-4px" }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#2c2c2c"
                  strokeWidth="2.5"
                />
                <circle cx="20" cy="14" r="4" fill="#2c2c2c" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#2c2c2c" />
                <circle cx="11" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#2c2c2c" />
                <circle cx="29" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#2c2c2c" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">nksy</span>
            </button>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100 fade-in-up">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 linksy-font">
                  Set New Password
                </h1>
                <p className="text-gray-500 linksy-font">
                  Choose a strong password for your account
                </p>
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm linksy-font flex items-center gap-3"
                  style={{ marginTop: "10px" }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      flexShrink: 0,
                      width: "18px",
                      height: "18px",
                      overflow: "visible",
                    }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      style={{
                        position: "absolute",
                        right: "14px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {showNewPassword ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type={showConfirmNewPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }
                      style={{
                        position: "absolute",
                        right: "14px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {showConfirmNewPassword ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  onClick={async () => {
                    setError("");
                    if (isAuthLoading) return;
                    if (!newPassword || !confirmNewPassword) {
                      setError("Please fill in both fields");
                      return;
                    }
                    const passwordError = validatePassword(newPassword);
                    if (passwordError) {
                      setError(passwordError);
                      return;
                    }
                    if (newPassword !== confirmNewPassword) {
                      setError("Passwords do not match");
                      return;
                    }
                    try {
                      setIsAuthLoading(true);
                      const { success, error: updateError } =
                        await updatePassword(newPassword);
                      if (success) {
                        window._isPasswordRecovery = false;
                        setIsPasswordRecovery(false);
                        setNewPassword("");
                        setConfirmNewPassword("");
                        window.showToast(
                          "Password updated successfully!",
                          "success",
                        );
                        // Sign out first so user logs in fresh with new password
                        await logoutUser();
                        setCurrentUser(null);
                        setFirebaseUser(null);
                        window.currentAppUser = null;
                        navigateTo("login");
                      } else {
                        setError(updateError || "Failed to update password");
                      }
                    } catch (err) {
                      setError("Something went wrong. Please try again.");
                    } finally {
                      setIsAuthLoading(false);
                    }
                  }}
                  className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold flex items-center justify-center gap-2 mt-2 shadow-lg hover:shadow-xl linksy-font"
                  style={{
                    opacity: isAuthLoading ? 0.7 : 1,
                    cursor: isAuthLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isAuthLoading ? (
                    <>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      Update Password
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>

        <footer className="w-full py-6 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-sm text-gray-400 linksy-font">
              © {new Date().getFullYear()} Linksy
            </p>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === "admin" && window.isAdmin(currentUser?.uid)) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f8f9fa",
          fontFamily: "'Outfit', sans-serif",
        }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');`}</style>

        {/* Header */}
        <div
          style={{
            background: "white",
            borderBottom: "1px solid #e0e0e0",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            position: "sticky",
            top: 0,
            zIndex: 100,
          }}
        >
          <button
            onClick={() => navigateTo("map")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: "8px",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#374151"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            <h1
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "700",
                color: "#1a1a1a",
              }}
            >
              Admin Panel
            </h1>
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
              Manage reports, events & users
            </p>
          </div>
          <button
            id="admin-refresh-btn"
            style={{
              background: "#f3f4f6",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px",
              borderRadius: "10px",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#e5e7eb")}
            onMouseOut={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#374151"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <AdminPanelContent currentUser={currentUser} navigateTo={navigateTo} />
      </div>
    );
  }

  if (currentView === "banned") {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col items-center justify-center px-6"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');`}</style>
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "48px 32px",
            maxWidth: "420px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ea4335"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "12px",
            }}
          >
            Account Suspended
          </h1>
          <p
            style={{
              fontSize: "15px",
              color: "#5f6368",
              marginBottom: "24px",
              lineHeight: "1.6",
            }}
          >
            Your account has been suspended due to a violation of our community
            guidelines.
          </p>
          {currentUser?.banReason && (
            <div
              style={{
                background: "#fef7f7",
                border: "1px solid #fecaca",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "24px",
                textAlign: "left",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#ea4335",
                  margin: "0 0 4px",
                }}
              >
                Reason:
              </p>
              <p style={{ fontSize: "14px", color: "#5f6368", margin: 0 }}>
                {currentUser.banReason}
              </p>
            </div>
          )}
          <p
            style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "24px" }}
          >
            If you believe this is a mistake, please contact us at{" "}
            <a
              href="mailto:support@linksy.community"
              style={{ color: "#1a73e8", textDecoration: "underline" }}
            >
              support@linksy.community
            </a>
          </p>
          <button
            onClick={async () => {
              window._userBanned = false;
              await logoutUser();
              setCurrentUser(null);
              setFirebaseUser(null);
              window.currentAppUser = null;
              navigateTo("landing");
            }}
            style={{
              width: "100%",
              padding: "14px",
              background: "#f3f4f6",
              color: "#374151",
              border: "none",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        </div>
        <p style={{ marginTop: "24px", fontSize: "12px", color: "#9ca3af" }}>
          © {new Date().getFullYear()} Linksy
        </p>
      </div>
    );
  }

  if (currentView === "profile-setup") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white flex flex-col">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          .linksy-font { font-family: 'Outfit', sans-serif; }
          @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse-ring {
            0% { transform: scale(1); opacity: 0.5; }
            100% { transform: scale(1.3); opacity: 0; }
          }
          .fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
.input-modern {
            transition: all 0.3s ease;
            background: #f8fafc;
          }
          .input-modern:focus {
            background: white;
            box-shadow: 0 0 0 4px rgba(66, 133, 244, 0.1);
          }
          input[type="password"]::-ms-reveal,
          input[type="password"]::-ms-clear {
            display: none;
          }
          input[type="password"]::-webkit-credentials-auto-fill-button,
          input[type="password"]::-webkit-textfield-decoration-container {
            display: none !important;
          }
          input::-ms-reveal {
            display: none;
          }
          .avatar-ring {
            position: absolute;
            inset: -4px;
            border: 2px solid #4285f4;
            border-radius: 50%;
            animation: pulse-ring 2s ease-out infinite;
          }
        `}</style>

        {/* Header */}
        <header className="w-full py-6 px-8">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => navigateTo("landing")}
              className="flex items-center gap-1 linksy-font hover:opacity-70 transition-opacity"
            >
              <span className="text-2xl font-bold text-gray-800">L</span>
              <svg
                viewBox="0 0 40 55"
                className="w-5 h-7 -mx-0.5"
                style={{ marginBottom: "-4px" }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#2c2c2c"
                  strokeWidth="2.5"
                />
                {/* Center person (front, larger) */}
                <circle cx="20" cy="14" r="4" fill="#2c2c2c" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#2c2c2c" />
                {/* Left person */}
                <circle cx="11" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#2c2c2c" />
                {/* Right person */}
                <circle cx="29" cy="16" r="3" fill="#2c2c2c" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#2c2c2c" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">nksy</span>
            </button>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div
            className={`max-w-md w-full transition-all duration-300 ${slideAnimation}`}
          >
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100 fade-in-up">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 linksy-font">
                  Create Your Profile
                </h1>
                <p className="text-gray-500 linksy-font">
                  Just a few quick details to get started
                </p>
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm linksy-font flex items-center gap-3"
                  style={{ marginTop: "10px" }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      flexShrink: 0,
                      width: "18px",
                      height: "18px",
                      overflow: "visible",
                    }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="space-y-6">
                {/* Profile Picture Upload */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="avatar-ring"></div>
                    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-4 border-white shadow-lg overflow-hidden flex items-center justify-center">
                      {profileData.profilePicture ? (
                        <img
                          src={profileData.profilePicture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={40} className="text-gray-400" />
                      )}
                    </div>
                    <label
                      className="absolute -bottom-1 -right-1 bg-gray-900 text-white p-2.5 rounded-full cursor-pointer hover:bg-gray-800 active:scale-95 transition-all shadow-lg"
                      onClick={async (e) => {
                        if (window._isNativeApp?.()) {
                          e.preventDefault();
                          const file = await window._pickImageFile?.({ maxSize: 800, quality: 90 });
                          if (file) handleImageUpload({ target: { files: [file], value: '' } });
                        }
                      }}
                    >
                      <Camera size={18} />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm mt-4 linksy-font">
                    Tap to upload profile picture
                  </p>
                </div>

                {/* Name Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Your Name
                  </label>
                  <div className="relative">
                    <User
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) =>
                        setProfileData({ ...profileData, name: e.target.value })
                      }
                      className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                      placeholder="Enter your name"
                    />
                  </div>
                </div>

                {/* Date of Birth Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Date of Birth
                  </label>
                  <div className="relative">
                    <svg
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                      ></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      max={(() => {
                        const today = new Date();
                        today.setFullYear(today.getFullYear() - 18);
                        return today.toISOString().split("T")[0];
                      })()}
                      className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent linksy-font input-modern"
                    />
                  </div>
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Description
                  </label>
                  <textarea
                    value={profileData.description || ""}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        description: e.target.value,
                      })
                    }
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none linksy-font input-modern"
                    placeholder="Tell us about yourself..."
                  />
                  <p className="text-xs text-gray-400 mt-2 text-right linksy-font">
                    {(profileData.description || "").length}/500
                  </p>
                </div>

                {/* Gender Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 linksy-font">
                    Gender
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setProfileData({ ...profileData, gender: "man" })
                      }
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all linksy-font font-medium ${
                        profileData.gender === "man"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xl">👨</span>
                        <span className="text-sm">Man</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setProfileData({ ...profileData, gender: "woman" })
                      }
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all linksy-font font-medium ${
                        profileData.gender === "woman"
                          ? "border-pink-500 bg-pink-50 text-pink-700"
                          : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xl">👩</span>
                        <span className="text-sm">Woman</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setProfileData({ ...profileData, gender: "different" })
                      }
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all linksy-font font-medium ${
                        profileData.gender === "different"
                          ? "border-purple-500 bg-purple-50 text-purple-700"
                          : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xl">🌈</span>
                        <span className="text-sm">Different</span>
                      </div>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 linksy-font">
                    * This helps match you with relevant events
                  </p>
                </div>

                {/* Terms & Conditions - shown for Google users who skip registration */}
                {isGoogleUser && (
                  <div
                    className="flex items-start gap-3"
                    style={{ marginTop: "8px" }}
                  >
                    <input
                      type="checkbox"
                      id="terms-google"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        width: "20px",
                        height: "20px",
                        marginTop: "2px",
                        accentColor: "#1a73e8",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                    <label
                      htmlFor="terms-google"
                      className="text-sm text-gray-600 linksy-font"
                      style={{ cursor: "pointer", lineHeight: "1.5" }}
                    >
                      I agree to the{" "}
                      <a
                        href="https://linksy.community/terms"
                        target="_blank"
                        style={{
                          color: "#1a73e8",
                          fontWeight: 600,
                          textDecoration: "underline",
                        }}
                      >
                        Terms & Conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://linksy.community/privacy"
                        target="_blank"
                        style={{
                          color: "#1a73e8",
                          fontWeight: 600,
                          textDecoration: "underline",
                        }}
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                )}

                <button
                  onClick={handleCreateProfile}
                  className="w-full bg-gray-900 text-white py-4 px-6 rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl linksy-font"
                >
                  Start Exploring
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full py-6 px-6 border-t border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-1 linksy-font">
                <span className="text-lg font-bold text-gray-400">L</span>
                <svg
                  viewBox="0 0 40 55"
                  className="w-4 h-5 -mx-0.5"
                  style={{ marginBottom: "-3px" }}
                >
                  <path
                    d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2.5"
                  />
                  <circle cx="20" cy="14" r="4" fill="#9ca3af" />
                  <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#9ca3af" />
                  <circle cx="11" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="11" cy="24" rx="4" ry="3" fill="#9ca3af" />
                  <circle cx="29" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="29" cy="24" rx="4" ry="3" fill="#9ca3af" />
                </svg>
                <span className="text-lg font-bold text-gray-400">nksy</span>
              </div>
              <div className="flex gap-3 text-sm linksy-font">
                <a href="https://linksy.community/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Terms</a>
                <span style={{ color: '#9ca3af' }}>·</span>
                <a href="https://linksy.community/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>Privacy</a>
              </div>
              <div className="flex items-center gap-1">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span className="text-sm text-gray-400 linksy-font">
                  support@linksy.community
                </span>
              </div>
              <p className="text-sm text-gray-400 linksy-font">
                © {new Date().getFullYear()} Linksy
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (currentView === "guest-map") {
    return (
      <div
        className="min-h-screen flex flex-col overflow-hidden"
style={{ height: "100vh", background: "transparent" }}
      >
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

        * {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .leaflet-container {
          width: 100%;
          height: 100%;
          z-index: 1;
        }

        @media (max-width: 640px) {
          .guest-nav-bar { padding: 8px 16px !important; }
          .guest-nav-text { display: none !important; }
          .guest-footer { padding: 8px 12px !important; }
          .guest-footer-email { display: none !important; }
          .guest-footer-copyright { display: none !important; }
        }
      `}</style>

        {/* Nav Bar */}
        <div
          className="guest-nav-bar"
          style={{
            position: "relative",
            zIndex: 10000,
            padding: "12px 20px",
            borderBottom: "1px solid #e5e7eb",
            background: "white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1f2937",
                }}
              >
                L
              </span>
              <svg
                viewBox="0 0 40 55"
                style={{
                  width: "20px",
                  height: "28px",
                  marginBottom: "-4px",
                  marginLeft: "-2px",
                  marginRight: "-2px",
                }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#1f2937"
                  strokeWidth="2.5"
                />
                <circle cx="20" cy="14" r="4" fill="#1f2937" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#1f2937" />
                <circle cx="11" cy="16" r="3" fill="#1f2937" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#1f2937" />
                <circle cx="29" cy="16" r="3" fill="#1f2937" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#1f2937" />
              </svg>
              <span
                style={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#1f2937",
                }}
              >
                nksy
              </span>
            </div>

            {/* CTA */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span className="guest-nav-text" style={{ fontSize: "14px", color: "#6b7280" }}>
                Join to interact with events
              </span>
              <button
                onClick={() => {
                  setIsGuestView(false);
                  setGuestEventId(null);
                  window.cleanupGuestMap();
                  setCurrentView("landing");
                  window.history.replaceState({}, "", window.location.pathname);
                }}
                style={{
                  padding: "10px 20px",
                  background: "#1f2937",
                  color: "white",
                  border: "none",
                  borderRadius: "50px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#374151")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = "#1f2937")
                }
              >
                Sign Up Free
              </button>
            </div>
          </div>
        </div>

        {/* Map Container */}
        <div style={{ flex: 1, position: "relative" }}>
          <div
            id="guest-map-container"
            ref={(el) => {
              if (el && !window.guestMapInitialized) {
                window.guestMapInitialized = true;

                // Clean up any existing map first
                if (window.guestMapRef) {
                  try {
                    window.guestMapRef.remove();
                  } catch (e) {
                    console.warn("Error removing existing guest map:", e);
                  }
                  window.guestMapRef = null;
                }

                // Small delay to ensure DOM is ready
                setTimeout(() => {
                  try {
                    const L = window.L;
                    if (!L) {
                      console.error("❌ Leaflet not loaded");
                      window.guestMapInitialized = false;
                      return;
                    }

                    // Check if element still exists in DOM
                    if (!el || !document.body.contains(el)) {
                      console.error("❌ Map container not in DOM");
                      window.guestMapInitialized = false;
                      return;
                    }

                    const guestMap = L.map(el, {
                      zoomControl: false,
                      attributionControl: false,
                    });

                    // Set view after a small delay to avoid Leaflet timing issues
                    setTimeout(() => {
                      guestMap.setView([54.5189, 18.5305], 12);
                    }, 50);

                    L.tileLayer(
                      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
                      {
                        maxZoom: 19,
                        attribution: "",
                      },
                    ).addTo(guestMap);

                    L.control.zoom({ position: "bottomright" }).addTo(guestMap);

                    window.guestMapRef = guestMap;

                    // Create marker cluster group for guest map
                    const guestClusterGroup = L.markerClusterGroup({
                      maxClusterRadius: 60,
                      showCoverageOnHover: false,
                      zoomToBoundsOnClick: true,
                      spiderfyOnMaxZoom: true,
                      chunkedLoading: true,
                      chunkInterval: 100,
                      chunkDelay: 16,
                      removeOutsideVisibleBounds: true,
                      animateAddingMarkers: false,
                      iconCreateFunction: (cluster) => {
                        const count = cluster.getChildCount();
                        return L.divIcon({
                          html: `<div style="
                            width: 40px;
                            height: 40px;
                            background: #1f2937;
                            border: 3px solid white;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: white;
                            font-size: 13px;
                            font-weight: 700;
                            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                            font-family: 'Outfit', sans-serif;
                          ">${count}</div>`,
                          className: '',
                          iconSize: [40, 40],
                          iconAnchor: [20, 20],
                        });
                      },
                    });
                    guestClusterGroup.addTo(guestMap);
                    window.guestClusterGroup = guestClusterGroup;

                    // Inject pulse animation for guest markers
                    if (!document.getElementById("guest-marker-pulse-style")) {
                      const style = document.createElement("style");
                      style.id = "guest-marker-pulse-style";
                      style.textContent = `
                      @keyframes slowPing {
                        0%, 100% { transform: scale(0.8); opacity: 0; }
                        50% { transform: scale(1.2); opacity: 0.4; }
                      }
                    `;
                      document.head.appendChild(style);
                    }

                    // Load events after map is fully ready
                    guestMap.whenReady(() => {
                      loadGuestEvents();
                    });

                    // Load all events and create markers
                    const loadGuestEvents = async () => {
                      try {
                        const supabaseClient = window.supabase || supabase;
                        const { data: events, error } = await supabaseClient
                          .from("events")
                          .select("*")
                          .eq("archived", false)
                          .limit(100);

                        if (error) {
                          console.error("Error loading events:", error);
                          return;
                        }

                        if (events && events.length > 0) {
                          events.forEach((event) => {
                            // Calculate status color
                            const now = new Date();
                            const startDate = new Date(event.start_date_time);
                            const endDate = new Date(event.end_date_time);
                            const hasStarted = now >= startDate;
                            const hasEnded = now >= endDate;

                            let markerColor = "#4285f4"; // blue - upcoming
                            if (hasEnded) {
                              markerColor = "#9e9e9e"; // gray - ended
                            } else if (hasStarted) {
                              markerColor = "#34a853"; // green - ongoing
                            }

                            // Create custom icon
                            const showPulse = !hasEnded;
                            const icon = L.divIcon({
                              className: "guest-event-marker",
                              html: `
                          <div style="
                            position: relative;
                            width: 48px;
                            height: 48px;
                          ">
                            ${
                              showPulse
                                ? `
                            <div style="
                              position: absolute;
                              inset: -10px;
                              background: ${markerColor};
                              opacity: 0.4;
                              border-radius: 50%;
                              animation: slowPing 2.5s ease-in-out infinite;
                            "></div>
                            <div style="
                              position: absolute;
                              inset: -5px;
                              background: ${markerColor};
                              opacity: 0.5;
                              border-radius: 50%;
                              animation: slowPing 2.5s ease-in-out infinite 0.3s;
                            "></div>
                            `
                                : ""
                            }
                            <div style="
                              position: relative;
                              z-index: 10;
                              width: 48px;
                              height: 48px;
                              background: white;
                              border: 3px solid ${markerColor};
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              box-shadow: 0 4px 12px rgba(0,0,0,0.25);
                              cursor: pointer;
                            ">
                              ${
                                event.place_icon
                                  ? `<img src="${event.place_icon}" style="width: 26px; height: 26px; border-radius: 4px;" />`
                                  : `
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="${markerColor}">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                                </svg>
                              `
                              }
                            </div>
                          </div>
                        `,
                              iconSize: [48, 48],
                              iconAnchor: [24, 24],
                            });

                            const marker = L.marker(
                              [event.latitude, event.longitude],
                              { icon },
                            ).addTo(guestClusterGroup);

                            marker.bindTooltip(event.title, {
                              permanent: false,
                              direction: "top",
                              offset: [0, -20],
                            });

                            marker.on("click", () => {
                              window.showGuestEventDetails(event);
                            });

                            // If this is the shared event, center on it and show details
                            if (
                              guestEventId &&
                              event.event_id === guestEventId
                            ) {
                              // Use setTimeout to ensure map is stable before setView
                              setTimeout(() => {
                                try {
                                  guestMap.setView(
                                    [event.latitude, event.longitude],
                                    15,
                                  );
                                } catch (e) {
                                  console.warn("Could not set view:", e);
                                }
                                window.showGuestEventDetails(event);
                              }, 200);
                            }
                          });
                        }
                      } catch (err) {
                        console.error("Error in loadGuestEvents:", err);
                      }
                    };
                  } catch (err) {
                    console.error("❌ Error initializing guest map:", err);
                    window.guestMapInitialized = false;
                  }
                }, 100); // End of setTimeout
              }
            }}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />

          {/* Guest Event Details Panel */}
          <GuestEventDetailsPanel />

          {/* Footer */}
          <div
            className="guest-footer"
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "white",
              borderTop: "1px solid #e5e7eb",
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              zIndex: 1000,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#9ca3af",
                }}
              >
                L
              </span>
              <svg
                viewBox="0 0 40 55"
                style={{
                  width: "12px",
                  height: "16px",
                  marginBottom: "-2px",
                  marginLeft: "-1px",
                  marginRight: "-1px",
                }}
              >
                <path
                  d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2.5"
                />
                <circle cx="20" cy="14" r="4" fill="#9ca3af" />
                <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#9ca3af" />
                <circle cx="11" cy="16" r="3" fill="#9ca3af" />
                <ellipse cx="11" cy="24" rx="4" ry="3" fill="#9ca3af" />
                <circle cx="29" cy="16" r="3" fill="#9ca3af" />
                <ellipse cx="29" cy="24" rx="4" ry="3" fill="#9ca3af" />
              </svg>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#9ca3af",
                }}
              >
                nksy
              </span>
            </div>
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>
              linksy.community
            </span>
            <div className="guest-footer-email" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ca3af"
                strokeWidth="2"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                support@linksy.community
              </span>
            </div>
            <span className="guest-footer-copyright" style={{ fontSize: "12px", color: "#9ca3af" }}>
              © {new Date().getFullYear()} Linksy
            </span>
          </div>
        </div>
      </div>
    );
  }

if (currentView === "map") {
    return (
      <>
      {/* Map loading overlay — shown until events finish loading */}
      {!isMapReady && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99999,
          background: "linear-gradient(to bottom, #ffffff, #f9fafb, #ffffff)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "opacity 0.4s ease",
        }}>
          <style>{`
            @keyframes pinBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
            @keyframes pinPulse  { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.05);opacity:0.9} }
            @keyframes peoplePulse { 0%,100%{opacity:0.6} 50%{opacity:1} }
            @keyframes ripple { 0%{transform:translateX(-50%) scale(0.8);opacity:0.6} 100%{transform:translateX(-50%) scale(2.5);opacity:0} }
            @keyframes fadeInUp { 0%{opacity:0;transform:translateY(10px)} 100%{opacity:1;transform:translateY(0)} }
          `}</style>
          <div style={{ textAlign: "center" }}>
            <div style={{ position: "relative", width: "80px", height: "80px", margin: "0 auto 20px" }}>
              <div style={{ position:"absolute", bottom:"-8px", left:"50%", transform:"translateX(-50%)", width:"50px", height:"12px", background:"radial-gradient(ellipse, rgba(66,133,244,0.4) 0%, transparent 70%)", borderRadius:"50%", animation:"ripple 1.5s ease-out infinite" }} />
              <div style={{ position:"absolute", bottom:"-8px", left:"50%", transform:"translateX(-50%)", width:"50px", height:"12px", background:"radial-gradient(ellipse, rgba(66,133,244,0.4) 0%, transparent 70%)", borderRadius:"50%", animation:"ripple 1.5s ease-out 0.75s infinite" }} />
              <svg viewBox="0 0 60 80" style={{ width:"100%", height:"100%", animation:"pinBounce 1s ease-in-out infinite, pinPulse 2s ease-in-out infinite", filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.15))" }}>
                <path d="M30 2 C12 2 2 15 2 30 C2 50 30 78 30 78 C30 78 58 50 58 30 C58 15 48 2 30 2 Z" fill="none" stroke="#2c2c2c" strokeWidth="3" />
                <path d="M30 5 C14 5 5 16 5 29 C5 47 30 73 30 73 C30 73 55 47 55 29 C55 16 46 5 30 5 Z" fill="white" />
                <g style={{ animation:"peoplePulse 1.5s ease-in-out infinite" }}>
                  <circle cx="30" cy="24" r="6" fill="#2c2c2c" />
                  <ellipse cx="30" cy="40" rx="9" ry="7" fill="#2c2c2c" />
                  <circle cx="16" cy="27" r="4.5" fill="#2c2c2c" />
                  <ellipse cx="16" cy="40" rx="6.5" ry="5" fill="#2c2c2c" />
                  <circle cx="44" cy="27" r="4.5" fill="#2c2c2c" />
                  <ellipse cx="44" cy="40" rx="6.5" ry="5" fill="#2c2c2c" />
                </g>
              </svg>
            </div>
            <p style={{ color:"#5f6368", fontSize:"16px", fontFamily:"'Outfit', sans-serif", fontWeight:"400", animation:"fadeInUp 0.5s ease-out 0.3s backwards" }}>Loading...</p>
          </div>
        </div>
      )}
      <div
        className="flex flex-col overflow-hidden"
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "transparent" }}
      >
        <DevBadge />
        {/* ADD THIS STYLE TAG HERE */}
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
        
        * {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .leaflet-container {
          width: 100%;
          height: 100%;
        }
        
/* Prevent multiple map initialization */
#map-container.leaflet-container {
          position: relative;
        }
        .notification-bell-btn:hover {
          box-shadow: 0 6px 16px rgba(0,0,0,0.2), 0 3px 8px rgba(0,0,0,0.15) !important;
          transform: scale(1.05);
        }
        :root { --footer-height: 46px; --safe-bottom: env(safe-area-inset-bottom, 0px); }

        @media (max-width: 800px) {
          /* tab bar = 44px, attribution = 20px, footer = 46px */
          /* satellite/locate: just above tab bar */
          #map-btn-satellite  { bottom: 110px !important; }
#map-btn-locate     { bottom: 165px !important; }
.map-btn-support    { bottom: 110px !important; }
.map-btn-patchnotes { bottom: 165px !important; }
          .leaflet-bottom.leaflet-right { display: none !important; }
          .leaflet-bottom.leaflet-left  { display: none !important; }
          .mobile-leaflet-attribution { display: block !important; }
        }
        .mobile-leaflet-attribution { display: none; }
        .leaflet-top.leaflet-right {
  top: 20px !important;
}
.leaflet-top.leaflet-right .leaflet-control-attribution {
  display: block !important;
  font-size: 11px !important;
  margin: 0 !important;
  border-radius: 4px 0 0 4px !important;
  background: rgba(255,255,255,0.8) !important;
}
.leaflet-bottom.leaflet-right {
  display: none !important;
}
      `}</style>
        <div
          className={`transition-all duration-300 ${slideAnimation} flex-1 flex flex-col overflow-hidden`}
style={{ background: 'transparent', height: '100%' }}
        >
{/* Header */}
          <MapHeader
            currentUser={currentUser}
            isOnline={isOnline}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            isSearching={isSearching}
            userLocation={userLocation}
            handleSearch={handleSearch}
            handleResultClick={handleResultClick}
            lastSelectionTimeRef={lastSelectionTime}
            ignoreSearchResultsRef={ignoreSearchResults}
            lastSearchQueryRef={lastSearchQuery}
            cachedResultsRef={cachedResults}
            isPinMode={isPinMode}
            setIsPinMode={setIsPinMode}
            pinLocation={pinLocation}
            setPinLocation={setPinLocation}
            googleMapRef={googleMapRef}
            openCreateEventPanelWithCustomLocation={openCreateEventPanelWithCustomLocation}
            isToolsMenuOpen={isToolsMenuOpen}
            setIsToolsMenuOpen={setIsToolsMenuOpen}
            searchRadius={searchRadius}
            updateRangeCircle={updateRangeCircle}
            loadExistingEvents={loadExistingEvents}
isNotificationsPanelOpen={isNotificationsPanelOpen}
            setIsNotificationsPanelOpen={setIsNotificationsPanelOpen}
            notificationCount={notificationCount}
            hasUnreadChats={hasUnreadChats}
            isChatsListPanelOpen={isChatsListPanelOpen}
            onOpenProfile={() => {
              if (window.innerWidth <= 800) {
                window.closeAllPanels?.();
                window.closeChatPanel?.();
                window.closeChatsListPanel?.();
                window.closeAllParticipantsPanel?.();
                window.closeUserProfilePanel?.();
              }
              setIsProfilePanelOpen(true);
            }}
          />

          {/* Google Maps Container */}
          <div
  ref={mapRef}
  id="map-container"
  className="flex-1"
  style={{ minHeight: "400px", marginTop: "-20px", paddingTop: "20px" }}
            onClick={() => {
              if (isToolsMenuOpen) setIsToolsMenuOpen(false);
            }}
          />

          {/* Center Pin Overlay - Only visible in pin mode */}
          {isPinMode && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -100%)",
                zIndex: 400,
                pointerEvents: "none",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
              }}
            >
              <svg width="48" height="64" viewBox="0 0 48 64" fill="none">
                <path
                  d="M24 0C10.745 0 0 10.745 0 24c0 18 24 40 24 40s24-22 24-40C48 10.745 37.255 0 24 0z"
                  fill="#2c2c2c"
                />
                <circle cx="24" cy="24" r="10" fill="white" />
              </svg>
            </div>
          )}

{/* Filters Panel */}
          <FiltersPanel
            searchRadius={searchRadius}
            setSearchRadius={setSearchRadius}
            rangeValues={rangeValues}
            getSliderIndex={getSliderIndex}
            getKmFromIndex={getKmFromIndex}
            formatDistance={formatDistance}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            loadExistingEvents={loadExistingEvents}
            updateRangeCircle={updateRangeCircle}
          />

          {/* Patch Notes Button - Bottom Left (above Support) */}
          <button
            className="map-btn-patchnotes"
            onClick={() => window.showPatchNotesModal()}
            style={{
              position: "absolute",
              bottom: "125px",
              left: "16px",
              width: "44px",
              height: "44px",
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1000,
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#f8f9fa";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "white";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="What's New"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8b7cf8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </button>

          {/* Support Button - Bottom Left */}
          <button
            className="map-btn-support"
            onClick={() => window.showSupportModal()}
            style={{
              position: "absolute",
              bottom: "70px",
              left: "16px",
              width: "44px",
              height: "44px",
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1000,
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#f8f9fa";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "white";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Contact Support"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#34a853"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </button>

          {/* Find My Location Button - Bottom Right */}
<button
  id="map-btn-locate"
onClick={() => {
  const flyTo = (lat, lng) => {
    if (window.leafletMap) window.leafletMap.flyTo([lat, lng], 15, { duration: 1 });
    if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
    setIsAtUserLocation(true);
    const btn = document.getElementById('map-btn-locate');
    if (btn) {
      btn.style.background = 'white';
      btn.style.border = '1px solid #e5e7eb';
      btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      btn.querySelector('svg').style.stroke = '#4285f4';
    }
  };

  // Use cached position instantly if available
  if (lastKnownPositionRef.current) {
    flyTo(lastKnownPositionRef.current.lat, lastKnownPositionRef.current.lng);
    return;
  }

  // Fallback to userLocation state
  if (userLocation) {
    flyTo(userLocation.lat, userLocation.lng);
    return;
  }

  // Last resort: fresh geolocation request
  if (window.Capacitor?.isNativePlatform?.()) {
    Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      .then((pos) => flyTo(pos.coords.latitude, pos.coords.longitude))
      .catch((e) => console.warn('locate me (native) failed:', e));
  } else if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => flyTo(position.coords.latitude, position.coords.longitude),
      () => {},
      { timeout: 5000, maximumAge: 10000 }
    );
  }
}}
  style={{
    position: "absolute",
    bottom: "125px",
    right: "16px",
    width: "44px",
    height: "44px",
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 1000,
    transition: "all 0.2s",
    color: "#5f6368",
  }}
  onMouseOver={(e) => { e.currentTarget.style.background = "#f8f9fa"; e.currentTarget.style.transform = "scale(1.05)"; }}
  onMouseOut={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.transform = "scale(1)"; }}
  title="Find My Location"
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isAtUserLocation ? "#4285f4" : "#9ca3af"} strokeWidth="2.5">
    <circle cx="12" cy="12" r="3"></circle>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
  </svg>
</button>

{/* Satellite Toggle Button - Bottom Right */}
<button
  id="map-btn-satellite"
  onClick={() => {
    if (!window._satelliteLayer) {
window._satelliteLayer = L.tileLayer(
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  { attribution: "", maxZoom: 19 }
);
    }
    const btn = document.getElementById('map-btn-satellite');
    if (!window._satelliteActive) {
      window._satelliteLayer.addTo(window.leafletMap);
      window._satelliteActive = true;
      if (btn) {
btn.style.background = '#fff8f0';
btn.style.border = '1px solid #f5a623';
btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
btn.querySelector('svg').style.stroke = '#f5a623';
      }
    } else {
      window.leafletMap.removeLayer(window._satelliteLayer);
      window._satelliteActive = false;
      if (btn) {
btn.style.background = 'white';
btn.style.border = '1px solid #e5e7eb';
btn.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
btn.querySelector('svg').style.stroke = '#2d7a4f';
btn.style.opacity = '1';
      }
    }
  }}
  style={{
    position: "absolute",
    bottom: "70px",
    right: "16px",
    width: "44px",
    height: "44px",
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 1000,
    transition: "all 0.2s",
    color: "#5f6368",
  }}
  onMouseOver={(e) => { e.currentTarget.style.background = "#f8f9fa"; e.currentTarget.style.transform = "scale(1.05)"; }}
  onMouseOut={(e) => { e.currentTarget.style.background = "white"; e.currentTarget.style.transform = "scale(1)"; }}
  title="Toggle Satellite View"
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2d7a4f" strokeWidth="2">
    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"></polygon>
    <line x1="9" y1="3" x2="9" y2="18"></line>
    <line x1="15" y1="6" x2="15" y2="21"></line>
  </svg>
</button>

{/* Mobile Leaflet attribution - top right, under nav bar */}
<div className="mobile-leaflet-attribution" style={{
  position: "absolute",
  top: 0,
  right: 0,
  background: "rgba(255,255,255,0.8)",
  padding: "1px 5px",
  fontSize: "11px",
  color: "#333",
  zIndex: 599,
  borderBottomLeftRadius: "4px",
  lineHeight: "1.4",
  boxShadow: "-1px 1px 2px rgba(0,0,0,0.1)",
}}>
  <a href="https://leafletjs.com" target="_blank" rel="noopener noreferrer" style={{ color: "#333" }}>Leaflet</a>
  {" | "}
  © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={{ color: "#333" }}>OpenStreetMap</a>
  {" "}
  <span title="OpenStreetMap contributors">contributors</span>
</div>

          {/* Mobile Tab Bar - visible only on mobile (<800px) */}
          <div className="mobile-tab-bar" style={{
            display: "none",
            position: "absolute",
            bottom: "var(--footer-height, 46px)",
            left: 0,
            right: 0,
            height: "44px",
            background: "white",
            borderTop: "1px solid rgb(139 139 139)",
            borderTopLeftRadius: "40px",
            borderTopRightRadius: "40px",
            zIndex: 1010,
            alignItems: "center",
            justifyContent: "space-around",
            padding: "0",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
          }}>
{/* Chats */}
<button
  onClick={() => isChatsListPanelOpen ? window.closeChatsListPanel() : window.openChatsListPanel()}
  style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", padding: "0", width: "44px", height: "44px", position: "relative", visibility: isPinMode ? "hidden" : "visible" }}
>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              {hasUnreadChats && (
                <span style={{ position: "absolute", top: "6px", right: "6px", width: "8px", height: "8px", background: "#ea4335", borderRadius: "50%", border: "2px solid white" }}></span>
              )}
            </button>

            {/* Custom Pin */}
            {isPinMode ? (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsPinMode(false); setPinLocation(null); }}
                  style={{ width: "36px", height: "36px", background: "#fee2e2", border: "none", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea4335" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <button
                  onClick={(e) => {
  e.stopPropagation();
  e.preventDefault();
  const loc = pinLocation
    ? { ...pinLocation }
    : googleMapRef.current
      ? (() => { const c = googleMapRef.current.getCenter(); return { lat: c.lat, lng: c.lng }; })()
      : null;
  setIsPinMode(false);
  setPinLocation(null);
  if (loc) {
    setTimeout(() => openCreateEventPanelWithCustomLocation(loc), 50);
  }
}}
                  style={{ width: "36px", height: "36px", background: "#dcfce7", border: "none", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34a853" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
              </div>
            ) : (
              <button
              data-pin-btn
                onClick={() => {
                  setIsPinMode(true);
                  if (googleMapRef.current) {
                    const center = googleMapRef.current.getCenter();
                    setPinLocation({ lat: center.lat, lng: center.lng });
                  }
                }}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", padding: "0", width: "44px", height: "44px" }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
              </button>
            )}

            {/* Filters */}
            <button
              onClick={() => {
                const slider = document.getElementById('rangeSliderPanel');
                if (!slider) return;
                const isOpen = slider.dataset.snap && slider.dataset.snap !== 'closed';
                if (isOpen) {
                  slider.dataset.snap = 'closed';
                  slider.style.bottom = '0';
                  slider.style.transform = 'translateX(-50%) translateY(100%)';
                  if (window.rangeCircle) { window.rangeCircle.remove(); window.rangeCircle = null; }
                } else {
                  const isMobile = window.innerWidth <= 800;
                  slider.style.bottom = isMobile ? 'calc(var(--footer-height, 46px) + 44px + 10px)' : '46px';
                  if (isMobile) {
                    slider.dataset.snap = 'half';
                    slider.style.transform = 'translateX(-50%) translateY(calc(100% - 265px))';
                  } else {
                    slider.dataset.snap = 'full';
                    slider.style.transform = 'translateX(-50%) translateY(0)';
                  }
                  updateRangeCircle(searchRadius);
                  loadExistingEvents(searchRadius);
                }
              }}
              data-filter-btn style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", padding: "0", width: "44px", height: "44px", visibility: isPinMode ? "hidden" : "visible" }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2c2c2c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="18" x2="20" y2="18"></line>
                <circle cx="16" cy="6" r="2" fill="white" stroke="#2c2c2c" strokeWidth="2"></circle>
                <circle cx="9" cy="12" r="2" fill="white" stroke="#2c2c2c" strokeWidth="2"></circle>
                <circle cx="15" cy="18" r="2" fill="white" stroke="#2c2c2c" strokeWidth="2"></circle>
              </svg>
            </button>
          </div>

{/* Event count footer with branding */}
          <div id="map-footer" className="bg-white border-t border-gray-200 p-3 shadow-lg" style={{zIndex: 1010}}>
            <div className="flex items-center justify-between px-2">
              {/* Logo */}
              <div
                style={{ display: "flex", alignItems: "center", gap: "2px" }}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#9ca3af",
                  }}
                >
                  L
                </span>
                <svg
                  viewBox="0 0 40 55"
                  style={{
                    width: "12px",
                    height: "16px",
                    marginBottom: "-2px",
                    marginLeft: "-1px",
                    marginRight: "-1px",
                  }}
                >
                  <path
                    d="M20 2 C9 2 2 10 2 20 C2 34 20 53 20 53 C20 53 38 34 38 20 C38 10 31 2 20 2 Z"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2.5"
                  />
                  <circle cx="20" cy="14" r="4" fill="#9ca3af" />
                  <ellipse cx="20" cy="24" rx="5.5" ry="4" fill="#9ca3af" />
                  <circle cx="11" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="11" cy="24" rx="4" ry="3" fill="#9ca3af" />
                  <circle cx="29" cy="16" r="3" fill="#9ca3af" />
                  <ellipse cx="29" cy="24" rx="4" ry="3" fill="#9ca3af" />
                </svg>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#9ca3af",
                  }}
                >
                  nksy
                </span>
              </div>

              {/* Event count */}
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-blue-600" />
                <p className="text-xs font-medium text-gray-600">
                  {(() => {
                    const userEvents = eventMarkersRef.current.filter((m) => {
                      const markerElement = m?.getElement?.();
                      return (
                        markerElement?.querySelector("[data-place-id]")?.dataset
                          ?.creatorId === currentUser?.uid
                      );
                    }).length;

                    const otherEvents = totalEventsCount - userEvents;

                    if (userEvents > 0 && otherEvents > 0) {
                      return `${totalEventsCount} events (${userEvents} yours, ${otherEvents} +${searchRadius}km)`;
                    } else if (userEvents > 0) {
                      return `${userEvents} event${userEvents !== 1 ? "s" : ""} (yours)`;
                    } else {
                      return `${totalEventsCount} event${totalEventsCount !== 1 ? "s" : ""} (+${searchRadius}km)`;
                    }
                  })()}
                </p>
              </div>

              {/* Copyright */}
              <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                © {new Date().getFullYear()} Linksy
              </span>
            </div>
          </div>

          {/* Chats List Panel - Slides from left, same as profile panel */}
          {isChatsListPanelOpen && (
            <>
              {/* Overlay */}
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "transparent",
                  zIndex: 9998,
                  opacity: 1,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const panel = document.getElementById("chatsListPanel");
                  if (panel) {
                    setChatsListPanelAnimating("exiting");
                    setTimeout(() => {
                      setIsChatsListPanelOpen(false);
                      setChatsListPanelAnimating("");
                    }, 300);
                  }
                }}
              />

              {/* Chats Panel */}
              <div
                id="chatsListPanel"
                data-panel="chats-list"
                style={{
                  position: "fixed",
                  top: "79px",
                  left: 0,
                  width: "380px",
                  maxWidth: "calc(100vw - 4px)",
                  height: "calc(100vh - 79px - var(--footer-height, 50px))",
                  background: "white",
                  borderBottomRightRadius: "20px",
                  borderTopRightRadius: "20px",
                  boxShadow: "4px 0 20px rgba(0,0,0,0.08)",
                  zIndex: 9999,
                  transform:
                    chatsListPanelAnimating === "visible"
                      ? "translateX(0)"
                      : "translateX(-100%)",
                  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  fontFamily: "'Outfit', sans-serif",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e5e7eb",
                    flexShrink: 0,
                    background: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5f6368"
                    strokeWidth="2"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: "500",
                      color: "#202124",
                      flex: 1,
                    }}
                  >
                    Chats
                  </h3>
                  <button
                    onClick={() => {
                      setChatsListPanelAnimating("exiting");
                      setTimeout(() => {
                        setIsChatsListPanelOpen(false);
                        setChatsListPanelAnimating("");
                      }, 300);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#5f6368",
                      cursor: "pointer",
                      padding: "8px",
                      width: "36px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      transition: "background 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = "#f3f4f6")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = "none")
                    }
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                {/* Chats List Container - loadChatsList() fills this */}
                <div
                  style={{ flex: 1, overflowY: "auto", background: "white" }}
                >
                  <ChatsListContent />
                </div>
              </div>
            </>
          )}

{/* Profile Panel */}
{isProfilePanelOpen && (
            <ProfilePanel
              currentUser={currentUser}
              setCurrentUser={setCurrentUser}
              profileBackground={profileBackground}
              setProfileBackground={setProfileBackground}
              onClose={() => {
                setIsProfilePanelOpen(false);
                setProfilePanelInitialView('landing');
              }}
              navigateTo={navigateTo}
              handleLogOut={handleLogOut}
              initialView={profilePanelInitialView}
            />
          )}

{/* Notifications Dropdown - Portaled to body to escape stacking contexts */}
          {isNotificationsPanelOpen && createPortal(
            <NotificationsPanel
              notifications={notifications}
              onClose={() => setIsNotificationsPanelOpen(false)}
              onOpenRequests={() => {
                setIsNotificationsPanelOpen(false);
                setProfilePanelInitialView('requests');
                setIsProfilePanelOpen(true);
              }}
            />,
            document.body
          )}
        </div>
        <Toast />
        <FullScreenImage />
        <ConfirmModal />
        <PatchNotesModal />
        <SupportModal />
        <LocationPermissionBanner />
        <ReportModal />
        <JoinRequestPopup />
        <EditPeopleLimitModal />
        </div>
      <UserProfilePanel />
      <AllParticipantsPanel />
    </>
  );
  }

  return null;
}
